import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Psychology from '@mui/icons-material/Psychology'
import PersonSearch from '@mui/icons-material/PersonSearch'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import ArrowBack from '@mui/icons-material/ArrowBack'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import ExpandMore from '@mui/icons-material/ExpandMore'
import PersonAddAlt1 from '@mui/icons-material/PersonAddAlt1'
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked'
import CloseIcon from '@mui/icons-material/Close'
import Warning from '@mui/icons-material/Warning'
import Refresh from '@mui/icons-material/Refresh'
import Save from '@mui/icons-material/Save'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { toast } from 'react-toastify'
import { mediaURL } from 'src/consumer/services'
import {
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_SCREENING,
  EVENT_TYPE_THERAPY_SESSION,
  GENDER_LIST_ONBOARDING,
  MENTOR_REG_ID_TYPE_ARRAY,
  THERAPY_DISCIPLINE_OPTIONS,
  THERAPY_DISCIPLINE_PSYCHOTHERAPY,
  therapyDisciplineLabel,
} from 'src/consumer/constants'
import {
  deleteSPDataWithParams,
  getSPDataWithParams,
  postSPDataWithoutParams,
} from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import useServerAuth from 'src/customHooks/useServerAuth'

// Helper to build the support draft endpoint URL with practitioner-id + type query params.
// "new" stands in for an unsaved practitioner so the server's compound index has a stable key.
function buildDraftEndpoint(practitionerId, practitionerType) {
  const params = new URLSearchParams()
  params.set('practitioner-id', practitionerId || 'new')
  if (practitionerType) params.set('type', practitionerType)
  return `/api/secure/cc/support/practitioner-onboarding/draft?${params.toString()}`
}
import PictureUploadComponent from 'src/consumer/components/Helpers/PictureUploadComponent/PictureUploadComponent'
import SignaturePad from 'src/consumer/components/Helpers/SignaturePad/SignaturePad'
import { DocumentUploadComponent } from 'src/consumer/components/helperComponents'
import PaymentConfigForm from 'src/supportPortal/components/common/paymentConfig/PaymentConfigForm'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import ApplicantSubmissionsPanel from 'src/supportPortal/views/practitioners/ApplicantSubmissionsPanel'
import {
  checkPractitionerSlugAvailability,
  getClinicAppointmentTypes,
  getPractitionerOnboardingBootstrap,
  getRoleEventTypesPolicy,
  getPractitionerSpecializationCatalog,
  getPractitionerQualificationCatalog,
  checkPractitionerContactExistence,
  markPractitionerContactVerifiedOffline,
  saveSupportFeeModel,
  getPractitionerOnboardingDetail,
  getSupportPractitionerPatientContactVisibility,
  getSupportPractitionerSettings,
  savePractitionerOnboarding,
  saveSupportPractitionerPatientContactVisibility,
  saveSupportPractitionerSettings,
  suggestPractitionerSlug,
  updateSupportPractitionerEnabled,
} from 'src/supportPortal/services/practitionerOnboardingService'

const PRACTITIONER_TYPE_LABELS = {
  doctor: 'Doctor',
  therapist: 'Therapist',
  screener: 'Screener',
}

// Auto-save draft (Phase 14g). The in-flight form is persisted to the server (collection
// practitioner_onboarding_drafts) on every change (debounced) so the agent's work survives
// tab-close, browser-crash, or moving to another machine. Drafts are scoped per practitioner-id
// (or `new` for fresh-form flows) + per practitioner-type on the server.
//
// Drafts are STRICTLY separate from formal submission — auto-save POSTs only to /draft, never
// /save. The formal /save endpoint clears the draft on success.
const DRAFT_AUTOSAVE_DEBOUNCE_MS = 1500

// License Type catalog — discipline + role aware. Curated from the bodies practitioners actually
// register with in India + an international fallback so the support agent picks from a finite
// realistic set rather than typing free-text. The two synthetic entries at the end cover cases
// where the relevant authority is irrelevant for the role (e.g. an in-house screener) or where
// the practitioner's licence is genuinely pending / not on hand.
const LICENSE_NOT_REQUIRED = 'License Not Required'
const LICENSE_NOT_AVAILABLE = 'License Not Available'

const LICENSE_OPTIONS_BY_KEY = {
  // Doctors — Indian medical bodies + international fallback.
  doctor: [
    'NMC (National Medical Commission)',
    'State Medical Council',
    'MCI (Medical Council of India)',
    'DCI (Dental Council of India)',
    'NMC Letter of Permission',
    'BMA (Foreign — equivalent registration)',
  ],
  // Mental-health professionals — psychotherapy track.
  'therapist:psychotherapy:psychologist': [
    'RCI (Rehabilitation Council of India)',
    'APA (American Psychological Association)',
    'BPS (British Psychological Society)',
  ],
  'therapist:psychotherapy:psychotherapist': [
    'RCI (Rehabilitation Council of India)',
    'IACP (Indian Association of Clinical Psychologists)',
    'BACP (British Association for Counselling and Psychotherapy)',
    'EAP (European Association for Psychotherapy)',
  ],
  'therapist:psychotherapy:counsellor': [
    'RCI (Rehabilitation Council of India)',
    'BACP (British Association for Counselling and Psychotherapy)',
    'IACP (Indian Association of Clinical Psychologists)',
    'NBCC (National Board for Certified Counselors)',
  ],
  'therapist:psychotherapy': [
    'RCI (Rehabilitation Council of India)',
    'IACP (Indian Association of Clinical Psychologists)',
    'APA (American Psychological Association)',
    'BACP (British Association for Counselling and Psychotherapy)',
  ],
  // Nutrition track.
  'therapist:nutrition-consultation:nutritionist': [
    'IDA (Indian Dietetic Association)',
    'CDR (Commission on Dietetic Registration)',
    'AND (Academy of Nutrition and Dietetics)',
    'ICMR-NIN Certified',
  ],
  'therapist:nutrition-consultation:dietitian': [
    'IDA (Indian Dietetic Association — RD)',
    'CDR (Commission on Dietetic Registration — RDN)',
    'AND (Academy of Nutrition and Dietetics)',
  ],
  'therapist:nutrition-consultation': [
    'IDA (Indian Dietetic Association)',
    'CDR (Commission on Dietetic Registration)',
    'AND (Academy of Nutrition and Dietetics)',
  ],
  // Screener / psychology-student fallbacks — typically no formal license required.
  screener: [
    'RCI (Rehabilitation Council of India)',
    'In-house screener certification',
  ],
  'psychology-student': [
    'University ID (current enrollment)',
    'Student membership — APA / BPS / IACP',
  ],
}

function buildLicenseOptions(form) {
  const t = form?.practitionerType
  const d = (form?.approvedTherapyDisciplines || [])[0]
  const r = form?.role
  const primary =
    (t && d && r && LICENSE_OPTIONS_BY_KEY[`${t}:${d}:${r}`]) ||
    (t && d && LICENSE_OPTIONS_BY_KEY[`${t}:${d}`]) ||
    (t && LICENSE_OPTIONS_BY_KEY[t]) ||
    []
  // The two synthetic entries always come at the bottom so they're a deliberate choice rather
  // than an accidental top-of-list pick.
  return [...primary, LICENSE_NOT_REQUIRED, LICENSE_NOT_AVAILABLE]
}

// Bio hint generator (Phase 14g) — builds a context-aware example bio used as Tab-to-fill on the
// Bio TextField. Uses the actual applicant name when filled, else a friendly stand-in. Pronouns
// stay gender-neutral ("they") so the agent can edit minimally for any practitioner.
function buildBioHint(form, options = {}) {
  const { specializationLabels = {} } = options
  const firstName = (form?.firstName || '').trim()
  const lastName = (form?.lastName || '').trim()
  const dummyName = form?.practitionerType === 'doctor' ? 'Dr. Anwei Pan' : 'Anwei Pan'
  const fullName = (firstName + ' ' + lastName).trim() || dummyName
  const discipline = (form?.approvedTherapyDisciplines || [])[0] || ''
  const specCodes = (form?.specializations || []).slice(0, 3)
  const specs = specCodes
    .map((code) => specializationLabels[code] || code)
    .filter(Boolean)
  const specPhrase = specs.length
    ? specs.length === 1
      ? specs[0]
      : specs.length === 2
        ? `${specs[0]} and ${specs[1]}`
        : `${specs[0]}, ${specs[1]}, and ${specs[2]}`
    : null

  if (form?.practitionerType === 'doctor') {
    const focus = specPhrase || 'general practice'
    return `${fullName} is a doctor with deep experience across ${focus}. They bring a calm, evidence-led approach to consultations and prescribe only when necessary. Sessions are offered both in-clinic and online, in English.`
  }
  if (form?.practitionerType === 'screener') {
    return `${fullName} is a mental-health screener trained to conduct intake assessments and route patients to the right kind of care. They listen carefully, work non-judgementally, and complete each screen in 15–20 minutes. Sessions are offered in English and Hindi.`
  }
  if (form?.practitionerType === 'therapist') {
    if (discipline === 'nutrition-consultation') {
      const focus = specPhrase || 'metabolic health, sports nutrition, and weight management'
      return `${fullName} is a nutritionist who works with adults around ${focus}. They build sustainable plans grounded in food preference, lifestyle, and evidence — no fads, no shame. Sessions are offered in English and Hindi.`
    }
    // Default to psychotherapy.
    const focus = specPhrase || 'anxiety, burnout, and life transitions'
    return `${fullName} is a clinical psychologist with experience helping adults navigate ${focus}. They blend CBT, mindfulness, and trauma-informed care, and offer sessions in English and Hindi.`
  }
  return ''
}

const PRACTITIONER_TYPE_HELPER = {
  doctor: 'Creates a doctor profile and enables consultation-oriented setup for clinic teams.',
  therapist: 'Creates a therapist profile with therapy events, reporting, and clinic-room assignment support.',
  screener: 'Creates a screener profile with screening events, target specialty mapping, and clinic setup.',
}

const AUDITOR_TYPE_OPTIONS = [
  { id: 'SUPERVISOR', title: 'Supervisor' },
  { id: 'LEARNER', title: 'Learner' },
]

const REPORT_LANGUAGE_OPTIONS = ['en', 'hi']

// Section anchors used by the sticky ChecklistPanel highlight + click-to-scroll. Each
// SectionCard is wrapped in a <Box id={...} /> anchor; the IntersectionObserver above tracks
// which one is in view and the checklist highlights the matching item.
const SECTION_PROFILE_SCOPE = 'section-profile-scope'
const SECTION_BASIC_PROFILE = 'section-basic-profile'
const SECTION_CREDENTIALS = 'section-credentials'
const SECTION_SERVICE_APPROVALS = 'section-service-approvals'
const SECTION_ASSESSMENT = 'section-assessment'
const SECTION_CLINIC_ASSIGNMENT = 'section-clinic-assignment'
const SECTION_ENGAGEMENT = 'section-engagement'
const SECTION_PAYMENT = 'section-payment'
const SECTION_AUDITOR = 'auditor-access-section'
const SECTION_SAVE = 'section-save'

const ALL_SECTION_IDS = [
  SECTION_PROFILE_SCOPE,
  SECTION_BASIC_PROFILE,
  SECTION_CREDENTIALS,
  SECTION_SERVICE_APPROVALS,
  SECTION_ASSESSMENT,
  SECTION_CLINIC_ASSIGNMENT,
  SECTION_ENGAGEMENT,
  SECTION_PAYMENT,
  SECTION_AUDITOR,
  SECTION_SAVE,
]

const CHECKLIST_TO_SECTION_ID = {
  'profile-created': SECTION_BASIC_PROFILE,
  'kyc-approved': SECTION_BASIC_PROFILE,
  'practitioner-enabled': SECTION_BASIC_PROFILE,
  'credentials-added': SECTION_CREDENTIALS,
  'services-enabled': SECTION_SERVICE_APPROVALS,
  'screening-target-specialty': SECTION_SERVICE_APPROVALS,
  'events-created': SECTION_SERVICE_APPROVALS,
  'clinic-attached': SECTION_CLINIC_ASSIGNMENT,
  'rooms-attached': SECTION_CLINIC_ASSIGNMENT,
  'assistants-linked': SECTION_CLINIC_ASSIGNMENT,
  'engagement-model': SECTION_ENGAGEMENT,
  'report-settings': SECTION_ENGAGEMENT,
  'auditor-access': SECTION_AUDITOR,
}

const ENGAGEMENT_APPLICABLE_TO_OPTIONS = [
  { id: 'aumhum-network-clinics', title: 'AUMHUM Network Clinics' },
  { id: 'private-patients', title: 'Private Patients' },
  { id: 'aumhum-direct-patients', title: 'AUMHUM Direct Patients' },
]

function createDefaultEngagementFeeModelIdsByApplicableTo() {
  return ENGAGEMENT_APPLICABLE_TO_OPTIONS.reduce((acc, option) => {
    acc[option.id] = ''
    return acc
  }, {})
}

const APPOINTMENT_CALENDAR_FIELDS = [
  {
    id: 'showAppointmentNumberHorizontally',
    label: 'Show appointment number horizontally',
  },
  {
    id: 'showPatientVisits',
    label: 'Show patient visit count',
  },
  {
    id: 'showPatientAgeAndGender',
    label: 'Show patient age and gender',
  },
  {
    id: 'showWaitingTime',
    label: 'Show waiting time',
  },
]

const EXPERIENCE_MONTH_OPTIONS = [
  { id: '01', title: 'January' },
  { id: '02', title: 'February' },
  { id: '03', title: 'March' },
  { id: '04', title: 'April' },
  { id: '05', title: 'May' },
  { id: '06', title: 'June' },
  { id: '07', title: 'July' },
  { id: '08', title: 'August' },
  { id: '09', title: 'September' },
  { id: '10', title: 'October' },
  { id: '11', title: 'November' },
  { id: '12', title: 'December' },
]

const EXPERIENCE_YEAR_OPTIONS = Array.from({ length: 55 }, (_item, index) => {
  const year = `${new Date().getFullYear() - index}`
  return { id: year, title: year }
})

const SECTION_PAPER_SX = {
  p: 3,
  borderRadius: 3,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 8px 30px rgba(15, 23, 42, 0.04)',
}

function getDefaultEventTypesForType(practitionerType) {
  switch (practitionerType) {
    case 'doctor':
      return [EVENT_TYPE_CONSULTATION]
    case 'therapist':
      return [EVENT_TYPE_THERAPY_SESSION]
    case 'screener':
      return [EVENT_TYPE_SCREENING]
    default:
      return []
  }
}

function getApprovedRoleForType(practitionerType) {
  switch (practitionerType) {
    case 'doctor':
      return 'ROLE_DOCTOR'
    case 'therapist':
      return 'ROLE_THERAPIST'
    case 'screener':
      return 'ROLE_SCREENER'
    default:
      return ''
  }
}

function createEmptyAssignment() {
  return {
    clinicBrandId: '',
    clinicLocationId: '',
    chamberRoomIds: [],
    clinicAssistantAssignmentIds: [],
  }
}

function createEmptyDocument() {
  return {
    title: '',
    fileId: '',
  }
}

function createEmptyRegistrationId() {
  return {
    typeId: '',
    id: '',
  }
}

function createEmptyAuditorAssignment() {
  return {
    mentorId: '',
    type: 'SUPERVISOR',
    chamberRoomIds: [],
  }
}

function createDefaultAppointmentCalendarSettings() {
  return {
    showAppointmentNumberHorizontally: false,
    showPatientVisits: true,
    showPatientAgeAndGender: true,
    showWaitingTime: true,
  }
}

function createEmptyForm(practitionerType = '') {
  return {
    practitionerId: '',
    practitionerSlug: '',
    practitionerType,
    userId: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    primaryContact: 'mobile',
    whatsappNumber: '',
    voiceCallNumber: '',
    emailVerified: false,
    mobileVerified: false,
    emailVerifiedOfflineBy: '',
    mobileVerifiedOfflineBy: '',
    mandatoryCredentialDocFileId: '',
    mandatoryCredentialDocTitle: '',
    gender: '',
    slug: '',
    title: '',
    profilePicId: '',
    shortDesc: '',
    longDesc: '',
    qualifications: [],
    experienceStartMonth: '',
    experienceStartYear: '',
    registrationIds: [],
    certificateDocuments: [],
    kycStatus: 'approved',
    enableServices: true,
    createDefaultEvents: true,
    allowedToPrescribe: practitionerType === 'doctor',
    allowSelfReschedule: true,
    allowSelfCancel: true,
    listableInWeb: false,
    specializations: [],
    // Therapy disciplines locked at KYC. Defaults to [psychotherapy] for therapists; ignored for
    // doctor/screener (the backend only honours it when approving the therapist role).
    approvedTherapyDisciplines:
      practitionerType === 'therapist' ? [THERAPY_DISCIPLINE_PSYCHOTHERAPY] : [],
    targetSpecialitySlugs: [],
    remarks: '',
    eventTypes: getDefaultEventTypesForType(practitionerType),
    eventCurrencyCode: 'inr',
    eventPrice: '',
    eventDurationInMins: '',
    clinicEventConfigurations: [],
    engagementApplicableTo: 'aumhum-network-clinics',
    engagementFeeModelId: '',
    engagementFeeModelIdsByApplicableTo: createDefaultEngagementFeeModelIdsByApplicableTo(),
    engagementCountryCode: 'IN',
    engagementCurrencyCode: 'inr',
    showFeeModelsOnMentorPortal: false,
    showPayoutReportsOnMentorPortal: false,
    signatureMediaId: '',
    licenseType: '',
    licenseNumber: '',
    appointmentCalendarSettings: createDefaultAppointmentCalendarSettings(),
    reportViewType: 'minimal',
    preferredReportLanguages: ['en'],
    allowedToAssess: practitionerType === 'therapist' ? false : undefined,
    assessmentListIds: [],
    clinicAssignments: [createEmptyAssignment()],
    auditorAssignments: [],
    // Phase 14k — incoming auditors: OTHER practitioners who audit THIS practitioner. Scoped per
    // chamber room so the audit relationship matches the physical room they share. Mirrors the
    // shape of auditorAssignments but in the inverse direction. Each entry: { chamberRoomId,
    // auditorPractitionerId, type ("SUPERVISOR" | "LEARNER") }.
    incomingAuditors: [],
  }
}

function trimValue(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeStringArray(values = []) {
  return values
    .map((value) => {
      if (typeof value === 'string') {
        return value.trim()
      }
      if (typeof value === 'object' && value !== null) {
        return trimValue(value.slug || value.id || value.title)
      }
      return ''
    })
    .filter(Boolean)
}

function resolveEngagementModelFeeModelId(engagementModel) {
  if (!engagementModel || !Array.isArray(engagementModel.feeRanges)) return ''
  const feeRange = engagementModel.feeRanges.find((range) => trimValue(range?.modelId))
  return trimValue(feeRange?.modelId)
}

function normalizeEngagementFeeModelSelections(detail, practitionerType) {
  const selections = createDefaultEngagementFeeModelIdsByApplicableTo()
  const validApplicableToIds = new Set(ENGAGEMENT_APPLICABLE_TO_OPTIONS.map((option) => option.id))

  if (detail?.engagementFeeModelIdsByApplicableTo && typeof detail.engagementFeeModelIdsByApplicableTo === 'object') {
    Object.entries(detail.engagementFeeModelIdsByApplicableTo).forEach(([applicableTo, feeModelId]) => {
      if (validApplicableToIds.has(applicableTo)) {
        selections[applicableTo] = trimValue(feeModelId)
      }
    })
  }

  const engagementModels = Array.isArray(detail?.engagementModels)
    ? detail.engagementModels
    : detail?.engagementModel
      ? [detail.engagementModel]
      : []
  engagementModels.forEach((engagementModel) => {
    const applicableTo = trimValue(engagementModel?.applicableTo)
    const feeModelId = resolveEngagementModelFeeModelId(engagementModel)
    if (validApplicableToIds.has(applicableTo) && feeModelId) {
      selections[applicableTo] = feeModelId
    }
  })

  const legacyApplicableTo = trimValue(detail?.engagementApplicableTo) || createEmptyForm(practitionerType).engagementApplicableTo
  const legacyFeeModelId = trimValue(detail?.engagementFeeModelId)
  if (validApplicableToIds.has(legacyApplicableTo) && legacyFeeModelId && !selections[legacyApplicableTo]) {
    selections[legacyApplicableTo] = legacyFeeModelId
  }

  return selections
}

function getSelectedEngagementFeeModelIds(form) {
  const selections = form?.engagementFeeModelIdsByApplicableTo || {}
  const selectedIds = Object.values(selections).map(trimValue).filter(Boolean)
  if (selectedIds.length > 0) return selectedIds
  const legacyFeeModelId = trimValue(form?.engagementFeeModelId)
  return legacyFeeModelId ? [legacyFeeModelId] : []
}

function firstFilledValue(...values) {
  return values.find((value) => trimValue(value)) || ''
}

function buildPaymentConfigPrefill(form, detail) {
  const practitioner = detail?.practitioner || {}
  const applicantDetails = detail?.applicantDetails || {}
  const banking = applicantDetails?.banking || {}
  const location = applicantDetails?.location || {}
  const displayName = firstFilledValue(
    [form?.firstName, form?.lastName].filter(Boolean).join(' '),
    form?.title,
    practitioner?.title,
    practitioner?.name,
  )

  return {
    entityId: firstFilledValue(form?.practitionerId, practitioner?.id),
    entityType: 'mentor',
    contactName: firstFilledValue(displayName, banking?.accountHolderName),
    contactMobile: firstFilledValue(form?.mobile, practitioner?.mobile),
    contactEmail: firstFilledValue(form?.email, practitioner?.email, detail?.user?.email),
    name: firstFilledValue(banking?.accountHolderName, displayName),
    address: firstFilledValue(location?.address),
    city: firstFilledValue(location?.city),
    pinCode: firstFilledValue(location?.pincode),
    country: firstFilledValue(location?.countryCode)?.toUpperCase() || 'IN',
    bankName: firstFilledValue(banking?.bankName),
    accountNumber: firstFilledValue(banking?.accountNumber),
    ifscCode: firstFilledValue(banking?.ifsc)?.toUpperCase() || '',
    upiId: firstFilledValue(banking?.upiId),
  }
}

function buildPractitionerIdentityParams(option = {}) {
  const practitionerId = trimValue(option?.id || option?.practitionerId)
  if (practitionerId) {
    return { practitionerId }
  }
  const practitionerSlug = trimValue(option?.slug || option?.practitionerSlug)
  return practitionerSlug ? { practitionerSlug } : {}
}

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) {
    return null
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function toIntegerOrNull(value) {
  if (value === '' || value === null || value === undefined) {
    return null
  }
  const parsed = parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : null
}

function getOptionLabel(option) {
  if (!option) {
    return ''
  }
  if (typeof option === 'string') {
    return option
  }
  return option.title || option.slug || option.id || ''
}

function getAllowedQueryType(allowedPractitionerTypes = []) {
  if (typeof window === 'undefined') {
    return ''
  }
  const queryType = trimValue(new URLSearchParams(window.location.search).get('type')).toLowerCase()
  return allowedPractitionerTypes.includes(queryType) ? queryType : ''
}

function getClinicAssignmentKey(clinicBrandId, clinicLocationId) {
  return `${trimValue(clinicBrandId)}::${trimValue(clinicLocationId)}`
}

function buildClinicEventConfigId(assignment, appointmentType) {
  return `${getClinicAssignmentKey(assignment?.clinicBrandId, assignment?.clinicLocationId)}::${
    appointmentType?.id || appointmentType?.type || appointmentType?.title || 'event'
  }`
}

function buildClinicEventConfigFromAppointmentType(assignment, appointmentType, defaultTargetSpecialtySlug = '') {
  const pricingDetail = appointmentType?.pricingDetail || {}
  const eventType = trimValue(appointmentType?.type || appointmentType?.eventType).toLowerCase()
  const fallbackTargetSpecialtySlug =
    eventType === EVENT_TYPE_SCREENING ? trimValue(defaultTargetSpecialtySlug) : ''
  const mode = trimValue(appointmentType?.mode).toLowerCase() || 'offline'
  const clinicalAssessmentIds = Array.isArray(appointmentType?.clinicalAssessmentIds)
    ? appointmentType.clinicalAssessmentIds
    : Array.isArray(appointmentType?.psychAssessmentIds)
      ? appointmentType.psychAssessmentIds
      : []
  // Assessment-type events are NOT seeded by default — the support agent (or the auto-enable
  // pass below) flips them on only when the appointment is part of an attached assessment list.
  // All other event types keep the seed=on default so existing flows aren't disrupted.
  const isAssessmentEvent = eventType === 'assessment' || eventType === 'assessments'
  return {
    id: buildClinicEventConfigId(assignment, appointmentType),
    clinicBrandId: assignment?.clinicBrandId || '',
    clinicLocationId: assignment?.clinicLocationId || '',
    clinicAppointmentTypeId: appointmentType?.id || '',
    eventType,
    title: appointmentType?.title || appointmentType?.name || '',
    targetSpecialtySlug:
      appointmentType?.targetSpecialtySlug ||
      appointmentType?.screeningType ||
      fallbackTargetSpecialtySlug,
    mode, // "online" → video call, "offline" → in-clinic
    clinicalAssessmentIds,
    isAssessmentEvent,
    currencyCode: trimValue(pricingDetail?.currencyId || appointmentType?.defaultCurrency || 'inr').toLowerCase(),
    price:
      pricingDetail?.discountedPrice ??
      pricingDetail?.introductoryPrice ??
      pricingDetail?.price ??
      '',
    durationInMins: appointmentType?.durationInMins ?? appointmentType?.duration ?? '',
    enabled: !isAssessmentEvent,
  }
}

function mergePractitionerOptionList(options = [], nextOption) {
  if (!nextOption?.id) {
    return options
  }
  const filtered = options.filter((option) => option?.id !== nextOption.id)
  return [...filtered, nextOption].sort((left, right) =>
    getOptionLabel(left).localeCompare(getOptionLabel(right)),
  )
}

function buildPractitionerOptionFromDetail(detail) {
  const practitioner = detail?.practitioner
  if (!practitioner?.id) {
    return null
  }
  return {
    id: practitioner.id,
    title: practitioner.title || practitioner.name || practitioner.id,
    subtitle: detail?.practitionerType || '',
    slug: practitioner.slug,
    type: 'practitioner',
    userId: practitioner.userId,
  }
}

function getConfiguredEventTypes(form) {
  const clinicEventTypes = (form?.clinicEventConfigurations || [])
    .filter((configuration) => Boolean(configuration?.enabled) && trimValue(configuration?.eventType))
    .map((configuration) => trimValue(configuration.eventType).toLowerCase())
  const dedupedClinicEventTypes = [...new Set(clinicEventTypes)]
  if (dedupedClinicEventTypes.length > 0) {
    return dedupedClinicEventTypes
  }
  return normalizeStringArray(form?.eventTypes).map((value) => value.toLowerCase())
}

function mapDetailToForm(detail) {
  const practitioner = detail?.practitioner || {}
  const practitionerType = detail?.practitionerType || ''
  const approvedRole = getApprovedRoleForType(practitionerType)
  const serviceDetail = practitioner?.teacherUnEditableFields?.servicesApproved?.[approvedRole] || {}
  const personalize = practitioner?.personalize || {}
  const linkedUser = detail?.user || practitioner?.user || {}
  const communicationPref = practitioner?.communicationPref || linkedUser?.communicationPref || {}

  return {
    ...createEmptyForm(practitionerType),
    practitionerId: practitioner.id || '',
    practitionerSlug: practitioner.slug || '',
    practitionerType,
    userId: practitioner.userId || linkedUser.userId || '',
    firstName: practitioner.firstName || '',
    lastName: practitioner.lastName || '',
    email: practitioner.email || '',
    mobile: practitioner.mobile || '',
    primaryContact:
      detail?.primaryContact ||
      practitioner?.primaryContact ||
      (practitioner.mobile ? 'mobile' : practitioner.email ? 'email' : 'mobile'),
    whatsappNumber: communicationPref?.whatsapp?.uid || '',
    voiceCallNumber: communicationPref?.voiceCall?.uid || '',
    emailVerified: Boolean(linkedUser?.emailVerified),
    mobileVerified: Boolean(linkedUser?.mobileVerified),
    emailVerifiedOfflineBy: linkedUser?.emailVerifiedOfflineBy || '',
    mobileVerifiedOfflineBy: linkedUser?.mobileVerifiedOfflineBy || '',
    mandatoryCredentialDocFileId:
      personalize?.mandatoryCredentialDocFileId ||
      detail?.mandatoryCredentialDocFileId ||
      '',
    mandatoryCredentialDocTitle:
      personalize?.mandatoryCredentialDocTitle ||
      detail?.mandatoryCredentialDocTitle ||
      '',
    gender: practitioner.gender || '',
    slug: practitioner.slug || '',
    title: practitioner.title || practitioner.name || '',
    profilePicId: practitioner.profilePicId || '',
    shortDesc: practitioner.shortDesc || '',
    longDesc: practitioner.longDesc || '',
    qualifications: Array.isArray(personalize?.qualifications) ? personalize.qualifications : [],
    experienceStartMonth: personalize?.experienceStartMonth || '',
    experienceStartYear: personalize?.experienceStartYear || '',
    registrationIds:
      Array.isArray(practitioner?.registrationIds) && practitioner.registrationIds.length > 0
        ? practitioner.registrationIds.map((registrationId) => ({
            typeId: registrationId?.typeId || '',
            id: registrationId?.id || '',
          }))
        : [],
    certificateDocuments:
      Array.isArray(personalize?.certificateIds) && personalize.certificateIds.length > 0
        ? personalize.certificateIds.map((fileId, index) => ({
            title: `Document ${index + 1}`,
            fileId,
          }))
        : [],
    signatureMediaId: personalize?.signatureMediaId || detail?.signatureMediaId || '',
    kycStatus: practitioner.kycStatus || 'approved',
    allowedToPrescribe: Boolean(practitioner.allowedToPrescribe) || practitionerType === 'doctor',
    allowSelfReschedule: practitioner.allowSelfReschedule !== false,
    allowSelfCancel: practitioner.allowSelfCancel !== false,
    listableInWeb: Boolean(practitioner.listableInWeb),
    specializations: Array.isArray(practitioner.specializedCategories)
      ? practitioner.specializedCategories
      : Array.isArray(serviceDetail?.specializations)
        ? serviceDetail.specializations
        : [],
    // Hydrate approved disciplines from the practitioner's existing KYC value when present;
    // default to [psychotherapy] for therapists with nothing set (matches the backend default).
    approvedTherapyDisciplines: Array.isArray(
      practitioner?.teacherUnEditableFields?.approvedTherapyDisciplines,
    ) && practitioner.teacherUnEditableFields.approvedTherapyDisciplines.length > 0
      ? practitioner.teacherUnEditableFields.approvedTherapyDisciplines
      : (practitionerType === 'therapist' ? [THERAPY_DISCIPLINE_PSYCHOTHERAPY] : []),
    targetSpecialitySlugs: Array.isArray(serviceDetail?.targetSpecialitySlugs)
      ? serviceDetail.targetSpecialitySlugs
      : [],
    eventTypes:
      Array.isArray(detail?.eventTypes) && detail.eventTypes.length > 0
        ? detail.eventTypes
        : getDefaultEventTypesForType(practitionerType),
    engagementApplicableTo:
      detail?.engagementApplicableTo ||
      detail?.engagementModel?.applicableTo ||
      createEmptyForm(practitionerType).engagementApplicableTo,
    engagementFeeModelId:
      detail?.engagementFeeModelId ||
      detail?.engagementModel?.feeRanges?.find((feeRange) => trimValue(feeRange?.modelId))?.modelId ||
      '',
    engagementFeeModelIdsByApplicableTo: normalizeEngagementFeeModelSelections(detail, practitionerType),
    engagementCountryCode: 'IN',
    engagementCurrencyCode: 'inr',
    clinicAssignments:
      Array.isArray(detail?.clinicAssignments) && detail.clinicAssignments.length > 0
        ? detail.clinicAssignments.map((assignment) => ({
            clinicBrandId: assignment?.clinicBrandId || '',
            clinicLocationId: assignment?.clinicLocationId || '',
            chamberRoomIds: (assignment?.chamberRooms || []).map((room) => room?.id).filter(Boolean),
            clinicAssistantAssignmentIds: (assignment?.clinicAssistants || [])
              .map((assistant) => assistant?.id)
              .filter(Boolean),
          }))
        : [createEmptyAssignment()],
    // Phase 14k — server returns incomingAuditors as flat list of room/auditor/type entries.
    incomingAuditors: Array.isArray(detail?.incomingAuditors)
      ? detail.incomingAuditors
          .filter((entry) => entry?.chamberRoomId && entry?.auditorPractitionerId)
          .map((entry) => ({
            chamberRoomId: entry.chamberRoomId,
            auditorPractitionerId: entry.auditorPractitionerId,
            type: entry.type || 'SUPERVISOR',
          }))
      : [],
  }
}

function buildDraftChecklist(form, hasExistingPractitioner) {
  const assignmentGroups = Array.isArray(form?.clinicAssignments) ? form.clinicAssignments : []
  const hasClinicAttachment = assignmentGroups.some(
    (assignment) => assignment?.clinicBrandId || assignment?.clinicLocationId,
  )
  const hasRoomAttachment = assignmentGroups.some(
    (assignment) => Array.isArray(assignment?.chamberRoomIds) && assignment.chamberRoomIds.length > 0,
  )
  const hasAssistantAttachment = assignmentGroups.some(
    (assignment) =>
      Array.isArray(assignment?.clinicAssistantAssignmentIds) &&
      assignment.clinicAssistantAssignmentIds.length > 0,
  )
  const eventTypes = getConfiguredEventTypes(form)
  const isScreener = form?.practitionerType === 'screener'
  const targetSpecialitySlugs = normalizeStringArray(form?.targetSpecialitySlugs)
  const hasCredentialSignal =
    normalizeStringArray(form?.qualifications).length > 0 ||
    (form?.registrationIds || []).some((registrationId) => trimValue(registrationId?.id))

  return [
    {
      id: 'profile-created',
      title: 'Profile Created',
      required: true,
      complete:
        hasExistingPractitioner ||
        (Boolean(trimValue(form?.firstName)) &&
          (Boolean(trimValue(form?.email)) || Boolean(trimValue(form?.mobile)))),
      detail: hasExistingPractitioner
        ? 'Existing practitioner selected'
        : 'First name plus email or mobile required',
    },
    {
      id: 'kyc-approved',
      title: 'KYC Approved',
      required: true,
      complete: trimValue(form?.kycStatus || 'approved') === 'approved',
      detail: 'This flow marks KYC as approved on save.',
    },
    {
      id: 'practitioner-enabled',
      title: 'Practitioner Enabled',
      required: true,
      complete: true,
      detail: 'The pre-approved create action keeps the practitioner active. Use the disable action to hold go-live.',
    },
    {
      id: 'credentials-added',
      title: 'Qualifications & Docs',
      required: false,
      complete: hasCredentialSignal,
      detail: hasCredentialSignal ? 'Credentials have been added.' : 'Optional but recommended.',
    },
    {
      id: 'services-enabled',
      title: 'Services Enabled',
      required: true,
      complete: Boolean(form?.enableServices),
      detail: form?.enableServices ? 'Service approval will run on save.' : 'Services are currently disabled.',
    },
    ...(isScreener
      ? [
          {
            id: 'screening-target-specialty',
            title: 'Screening Target Specialty',
            required: true,
            complete: targetSpecialitySlugs.length > 0,
            detail:
              targetSpecialitySlugs.length > 0
                ? targetSpecialitySlugs.join(', ')
                : 'Select the specialty this screener routes to.',
          },
        ]
      : []),
    {
      id: 'events-created',
      title: 'Events Created',
      required: true,
      complete: Boolean(form?.createDefaultEvents) || eventTypes.length > 0,
      detail:
        eventTypes.length > 0
          ? eventTypes.join(', ')
          : 'Default events will be created when enabled.',
    },
    {
      id: 'clinic-attached',
      title: 'Clinic Attached',
      required: true,
      complete: hasClinicAttachment,
      detail: hasClinicAttachment ? 'At least one clinic assignment is configured.' : 'Clinic is still pending.',
    },
    {
      id: 'rooms-attached',
      title: 'Rooms Attached',
      required: true,
      complete: hasRoomAttachment,
      detail: hasRoomAttachment ? 'Room mapping is configured.' : 'Chamber rooms are still pending.',
    },
    {
      id: 'assistants-linked',
      title: 'Assistants Linked',
      required: true,
      complete: hasAssistantAttachment,
      detail: hasAssistantAttachment ? 'Clinic assistants are linked.' : 'Clinic assistants are still pending.',
    },
    {
      id: 'engagement-model',
      title: 'Engagement Model',
      required: true,
      complete: getSelectedEngagementFeeModelIds(form).length > 0,
      detail:
        getSelectedEngagementFeeModelIds(form).length > 0
          ? `${getSelectedEngagementFeeModelIds(form).length} fee model selection(s).`
          : 'Select at least one fee model to complete pricing setup.',
    },
    {
      id: 'report-settings',
      title: 'Report Visibility',
      required: true,
      complete:
        Boolean(trimValue(form?.reportViewType)) ||
        (form?.preferredReportLanguages || []).length > 0 ||
        form?.showFeeModelsOnMentorPortal ||
        form?.showPayoutReportsOnMentorPortal,
      detail: 'Report defaults and portal visibility can be saved from this page.',
    },
    {
      id: 'auditor-access',
      title: 'Auditor Access',
      required: false,
      complete:
        Array.isArray(form?.auditorAssignments) &&
        form.auditorAssignments.some((assignment) => trimValue(assignment?.mentorId)),
      detail: 'Optional: add supervisor or learner auditor links.',
    },
  ]
}

function ChecklistPanel({ detail, checklist, activeSectionId, scrollContainerRef }) {
  function scrollToSection(sectionId) {
    if (typeof document === 'undefined' || !sectionId) return
    const el = document.getElementById(sectionId)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Phase 13 + bugfix — keep the active checklist item visible inside the sidebar's own
  // scroll container WITHOUT calling scrollIntoView on the child. scrollIntoView({block:'nearest'})
  // cascades to the window (or the SupportContentContainer's overflow:hidden scroll context) on
  // browsers / breakpoints where the inner panel isn't actually scrollable yet — that was the
  // mysterious "page jumps to top" regression users reported. Manual scrollTop confined to the
  // ref ensures we only ever move the inner panel.
  useEffect(() => {
    if (typeof document === 'undefined' || !activeSectionId) return
    const matchingItemId = Object.entries(CHECKLIST_TO_SECTION_ID).find(
      ([, sectionId]) => sectionId === activeSectionId,
    )?.[0]
    if (!matchingItemId) return
    const container = scrollContainerRef?.current
    const el = document.querySelector(`[data-checklist-item-id="${matchingItemId}"]`)
    if (!container || !el) return
    // Only auto-scroll when the panel has actual overflow (i.e. items are clipped). When
    // everything fits, leave the page alone so editing in the form never jolts the viewport.
    if (container.scrollHeight <= container.clientHeight + 4) return
    const containerRect = container.getBoundingClientRect()
    const itemRect = el.getBoundingClientRect()
    if (itemRect.top < containerRect.top || itemRect.bottom > containerRect.bottom) {
      const delta = itemRect.top - containerRect.top - 16
      container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' })
    }
  }, [activeSectionId, scrollContainerRef])

  const completedRequiredItems = checklist.filter((item) => item.required && item.complete).length
  const totalRequiredItems = checklist.filter((item) => item.required).length
  const completionPercent =
    totalRequiredItems > 0 ? Math.round((completedRequiredItems / totalRequiredItems) * 100) : 0

  return (
    <Stack spacing={1.5}>
      <Paper sx={{ ...SECTION_PAPER_SX, p: 1.5 }}>
        <Stack spacing={1.25}>
          {/* Compact header — keeps the sidebar tall enough to fit in viewport without an
              internal scrollbar on most screens. */}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                {detail?.practitioner?.title ||
                  detail?.practitioner?.name ||
                  detail?.practitioner?.id ||
                  'Onboarding Checklist'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {detail?.practitioner
                  ? PRACTITIONER_TYPE_LABELS[detail?.practitionerType] || 'Practitioner'
                  : 'Pre-save · checklist preview'}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={`${completionPercent}%`}
              color={completionPercent === 100 ? 'success' : 'default'}
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              variant="outlined"
              label={`${completedRequiredItems}/${totalRequiredItems} required`}
              color={completionPercent === 100 ? 'success' : 'default'}
            />
            {detail?.clinicAssignments?.length > 0 && (
              <Chip size="small" variant="outlined" label={`${detail.clinicAssignments.length} clinic`} />
            )}
            {typeof detail?.engagementModelCount === 'number' && detail.engagementModelCount > 0 && (
              <Chip size="small" variant="outlined" label={`${detail.engagementModelCount} fee`} />
            )}
          </Stack>

          {/* Roles + approved services condensed into a single row of small chips so the
              sidebar stays compact. */}
          {(Array.isArray(detail?.currentRoles) && detail.currentRoles.length > 0) ||
          (Array.isArray(detail?.approvedServiceRoles) && detail.approvedServiceRoles.length > 0) ? (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {(detail?.currentRoles || []).map((role) => (
                <Chip
                  key={`r-${role}`}
                  label={role.replace('ROLE_', '').replaceAll('_', ' ')}
                  size="small"
                  sx={{ height: 20, fontSize: 11 }}
                />
              ))}
              {(detail?.approvedServiceRoles || []).map((role) => (
                <Chip
                  key={`a-${role}`}
                  label={role.replace('ROLE_', '').replaceAll('_', ' ')}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              ))}
            </Stack>
          ) : null}

          <Divider sx={{ my: 0.25 }} />

          <Stack spacing={0.75}>
            {checklist.map((item) => {
              const sectionId = CHECKLIST_TO_SECTION_ID[item.id]
              const isActive = sectionId && sectionId === activeSectionId
              return (
                <Box
                  key={item.id}
                  data-checklist-item-id={item.id}
                  onClick={sectionId ? () => scrollToSection(sectionId) : undefined}
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    cursor: sectionId ? 'pointer' : 'default',
                    border: isActive
                      ? '2px solid rgba(37, 99, 235, 0.6)'
                      : item.complete
                        ? '1px solid rgba(22, 163, 74, 0.18)'
                        : '1px solid rgba(148, 163, 184, 0.3)',
                    boxShadow: isActive ? '0 2px 8px rgba(37, 99, 235, 0.18)' : 'none',
                    backgroundColor: isActive
                      ? 'rgba(239, 246, 255, 0.95)'
                      : item.complete
                        ? 'rgba(240, 253, 244, 0.9)'
                        : item.required
                          ? 'rgba(248, 250, 252, 0.95)'
                          : 'rgba(255, 250, 240, 0.95)',
                    transition: 'border 0.15s ease, box-shadow 0.15s ease',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {item.complete ? (
                      <CheckCircleOutline color="success" fontSize="small" />
                    ) : (
                      <RadioButtonUnchecked color={item.required ? 'disabled' : 'warning'} fontSize="small" />
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 700 : 600,
                          color: isActive ? 'primary.main' : 'text.primary',
                          lineHeight: 1.3,
                        }}
                      >
                        {item.title}
                      </Typography>
                      {/* Detail only renders for the active item — keeps the sidebar compact so
                          the entire checklist usually fits in the viewport, while still giving
                          the agent context for the section they're working on. */}
                      {isActive && item.detail && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {item.detail}
                        </Typography>
                      )}
                    </Box>
                    {!item.required && (
                      <Chip
                        label="Opt"
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
                      />
                    )}
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  )
}

function getOrdinalSuffix(n) {
  const num = Number(n)
  if (!Number.isFinite(num) || num <= 0) return String(n ?? '')
  const j = num % 10
  const k = num % 100
  if (j === 1 && k !== 11) return `${num}st`
  if (j === 2 && k !== 12) return `${num}nd`
  if (j === 3 && k !== 13) return `${num}rd`
  return `${num}th`
}

function formatPayoutSessions(payout) {
  const startId = Number(payout?.startId) || 1
  const endId = payout?.endId == null || payout?.endId === '' ? null : Number(payout.endId)
  if (endId == null) return `${getOrdinalSuffix(startId)} Session and Onwards`
  if (startId === endId) return `${getOrdinalSuffix(startId)} Session`
  return `${getOrdinalSuffix(startId)} ${endId - startId > 1 ? 'to' : 'and'} ${getOrdinalSuffix(endId)} Session`
}

/**
 * Engagement-model preview drawer with inline editor. The view mode replicates the Control
 * Center practitioner-models display (Sessions / Therapist Share / Comment CSS grid). Edit mode
 * opens a per-payout editable table with add/remove rows + ref fee range fields. Save posts to
 * the support /fee-model/save endpoint and patches the local bootstrap option's preview so the
 * next preview shows fresh data.
 */
function FeeModelPreviewDrawer({ open, feeModelOption, onClose, onSaved }) {
  const [editMode, setEditMode] = useState(false)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)

  const preview = feeModelOption?.preview

  useEffect(() => {
    if (!open) {
      setEditMode(false)
      setDraft(null)
      return
    }
    if (preview) {
      setDraft({
        ...preview,
        refFeeRange: { ...(preview.refFeeRange || {}) },
        payoutList: Array.isArray(preview.payoutList)
          ? preview.payoutList.map((p) => ({ ...p }))
          : [],
      })
    }
  }, [open, preview])

  const source = editMode ? draft : preview
  const refRange = source?.refFeeRange || {}
  const payouts = Array.isArray(source?.payoutList) ? source.payoutList : []
  const hasComments = payouts.some((p) => !!p?.comment)

  function updateDraftPayout(index, changes) {
    setDraft((prev) => ({
      ...prev,
      payoutList: prev.payoutList.map((p, i) => (i === index ? { ...p, ...changes } : p)),
    }))
  }

  function addPayoutRow() {
    setDraft((prev) => ({
      ...prev,
      payoutList: [...(prev.payoutList || []), { startId: '', endId: '', partnerFee: '', comment: '' }],
    }))
  }

  function removePayoutRow(index) {
    setDraft((prev) => ({
      ...prev,
      payoutList: prev.payoutList.filter((_p, i) => i !== index),
    }))
  }

  async function handleSave() {
    if (!draft) return
    setSaving(true)
    const payload = {
      ...draft,
      payoutList: (draft.payoutList || []).map((p) => ({
        ...p,
        startId: p.startId === '' || p.startId == null ? null : Number(p.startId),
        endId: p.endId === '' || p.endId == null ? null : Number(p.endId),
        partnerFee:
          p.partnerFee === '' || p.partnerFee == null ? null : Number(p.partnerFee),
      })),
      refFeeRange: {
        ...(draft.refFeeRange || {}),
        min:
          draft.refFeeRange?.min === '' || draft.refFeeRange?.min == null
            ? null
            : Number(draft.refFeeRange.min),
        max:
          draft.refFeeRange?.max === '' || draft.refFeeRange?.max == null
            ? null
            : Number(draft.refFeeRange.max),
      },
    }
    const resp = await saveSupportFeeModel(payload)
    setSaving(false)
    if (resp.ok) {
      toast.success('Fee model saved.')
      // The save endpoint returns whatever FeeModelConfigService.saveFeeModel returns; pull the
      // fee-model out of the wrapped response if present, else fall back to our payload.
      const saved = resp.data?.['fee-model'] || resp.data?.feeModel || payload
      onSaved?.(saved)
      setEditMode(false)
    } else {
      toast.error(resp.message || 'Could not save fee model.')
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 560 }, maxWidth: '100%' } }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
              {source?.title || feeModelOption?.title || 'Fee model'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {editMode ? 'Editing — changes save to fee_models on commit.' : 'Read-only preview.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {!editMode && (
              <Button size="small" variant="outlined" onClick={() => setEditMode(true)}>
                Edit
              </Button>
            )}
            {editMode && (
              <Button
                size="small"
                onClick={() => {
                  setEditMode(false)
                  setDraft(preview ? { ...preview, refFeeRange: { ...(preview.refFeeRange || {}) }, payoutList: (preview.payoutList || []).map((p) => ({ ...p })) } : null)
                }}
              >
                Cancel edits
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {source?.applicableTo && (
            <Chip size="small" label={`Applies to: ${source.applicableTo}`} variant="outlined" />
          )}
          {source?.countryCode && (
            <Chip size="small" label={`Country: ${source.countryCode}`} variant="outlined" />
          )}
          {source?.currencyCode && (
            <Chip size="small" label={`Currency: ${source.currencyCode}`} variant="outlined" />
          )}
          {source?.active != null && (
            <Chip
              size="small"
              color={source.active ? 'success' : 'default'}
              label={source.active ? 'Active' : 'Inactive'}
            />
          )}
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Reference fee range
            </Typography>
            {editMode ? (
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Min"
                  type="number"
                  value={draft?.refFeeRange?.min ?? ''}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      refFeeRange: { ...(prev?.refFeeRange || {}), min: e.target.value },
                    }))
                  }
                />
                <TextField
                  size="small"
                  label="Max"
                  type="number"
                  value={draft?.refFeeRange?.max ?? ''}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      refFeeRange: { ...(prev?.refFeeRange || {}), max: e.target.value },
                    }))
                  }
                />
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {refRange.min ?? '—'} {source?.currencyCode || ''} – {refRange.max ?? '—'}{' '}
                {source?.currencyCode || ''}
              </Typography>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Payout list ({payouts.length})
              </Typography>
              {editMode && (
                <Button size="small" startIcon={<AddCircleOutline />} onClick={addPayoutRow}>
                  Add row
                </Button>
              )}
            </Stack>

            {/* Header row — replicates Control Center's StyledPayoutListView grid. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: hasComments || editMode ? '1.4fr 0.9fr 1.5fr 0.4fr' : '1.4fr 0.9fr 1.5fr',
                gap: 1,
                alignItems: 'center',
                fontWeight: 700,
                fontSize: 13,
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 0.75,
                mb: 0.5,
              }}
            >
              <span>Sessions</span>
              <span>Therapist Share</span>
              <span>{hasComments || editMode ? 'Comment' : ''}</span>
              {(hasComments || editMode) && <span />}
            </Box>

            {payouts.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No payouts configured.
              </Typography>
            ) : (
              <Stack spacing={editMode ? 1 : 0.5} sx={{ mt: 0.5 }}>
                {payouts.map((payout, index) =>
                  editMode ? (
                    <Box
                      key={`payout-edit-${index}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.4fr 0.9fr 1.5fr 0.4fr',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Start"
                          value={payout.startId ?? ''}
                          onChange={(e) => updateDraftPayout(index, { startId: e.target.value })}
                          sx={{ width: 70 }}
                        />
                        <Typography variant="caption">to</Typography>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="End"
                          value={payout.endId ?? ''}
                          onChange={(e) => updateDraftPayout(index, { endId: e.target.value })}
                          sx={{ width: 70 }}
                        />
                      </Stack>
                      <TextField
                        size="small"
                        type="number"
                        placeholder="%"
                        value={payout.partnerFee ?? ''}
                        onChange={(e) => updateDraftPayout(index, { partnerFee: e.target.value })}
                      />
                      <TextField
                        size="small"
                        placeholder="Comment"
                        value={payout.comment ?? ''}
                        onChange={(e) => updateDraftPayout(index, { comment: e.target.value })}
                      />
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removePayoutRow(index)}
                        sx={{ minWidth: 'auto', px: 0.5 }}
                      >
                        <DeleteOutline fontSize="small" />
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      key={`payout-view-${index}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: hasComments ? '1.4fr 0.9fr 1.5fr' : '1.4fr 0.9fr 1.5fr',
                        gap: 1,
                        alignItems: 'center',
                        py: 0.5,
                        borderBottom: index < payouts.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <span>{formatPayoutSessions(payout)}</span>
                      <span>{payout.partnerFee != null ? `${payout.partnerFee}%` : '—'}</span>
                      <span>{payout.comment || ''}</span>
                    </Box>
                  ),
                )}
              </Stack>
            )}
          </Paper>
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose}>Close</Button>
          {editMode && (
            <Button variant="contained" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save fee model'}
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  )
}

/**
 * Tile picker rendered when the onboarding URL has no ?type. Three tiles
 * (Doctor / Therapist / Screener) deep-link into the same page with the right ?type so the
 * existing scoped-flow rendering kicks in.
 */
function PractitionerOnboardingTypePicker() {
  const navigate = useNavigate()
  const tiles = [
    {
      id: 'doctor',
      label: 'Doctor Onboarding',
      description: 'Onboard a clinic doctor — KYC, registration IDs, prescribing settings.',
      Icon: LocalHospital,
      gradient:
        'linear-gradient(135deg, rgba(0, 43, 107, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
    },
    {
      id: 'therapist',
      label: 'Therapist Onboarding',
      description: 'Onboard a psychotherapy or nutrition-consultation therapist.',
      Icon: Psychology,
      gradient:
        'linear-gradient(135deg, rgba(216, 101, 0, 0.95) 0%, rgba(194, 65, 12, 0.95) 100%)',
    },
    {
      id: 'screener',
      label: 'Screener Onboarding',
      description: 'Onboard a target-specialty screener with clinical access.',
      Icon: PersonSearch,
      gradient:
        'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(67, 56, 202, 0.95) 100%)',
    },
  ]
  return (
    <>
      <SupportHeader heading="Practitioner Onboarding" showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Pick the practitioner type to onboard. Each tile opens the form pre-scoped to that type
          so only the relevant sections (KYC fields, signature, license info, target-specialty,
          etc.) are shown.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          }}
        >
          {tiles.map(({ id, label, description, Icon, gradient }) => (
            <Paper
              key={id}
              elevation={0}
              role="button"
              tabIndex={0}
              onClick={() => {
                navigate(`/support-home/practitioner-management/onboarding?type=${id}`)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  navigate(
                    `/support-home/practitioner-management/onboarding?type=${id}`,
                  )
                }
              }}
              sx={{
                // Phase 14m fix — make the gradient-painted Paper a real clickable element.
                // Earlier the click handler sat on a Paper that turned out to render with
                // pointer-events:none in some MUI v5 themes when an ancestor flex container
                // shrank past its content; the explicit position/zIndex/pointerEvents combo
                // pins it as a focusable button so taps register on every browser we hit.
                p: 3,
                borderRadius: 3,
                cursor: 'pointer',
                background: gradient,
                color: 'white',
                position: 'relative',
                zIndex: 1,
                pointerEvents: 'auto',
                userSelect: 'none',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                },
                '&:focus-visible': {
                  outline: '2px solid rgba(255,255,255,0.7)',
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  mb: 1.5,
                }}
              >
                <Icon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                {description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    </>
  )
}

function SectionCard({ title, description, children, action }) {
  return (
    <Paper sx={SECTION_PAPER_SX}>
      <Stack spacing={2}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
        {children}
      </Stack>
    </Paper>
  )
}

export default function PractitionerOnboardingPage() {
  const { allRolesInBoolean } = useServerAuth()
  const [bootstrapState, setBootstrapState] = useState({
    loading: true,
    error: '',
    data: null,
  })
  const [detailState, setDetailState] = useState({
    loading: false,
    error: '',
    data: null,
  })
  const [supplementaryLoading, setSupplementaryLoading] = useState(false)
  const [patientContactVisibility, setPatientContactVisibility] = useState(null)
  const [loadingPatientContactVisibility, setLoadingPatientContactVisibility] = useState(false)
  const [savingPatientContactVisibility, setSavingPatientContactVisibility] = useState(false)
  const [patientContactVisibilityError, setPatientContactVisibilityError] = useState('')
  const [savingMode, setSavingMode] = useState('')
  const [selectedPractitioner, setSelectedPractitioner] = useState(null)
  const [form, setForm] = useState(createEmptyForm())
  const [clinicAppointmentTypesByKey, setClinicAppointmentTypesByKey] = useState({})
  // Specialization category options fetched once for the page lifetime. Each entry is
  // { id, name, categoryType: 'doctor' | 'therapist' }; we filter by practitionerType when
  // rendering the Specializations dropdown.
  const [categoryOptions, setCategoryOptions] = useState([])
  const [specializationCatalog, setSpecializationCatalog] = useState(null)
  const [qualificationCatalog, setQualificationCatalog] = useState(null)
  const [existenceCheck, setExistenceCheck] = useState({
    email: { count: null, samples: [], lookedUpFor: '' },
    mobile: { count: null, samples: [], lookedUpFor: '' },
  })
  const [showExistenceModal, setShowExistenceModal] = useState(null) // 'email' | 'mobile' | null
  const [verifyOfflinePending, setVerifyOfflinePending] = useState({ email: false, mobile: false })
  // Per-assignment "Therapy Session" / "Assessment" tab selection. Keyed by clinic-assignment key
  // so each assignment row remembers its own selection independently. Defaults to therapy-session.
  const [activeEventTabByAssignment, setActiveEventTabByAssignment] = useState({})
  // Engagement-model preview dialog. Toggled by the "Preview" button next to the fee-model picker.
  const [feeModelPreviewOption, setFeeModelPreviewOption] = useState(null)
  // Assessment-list preview dialog. Set to the assessment list id whose preview should open.
  const [assessmentPreviewId, setAssessmentPreviewId] = useState(null)
  // Bookmarkable "dedicated page" mode for Auditor Access. When the URL is the /auditor sub-route
  // the page auto-scrolls to the Auditor Access section after data loads, so the agent can deep-
  // link straight into it. Other sections stay rendered for context (and so the agent can still
  // save the rest if they need to). Declared BEFORE the IntersectionObserver effect so the
  // dependency array can reference it without hitting a temporal-dead-zone ReferenceError.
  const isAuditorDedicatedView = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.location.pathname.endsWith('/practitioner-management/onboarding/auditor'),
    [],
  )

  // Active section id (drives the ChecklistPanel highlight on the left). Set via the
  // IntersectionObserver below — whichever section anchor is currently in view wins.
  const [activeSectionId, setActiveSectionId] = useState(SECTION_BASIC_PROFILE)
  // Ref to the sticky-sidebar's scroll container. Used by ChecklistPanel to keep the active
  // checklist item visible without ever calling scrollIntoView (which can leak to window scroll).
  const checklistScrollRef = useRef(null)
  // Refs to required-section anchors so submit-time validation can scroll the first error into
  // view. Keyed by field-name so the same map covers every required field across sections.
  const fieldRefs = useRef({})
  const setFieldRef = useCallback((key) => (node) => {
    if (node) fieldRefs.current[key] = node
    else delete fieldRefs.current[key]
  }, [])
  // Submit-time field errors. Empty during normal editing — only populated when handleSave
  // bounces because of missing required fields. Cleared as soon as the user starts editing.
  const [submitErrors, setSubmitErrors] = useState({})

  // Auto-save draft (Phase 14g). `pendingDraft` holds whatever localStorage carried for the
  // current practitioner+type when the page mounted; surfaced via DraftRestoreBanner. Once the
  // agent acts (Resume / Discard) we clear it and revert to silent auto-save behavior.
  const [pendingDraft, setPendingDraft] = useState(null)
  // Suppresses the next auto-save tick whenever we hydrate the form from a non-edit source
  // (server detail load, draft restore, fresh template). Otherwise hydrating from server would
  // overwrite the localStorage draft with a clean copy and lose the agent's in-flight work.
  const draftAutosaveSuppressedRef = useRef(true)
  // "Just saved" timestamp surfaced as a tiny chip near the save buttons so the agent has visual
  // confirmation that auto-save is alive. Updated by the debounced effect, not by submit.
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState(null)

  // Phase 14k — drawer toggles for the payment configuration and auditor access editors. Both
  // sections are dense enough to warrant a focused side panel rather than competing with the
  // form for vertical space; the main page now shows a read-only summary so the agent can spot
  // gaps without opening the editor.
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false)
  const [auditorDrawerOpen, setAuditorDrawerOpen] = useState(false)
  // Phase 14al — index of the clinicAssignments[] entry currently shown inside the new
  // location drawer (rooms / assistants / events). Null when the drawer is closed. Indexing
  // by entry index (not (brand, location) key) keeps it stable across re-renders that
  // shuffle the underlying array order — closure captures the index at click time.
  const [activeAssignmentIndex, setActiveAssignmentIndex] = useState(null)
  // Phase 14al Slice 2 — per-(brand, location) focus mode inside the location drawer.
  // 'rooms' | 'assistants' | 'events' | 'all'. Defaults to 'rooms' (handled at read time).
  const [focusModeByAssignment, setFocusModeByAssignment] = useState({})
  // Phase 14al Slice 3 — server-truth role-event-type policy fetched on mount. Shape:
  // { therapist: ['therapy-session', 'assessment'], doctor: ['consultation', ...], ... }.
  // computeLocationStatus reads from this when it has the practitioner's type, falling back
  // to the hardcoded ROLE_ALLOWED_EVENT_TYPES constant if the fetch hasn't returned yet.
  const [roleEventTypePolicies, setRoleEventTypePolicies] = useState(null)
  // Phase 14l — event-edit drawer. Holds the ID of the clinicEventConfiguration the agent is
  // editing across all fields (title / type / mode / duration / price / target speciality /
  // enabled). The inline row editor stays for quick toggles; the drawer is for the long tail.
  const [eventEditDrawerConfigId, setEventEditDrawerConfigId] = useState(null)
  // Phase 14p — Professional Credentials document viewer. Holds { fileId, title } for the
  // document the agent is currently previewing in the side drawer. The drawer renders a PDF
  // <iframe> or an <img> based on extension and exposes an "Open in new tab" button so the
  // agent can also pop the file out into a real browser tab when they need a wider viewer.
  const [credentialDocViewer, setCredentialDocViewer] = useState(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!window.IntersectionObserver) return
    const observerOptions = { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    let activeIds = new Set()
    const sortedSectionIds = ALL_SECTION_IDS
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) activeIds.add(entry.target.id)
        else activeIds.delete(entry.target.id)
      })
      // Pick the topmost (in source order) visible anchor as the active one.
      const next = sortedSectionIds.find((id) => activeIds.has(id))
      if (next) setActiveSectionId(next)
    }, observerOptions)
    sortedSectionIds.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [bootstrapState.loading, isAuditorDedicatedView])
  const [slugState, setSlugState] = useState({
    suggestedSlug: '',
    loadingSuggestion: false,
    validating: false,
    available: null,
    message: 'Slug is auto-suggested from the name and checked for uniqueness.',
  })

  // Auto-save draft — debounced server-side POST on every form change. Drafts live in
  // practitioner_onboarding_drafts (one row per practitioner-id+type+agent), separately from
  // the formal /save endpoint. Skipped on the very first render after a hydration (server
  // detail, draft restore, fresh template) so the just-loaded data isn't immediately re-saved.
  useEffect(() => {
    if (draftAutosaveSuppressedRef.current) {
      draftAutosaveSuppressedRef.current = false
      return
    }
    const timer = setTimeout(async () => {
      try {
        await postSPDataWithoutParams(
          buildDraftEndpoint(form.practitionerId, form.practitionerType),
          form,
        )
        setLastDraftSavedAt(Date.now())
      } catch {
        // Best-effort — a network blip doesn't surface to the agent; the next change retries.
      }
    }, DRAFT_AUTOSAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [form])

  // Look for a server-side draft once the page knows which practitioner is loaded. The banner
  // surfaces only when the saved draft is meaningfully newer than what the form currently shows.
  useEffect(() => {
    if (bootstrapState.loading) return
    let cancelled = false
    ;(async () => {
      try {
        const resp = await getSPDataWithParams(
          '/api/secure/cc/support/practitioner-onboarding/draft',
          {
            'practitioner-id': form.practitionerId || '',
            type: form.practitionerType || '',
          },
        )
        if (cancelled) return
        if (!isCommonResponseSuccessfulV2(resp)) {
          setPendingDraft(null)
          return
        }
        const data = getDataMapFromCommonResponse(resp)?.draft
        if (!data || !data.payload) {
          setPendingDraft(null)
          return
        }
        // Compare a cheap subset of the fields most likely to differ between server and draft so
        // we don't propose to "restore" a draft that's identical to what's already on screen.
        const draftForm = data.payload
        const interestingFromDraft = JSON.stringify({
          shortDesc: draftForm.shortDesc,
          longDesc: draftForm.longDesc,
          qualifications: draftForm.qualifications,
          registrationIds: draftForm.registrationIds,
          specializations: draftForm.specializations,
          licenseType: draftForm.licenseType,
          licenseNumber: draftForm.licenseNumber,
        })
        const interestingFromForm = JSON.stringify({
          shortDesc: form.shortDesc,
          longDesc: form.longDesc,
          qualifications: form.qualifications,
          registrationIds: form.registrationIds,
          specializations: form.specializations,
          licenseType: form.licenseType,
          licenseNumber: form.licenseNumber,
        })
        if (interestingFromDraft === interestingFromForm) {
          setPendingDraft(null)
          return
        }
        setPendingDraft({
          form: draftForm,
          savedAt: data.modifiedTime ? Date.parse(data.modifiedTime) : null,
          savedByUserId: data.savedByUserId,
        })
      } catch {
        if (!cancelled) setPendingDraft(null)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapState.loading, form.practitionerId, form.practitionerType])

  function handleDraftRestore() {
    if (!pendingDraft?.form) return
    draftAutosaveSuppressedRef.current = true
    setForm((prev) => ({ ...prev, ...pendingDraft.form }))
    setPendingDraft(null)
    toast.success('Draft restored. Review the fields and Save when ready.')
  }

  async function handleDraftDiscard() {
    try {
      await deleteSPDataWithParams('/api/secure/cc/support/practitioner-onboarding/draft', {
        'practitioner-id': form.practitionerId || 'new',
        type: form.practitionerType || '',
      })
    } catch {
      // Server-side delete is best-effort — the banner clears either way.
    }
    setPendingDraft(null)
    setLastDraftSavedAt(null)
  }

  useEffect(() => {
    let active = true

    async function loadBootstrap() {
      setBootstrapState({ loading: true, error: '', data: null })
      const response = await getPractitionerOnboardingBootstrap()

      if (!active) {
        return
      }

      if (response.ok && response.data) {
        const requestedType = getAllowedQueryType(response.data.allowedPractitionerTypes || [])
        const defaultType = requestedType || response.data.allowedPractitionerTypes?.[0] || ''
        setBootstrapState({ loading: false, error: '', data: response.data })
        setForm((prev) => ({
          ...createEmptyForm(defaultType),
          ...prev,
          practitionerType: prev.practitionerType || defaultType,
          allowedToPrescribe:
            prev.practitionerType === 'doctor' || defaultType === 'doctor'
              ? true
              : Boolean(prev.allowedToPrescribe),
          eventTypes:
            Array.isArray(prev.eventTypes) && prev.eventTypes.length > 0
              ? prev.eventTypes
              : getDefaultEventTypesForType(defaultType),
        }))
        return
      }

      setBootstrapState({
        loading: false,
        error: response.message || 'Unable to load onboarding references.',
        data: null,
      })
    }

    loadBootstrap()
    // Phase 14al Slice 3 — load the role-event-types policy alongside the bootstrap. Failure
    // is non-fatal (the page falls back to the hardcoded ROLE_ALLOWED_EVENT_TYPES constant).
    ;(async () => {
      const policyResp = await getRoleEventTypesPolicy()
      if (!active) return
      if (policyResp?.ok && policyResp.data?.policies) {
        setRoleEventTypePolicies(policyResp.data.policies)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  // Phase 14n — auto-load the practitioner from a deep-link query param. Pages like Disabled
  // Practitioners, Applicants, and Therapists link here with ?practitioner-id=… (and sometimes
  // ?practitioner-slug=…). Without this effect the form just opened blank — the agent saw an
  // empty "create new" form even though the URL was scoped to a specific practitioner. We
  // resolve once after bootstrap loads (the practitioners list lives there) and only when
  // nothing is selected yet, so a manual selection later in the session isn't clobbered.
  const deepLinkResolvedRef = useRef(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (bootstrapState.loading) return
    if (deepLinkResolvedRef.current) return
    if (selectedPractitioner) return
    const params = new URLSearchParams(window.location.search)
    const practitionerId = trimValue(params.get('practitioner-id'))
    const practitionerSlug = trimValue(params.get('practitioner-slug'))
    if (!practitionerId && !practitionerSlug) {
      deepLinkResolvedRef.current = true
      return
    }
    const practitionerOptions = bootstrapState.data?.practitioners || []
    const matched =
      (practitionerId
        ? practitionerOptions.find((p) => p?.id === practitionerId)
        : null) ||
      (practitionerSlug
        ? practitionerOptions.find((p) => p?.slug === practitionerSlug)
        : null) ||
      // Bootstrap's practitioners list filters out kyc-disabled rows in some flows; fall back
      // to a synthetic option so loadPractitionerDetail can still resolve via id+slug. The
      // detail endpoint queries the practitioner directly so this works even when the row
      // isn't in the bootstrap response.
      (practitionerId || practitionerSlug
        ? { id: practitionerId || '', slug: practitionerSlug || '', title: practitionerId || practitionerSlug }
        : null)
    deepLinkResolvedRef.current = true
    if (matched) {
      loadPractitionerDetail(matched)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapState.loading, bootstrapState.data?.practitioners])

  // Auto-scroll to Auditor Access when the user landed on the dedicated /auditor sub-route, so
  // the bookmarkable URL acts like a deep-link into that section.
  useEffect(() => {
    if (!isAuditorDedicatedView) return
    if (bootstrapState.loading) return
    const target = document.getElementById('auditor-access-section')
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [isAuditorDedicatedView, bootstrapState.loading])

  // Fetch the doctor/therapist specialization catalogue once so the Specializations field can be
  // rendered as a constrained dropdown rather than a free-form tag input. Mirrors the source the
  // Service Approvals dialog uses, so the two surfaces stay aligned.
  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const resp = await getSPDataWithParams('/api/secure/cc/support/categories')
        if (!active) return
        if (resp?.status === 200 && Array.isArray(resp.data)) {
          setCategoryOptions(resp.data)
        }
      } catch (err) {
        // Non-fatal — Autocomplete will fall back to whatever IDs are already on the practitioner.
        console.error('Failed to load specialization categories', err)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  const filteredCategoryOptions = useMemo(() => {
    if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) return []
    const wantedType = form.practitionerType === 'doctor' ? 'doctor' : 'therapist'
    return categoryOptions
      .filter((opt) => opt?.categoryType === wantedType)
      .sort((a, b) => (a?.title || a?.name || '').localeCompare(b?.title || b?.name || ''))
  }, [categoryOptions, form.practitionerType])

  // Resolve the discipline lens used to pull discipline-aware specialization + qualification
  // catalogs. Therapists only have nutrition + psychotherapy in scope today; we use the first
  // selected discipline as the lens so the UI stays simple.
  const activeDiscipline = useMemo(() => {
    if (form.practitionerType !== 'therapist') return ''
    const list = Array.isArray(form.approvedTherapyDisciplines)
      ? form.approvedTherapyDisciplines.filter(Boolean)
      : []
    return list[0] || THERAPY_DISCIPLINE_PSYCHOTHERAPY
  }, [form.practitionerType, form.approvedTherapyDisciplines])

  // Discipline-aware specialization catalog (preferred + fallback).
  useEffect(() => {
    if (!activeDiscipline) {
      setSpecializationCatalog(null)
      return
    }
    let active = true
    ;(async () => {
      const resp = await getPractitionerSpecializationCatalog(activeDiscipline)
      if (!active) return
      if (resp.ok && resp.data) {
        setSpecializationCatalog(resp.data)
      } else {
        setSpecializationCatalog(null)
      }
    })()
    return () => {
      active = false
    }
  }, [activeDiscipline])

  // Specialization id → display label map. Used by the Bio Tab-to-fill hint generator so the
  // hint mentions friendly names ("clinical psychology") instead of internal codes ("clinical").
  const specializationLabels = useMemo(() => {
    const labels = {}
    const collect = (items) => {
      ;(items || []).forEach((item) => {
        if (!item) return
        const key = item.id || item.label
        if (key) labels[key] = item.label || item.id
      })
    }
    collect(specializationCatalog?.preferred)
    collect(specializationCatalog?.fallback)
    collect(categoryOptions)
    return labels
  }, [specializationCatalog, categoryOptions])

  // Bio Tab-to-fill text — recomputed whenever inputs change so the placeholder always reflects
  // the current name + discipline + specializations. Memoised so we don't churn on every render.
  const bioHint = useMemo(
    () => buildBioHint(form, { specializationLabels }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      form.firstName,
      form.lastName,
      form.practitionerType,
      form.role,
      form.approvedTherapyDisciplines,
      form.specializations,
      specializationLabels,
    ],
  )

  // License Type options — discipline + role aware. Always ends with the two synthetic
  // entries (Not Required / Not Available) so the agent has an out for edge cases.
  const licenseOptions = useMemo(() => buildLicenseOptions(form), [form.practitionerType, form.approvedTherapyDisciplines, form.role])

  // Phase 14g — discipline-first gating. Therapist-typed practitioners need a discipline picked
  // before specializations / clinic events / assessment lists can render meaningfully (the
  // catalog fetches and event-type filters all key off the active discipline). Until the agent
  // picks one, dependent sections show a prominent "Select therapy discipline first" callout
  // and the inputs underneath are disabled visually + functionally.
  const disciplineMissing = useMemo(
    () => form.practitionerType === 'therapist'
      && (!form.approvedTherapyDisciplines || form.approvedTherapyDisciplines.length === 0),
    [form.practitionerType, form.approvedTherapyDisciplines],
  )

  function scrollToDiscipline() {
    const node = fieldRefs.current.approvedTherapyDisciplines
    if (!node) return
    const rect = node.getBoundingClientRect()
    window.scrollTo({ top: Math.max(rect.top + window.scrollY - 96, 0), behavior: 'smooth' })
    if (typeof node.focus === 'function') setTimeout(() => node.focus(), 280)
  }

  /**
   * Renders the "Select therapy discipline first" banner above any therapist-only section.
   * Returns null when discipline is set or practitioner is not a therapist, so non-therapist
   * flows aren't disrupted.
   */
  function disciplineGateBanner() {
    if (!disciplineMissing) return null
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={scrollToDiscipline}>
            Pick discipline
          </Button>
        }
      >
        <strong>Select Therapy Discipline first.</strong> Specializations, clinic events, and
        assessment lists adapt to the discipline you pick — they stay disabled until then.
      </Alert>
    )
  }

  // Auto-pick primary contact when only one of email / mobile is filled. When both are filled
  // we leave whatever the agent last chose alone, but mark "primaryContact" as required so the
  // submit-time validation surfaces it explicitly. When neither is filled we clear the value
  // so it doesn't pre-commit to "mobile" before the agent has typed anything.
  useEffect(() => {
    const hasEmail = Boolean((form.email || '').trim())
    const hasMobile = Boolean((form.mobile || '').trim())
    if (!hasEmail && !hasMobile) {
      if (form.primaryContact) {
        draftAutosaveSuppressedRef.current = true
        setForm((prev) => ({ ...prev, primaryContact: '' }))
      }
      return
    }
    if (hasEmail && !hasMobile && form.primaryContact !== 'email') {
      draftAutosaveSuppressedRef.current = true
      setForm((prev) => ({ ...prev, primaryContact: 'email' }))
    }
    if (hasMobile && !hasEmail && form.primaryContact !== 'mobile') {
      draftAutosaveSuppressedRef.current = true
      setForm((prev) => ({ ...prev, primaryContact: 'mobile' }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, form.mobile])

  // Discipline-aware qualification catalog (preferred curated short list + Mongo fallback).
  useEffect(() => {
    if (!activeDiscipline) {
      setQualificationCatalog(null)
      return
    }
    let active = true
    ;(async () => {
      const resp = await getPractitionerQualificationCatalog(activeDiscipline, form.specializations)
      if (!active) return
      if (resp.ok && resp.data) {
        setQualificationCatalog(resp.data)
      } else {
        setQualificationCatalog(null)
      }
    })()
    return () => {
      active = false
    }
  }, [activeDiscipline, form.specializations])

  // Debounced existence-check for email and mobile. Fires when the user pauses typing for ~600ms
  // and the value has changed since the last lookup.
  useEffect(() => {
    const targetEmail = trimValue(form.email).toLowerCase()
    if (!targetEmail || targetEmail === existenceCheck.email.lookedUpFor) return
    const handle = setTimeout(async () => {
      const resp = await checkPractitionerContactExistence({ email: targetEmail })
      if (resp.ok && resp.data) {
        setExistenceCheck((prev) => ({
          ...prev,
          email: {
            count: resp.data.emailMatchCount || 0,
            samples: resp.data.emailSamples || [],
            lookedUpFor: targetEmail,
          },
        }))
      }
    }, 600)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email])

  useEffect(() => {
    const targetMobile = trimValue(form.mobile)
    if (!targetMobile || targetMobile === existenceCheck.mobile.lookedUpFor) return
    const handle = setTimeout(async () => {
      const resp = await checkPractitionerContactExistence({ mobile: targetMobile })
      if (resp.ok && resp.data) {
        setExistenceCheck((prev) => ({
          ...prev,
          mobile: {
            count: resp.data.mobileMatchCount || 0,
            samples: resp.data.mobileSamples || [],
            lookedUpFor: targetMobile,
          },
        }))
      }
    }, 600)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.mobile])

  async function handleMarkVerifiedOffline(channel) {
    if (!form.userId) {
      toast.warn('Save the practitioner first; offline verification needs a user record.')
      return
    }
    setVerifyOfflinePending((prev) => ({ ...prev, [channel]: true }))
    const resp = await markPractitionerContactVerifiedOffline({ userId: form.userId, channel })
    setVerifyOfflinePending((prev) => ({ ...prev, [channel]: false }))
    if (resp.ok) {
      toast.success(`${channel === 'email' ? 'Email' : 'Mobile'} marked as offline-verified.`)
      if (channel === 'email') setField('emailVerified', true)
      if (channel === 'mobile') setField('mobileVerified', true)
    } else {
      toast.error(resp.message || 'Could not mark as offline-verified.')
    }
  }

  const allowedPractitionerTypes = bootstrapState.data?.allowedPractitionerTypes || []
  const scopedPractitionerType = useMemo(
    () => getAllowedQueryType(allowedPractitionerTypes),
    [allowedPractitionerTypes],
  )
  const isScopedPractitionerFlow = Boolean(scopedPractitionerType)
  const effectivePractitionerType = trimValue(form.practitionerType || scopedPractitionerType).toLowerCase()
  const practitionerTypeLabel = PRACTITIONER_TYPE_LABELS[effectivePractitionerType] || 'Practitioner'
  const pageHeading = isScopedPractitionerFlow ? `${practitionerTypeLabel} Onboarding` : 'Practitioner Onboarding'
  const canManagePatientContactVisibility =
    allRolesInBoolean.isAdmin ||
    (effectivePractitionerType === 'doctor'
      ? allRolesInBoolean.isClinicAdmin
      : (effectivePractitionerType === 'therapist' || effectivePractitionerType === 'screener') &&
        allRolesInBoolean.isTherapistAdmin)
  const patientContactVisibilityPractitionerId = form.practitionerId || selectedPractitioner?.id || ''
  const patientContactVisibilityReference = {
    id: patientContactVisibilityPractitionerId,
    slug: patientContactVisibilityPractitionerId ? '' : selectedPractitioner?.slug || form.slug || '',
  }
  const hasPatientContactVisibilityReference = Boolean(
    trimValue(patientContactVisibilityReference.id) || trimValue(patientContactVisibilityReference.slug),
  )
  const showPatientContactDetailsOnMentorPortal =
    patientContactVisibility?.showPatientContactDetailsOnMentorPortal !== false
  const allClinicBrandOptions = bootstrapState.data?.clinicBrands || []
  const clinicLocationOptions = bootstrapState.data?.clinicLocations || []
  const chamberRoomOptions = bootstrapState.data?.chamberRooms || []
  const clinicAssistantOptions = bootstrapState.data?.clinicAssistants || []
  const targetSpecialtyOptions = bootstrapState.data?.targetSpecialties || []
  const feeModelOptions = bootstrapState.data?.feeModels || []
  const feeModelGroups = bootstrapState.data?.feeModelGroups || []
  const allAssessmentListOptions = bootstrapState.data?.assessmentLists || []
  const paymentConfigPrefill = useMemo(
    () =>
      buildPaymentConfigPrefill(
        {
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          mobile: form.mobile,
          practitionerId: form.practitionerId,
          title: form.title,
        },
        detailState.data,
      ),
    [
      detailState.data,
      form.email,
      form.firstName,
      form.lastName,
      form.mobile,
      form.practitionerId,
      form.title,
    ],
  )

  // Strict brand filter: only show clinic brands whose targetSpecialitySlugs intersects the
  // practitioner's targetSpecialitySlugs. Brands with no slugs declared are filtered OUT (no
  // match), and when the practitioner has no slugs picked yet the list is empty (the dropdown
  // surfaces a helper telling the agent to pick target specialties in Service Approvals first).
  const clinicBrandOptions = useMemo(() => {
    const practitionerSlugs = new Set(
      (Array.isArray(form.targetSpecialitySlugs) ? form.targetSpecialitySlugs : [])
        .map((s) => trimValue(s).toLowerCase())
        .filter(Boolean),
    )
    if (practitionerSlugs.size === 0) {
      return []
    }
    return allClinicBrandOptions.filter((brand) => {
      const brandSlugs = Array.isArray(brand?.targetSpecialitySlugs) ? brand.targetSpecialitySlugs : []
      if (brandSlugs.length === 0) return false
      return brandSlugs.some((s) => practitionerSlugs.has(trimValue(s).toLowerCase()))
    })
  }, [allClinicBrandOptions, form.targetSpecialitySlugs])

  // Filter assessment lists by the practitioner's approved discipline AND target specialties.
  // Lists with empty forDisciplines / targetSpecialitySlugs are treated as applicable everywhere
  // for that dimension (so a list scoped only by specialty isn't excluded for missing discipline
  // metadata, and vice versa). When neither dimension is set on the practitioner side, all lists
  // are returned.
  const assessmentListOptions = useMemo(() => {
    const practitionerDisciplines = new Set(
      (Array.isArray(form.approvedTherapyDisciplines) ? form.approvedTherapyDisciplines : [])
        .map((d) => trimValue(d).toLowerCase())
        .filter(Boolean),
    )
    const practitionerTargetSpecialties = new Set(
      (Array.isArray(form.targetSpecialitySlugs) ? form.targetSpecialitySlugs : [])
        .map((s) => trimValue(s).toLowerCase())
        .filter(Boolean),
    )
    if (practitionerDisciplines.size === 0 && practitionerTargetSpecialties.size === 0) {
      return allAssessmentListOptions
    }
    return allAssessmentListOptions.filter((option) => {
      const forDisciplines = Array.isArray(option?.forDisciplines) ? option.forDisciplines : []
      const forSpecialties = Array.isArray(option?.targetSpecialitySlugs)
        ? option.targetSpecialitySlugs
        : []
      const disciplineOk =
        practitionerDisciplines.size === 0 ||
        forDisciplines.length === 0 ||
        forDisciplines.some((d) => practitionerDisciplines.has(trimValue(d).toLowerCase()))
      const specialtyOk =
        practitionerTargetSpecialties.size === 0 ||
        forSpecialties.length === 0 ||
        forSpecialties.some((s) => practitionerTargetSpecialties.has(trimValue(s).toLowerCase()))
      return disciplineOk && specialtyOk
    })
  }, [allAssessmentListOptions, form.approvedTherapyDisciplines, form.targetSpecialitySlugs])

  const practitionerOptions = useMemo(() => {
    const options = bootstrapState.data?.practitioners || []
    return options
      .filter((option) => {
        const optionType = trimValue(option?.subtitle).toLowerCase()
        if (allowedPractitionerTypes.length === 0) {
          return true
        }
        if (!optionType || !allowedPractitionerTypes.includes(optionType)) {
          return false
        }
        if (!form.practitionerType) {
          return true
        }
        return optionType === form.practitionerType
      })
      .sort((left, right) => getOptionLabel(left).localeCompare(getOptionLabel(right)))
  }, [allowedPractitionerTypes, bootstrapState.data?.practitioners, form.practitionerType])

  const checklist = useMemo(
    () => detailState.data?.checklist || buildDraftChecklist(form, Boolean(selectedPractitioner)),
    [detailState.data?.checklist, form, selectedPractitioner],
  )

  const getFeeModelValue = useCallback(
    (feeModelId) => {
      const selectedFeeModelId = trimValue(feeModelId)
      if (!selectedFeeModelId) return null
      return (
        feeModelOptions.find((option) => option?.id === selectedFeeModelId) || {
          id: selectedFeeModelId,
          title: selectedFeeModelId,
          subtitle: 'Saved fee model',
          type: 'fee-model',
        }
      )
    },
    [feeModelOptions],
  )

  const selectedTargetSpecialties = useMemo(
    () =>
      targetSpecialtyOptions.filter((option) =>
        form.targetSpecialitySlugs.includes(option?.slug || option?.id),
      ),
    [form.targetSpecialitySlugs, targetSpecialtyOptions],
  )
  const selectedTargetSpecialty = selectedTargetSpecialties[0] || null
  const primaryTargetSpecialitySlug = normalizeStringArray(form.targetSpecialitySlugs)[0] || ''

  const registrationTypeOptions = useMemo(() => {
    const practitionerOptionsByType = MENTOR_REG_ID_TYPE_ARRAY.filter((option) => {
      if (form.practitionerType === 'doctor') {
        return option?.specializations?.includes('doctor')
      }
      if (form.practitionerType === 'therapist') {
        return option?.specializations?.includes('therapist')
      }
      return false
    })
    const existingOptions = (form.registrationIds || [])
      .map((registrationId) => trimValue(registrationId?.typeId))
      .filter(Boolean)
      .filter((typeId) => !practitionerOptionsByType.some((option) => option.id === typeId))
      .map((typeId) => ({
        id: typeId,
        title: typeId.toUpperCase(),
      }))
    return [...practitionerOptionsByType, ...existingOptions]
  }, [form.practitionerType, form.registrationIds])

  async function hydratePractitionerSettings(practitionerReference) {
    if (!practitionerReference?.id && !practitionerReference?.slug) {
      return
    }
    setSupplementaryLoading(true)
    const response = await getSupportPractitionerSettings(
      buildPractitionerIdentityParams(practitionerReference),
    )
    setSupplementaryLoading(false)

    if (!response.ok || !response.data) {
      return
    }

    setForm((prev) => ({
      ...prev,
      showFeeModelsOnMentorPortal: Boolean(response.data?.showFeeModelsOnMentorPortal),
      showPayoutReportsOnMentorPortal: Boolean(response.data?.showPayoutReportsOnMentorPortal),
      signatureMediaId: response.data?.signatureMediaId || prev.signatureMediaId || '',
      licenseType: response.data?.licenseType || prev.licenseType || '',
      licenseNumber: response.data?.licenseNumber || prev.licenseNumber || '',
    }))
  }

  async function hydratePatientContactVisibility(practitionerReference) {
    if (!practitionerReference?.id && !practitionerReference?.slug) {
      setPatientContactVisibility(null)
      setPatientContactVisibilityError('')
      return
    }

    setLoadingPatientContactVisibility(true)
    setPatientContactVisibilityError('')
    const response = await getSupportPractitionerPatientContactVisibility(
      buildPractitionerIdentityParams(practitionerReference),
    )
    setLoadingPatientContactVisibility(false)

    if (!response.ok || !response.data) {
      setPatientContactVisibility(null)
      setPatientContactVisibilityError(
        response.message || 'Unable to load patient contact visibility.',
      )
      return
    }

    setPatientContactVisibility(response.data)
  }

  async function handlePatientContactVisibilityChange(event) {
    if (!hasPatientContactVisibilityReference) {
      toast.warn('Save the practitioner before changing patient contact visibility.')
      return
    }

    const nextVisible = event.target.checked
    const previousVisibility = patientContactVisibility
    setSavingPatientContactVisibility(true)
    setPatientContactVisibilityError('')
    setPatientContactVisibility((prev) => ({
      ...(prev || {}),
      practitionerId: patientContactVisibilityReference.id,
      practitionerSlug: patientContactVisibilityReference.slug,
      practitionerType: effectivePractitionerType,
      showPatientContactDetailsOnMentorPortal: nextVisible,
    }))

    const response = await saveSupportPractitionerPatientContactVisibility(
      {
        practitionerId: patientContactVisibilityReference.id,
        practitionerSlug: patientContactVisibilityReference.slug,
      },
      {
        showPatientContactDetailsOnMentorPortal: nextVisible,
      },
    )
    setSavingPatientContactVisibility(false)

    if (!response.ok || !response.data) {
      setPatientContactVisibility(previousVisibility)
      setPatientContactVisibilityError(
        response.message || 'Unable to save patient contact visibility.',
      )
      toast.error(response.message || 'Unable to save patient contact visibility.')
      return
    }

    setPatientContactVisibility(response.data)
    toast.success(
      nextVisible
        ? `Patient contact details enabled for this ${practitionerTypeLabel.toLowerCase()}.`
        : `Patient contact details hidden for this ${practitionerTypeLabel.toLowerCase()}.`,
    )
  }

  async function loadPractitionerDetail(option) {
    if (!option?.id && !option?.slug) {
      return
    }

    setDetailState((prev) => ({ ...prev, loading: true, error: '' }))
    const response = await getPractitionerOnboardingDetail(buildPractitionerIdentityParams(option))

    if (response.ok && response.data) {
      const nextForm = mapDetailToForm(response.data)
      setSelectedPractitioner(option)
      setDetailState({ loading: false, error: '', data: response.data })
      // Suppress the next auto-save tick — this setForm is hydration, not user edit.
      draftAutosaveSuppressedRef.current = true
      setForm(nextForm)
      setClinicAppointmentTypesByKey({})
      await Promise.all([
        hydratePractitionerSettings(option),
        canManagePatientContactVisibility
          ? hydratePatientContactVisibility(option)
          : Promise.resolve(),
      ])
      return
    }

    setDetailState({
      loading: false,
      error: response.message || 'Unable to load practitioner detail.',
      data: null,
    })
    toast.error(response.message || 'Unable to load practitioner detail.')
  }

  useEffect(() => {
    const sourceName = trimValue(form.title || form.firstName)
    if (!sourceName || trimValue(form.slug) || !trimValue(form.practitionerType)) {
      return undefined
    }

    let active = true
    const timeoutId = window.setTimeout(async () => {
      setSlugState((prev) => ({ ...prev, loadingSuggestion: true }))
      const response = await suggestPractitionerSlug(sourceName)
      if (!active) {
        return
      }
      const suggestedSlug = trimValue(response.data)
      setSlugState((prev) => ({
        ...prev,
        loadingSuggestion: false,
        suggestedSlug,
      }))
      if (suggestedSlug && !trimValue(form.slug)) {
        setForm((prev) => ({
          ...prev,
          slug: prev.slug || suggestedSlug,
        }))
      }
    }, 350)

    return () => {
      active = false
      window.clearTimeout(timeoutId)
    }
  }, [form.firstName, form.title, form.practitionerType, form.slug])

  useEffect(() => {
    const slug = trimValue(form.slug)
    if (!slug) {
      setSlugState((prev) => ({
        ...prev,
        validating: false,
        available: null,
        message: prev.suggestedSlug
          ? `Suggested slug: ${prev.suggestedSlug}`
          : 'Slug is auto-suggested from the name and checked for uniqueness.',
      }))
      return undefined
    }

    if (slug === trimValue(form.practitionerSlug)) {
      setSlugState((prev) => ({
        ...prev,
        validating: false,
        available: true,
        message: 'Current slug will be retained.',
      }))
      return undefined
    }

    const slugPattern = /^[a-z-]+$/
    if (!slugPattern.test(slug)) {
      setSlugState((prev) => ({
        ...prev,
        validating: false,
        available: false,
        message: 'Use lowercase letters and hyphens only.',
      }))
      return undefined
    }

    let active = true
    const timeoutId = window.setTimeout(async () => {
      setSlugState((prev) => ({ ...prev, validating: true }))
      // Phase 14ai — pass the current practitioner-id so the backend ignores the row's own
      // slug when answering "available?". Editing a /apply-submitted practitioner used to
      // hit "slug is taken" on every save because the slug already lived on its own record.
      const response = await checkPractitionerSlugAvailability(slug, form.practitionerId)
      if (!active) {
        return
      }
      setSlugState((prev) => ({
        ...prev,
        validating: false,
        available: Boolean(response.data?.available),
        message: response.data?.available ? 'Slug is available.' : 'This slug is already taken.',
      }))
    }, 350)

    return () => {
      active = false
      window.clearTimeout(timeoutId)
    }
  }, [form.slug, form.practitionerSlug, form.practitionerId])

  useEffect(() => {
    let active = true

    async function loadMissingClinicAppointmentTypes() {
      const assignmentsToFetch = (form.clinicAssignments || []).filter(
        (assignment) =>
          trimValue(assignment?.clinicBrandId) &&
          trimValue(assignment?.clinicLocationId) &&
          !clinicAppointmentTypesByKey[
            getClinicAssignmentKey(assignment?.clinicBrandId, assignment?.clinicLocationId)
          ],
      )

      for (const assignment of assignmentsToFetch) {
        const key = getClinicAssignmentKey(assignment?.clinicBrandId, assignment?.clinicLocationId)
        const response = await getClinicAppointmentTypes({
          clinicBrandId: assignment?.clinicBrandId,
          clinicLocationId: assignment?.clinicLocationId,
        })
        if (!active) {
          return
        }
        const appointmentTypes = response.ok ? response.data || [] : []
        setClinicAppointmentTypesByKey((prev) => ({
          ...prev,
          [key]: appointmentTypes,
        }))
        setForm((prev) => {
          const existingConfigIds = new Set((prev.clinicEventConfigurations || []).map((config) => config.id))
          const nextConfigurations = [...(prev.clinicEventConfigurations || [])]
          appointmentTypes.forEach((appointmentType) => {
            const defaultTargetSpecialtySlug =
              prev.practitionerType === 'screener'
                ? normalizeStringArray(prev.targetSpecialitySlugs)[0] || ''
                : ''
            const nextConfig = buildClinicEventConfigFromAppointmentType(
              assignment,
              appointmentType,
              defaultTargetSpecialtySlug,
            )
            if (prev.practitionerType === 'screener' && nextConfig.eventType !== EVENT_TYPE_SCREENING) {
              return
            }
            if (!existingConfigIds.has(nextConfig.id)) {
              nextConfigurations.push(nextConfig)
              existingConfigIds.add(nextConfig.id)
            }
          })
          return {
            ...prev,
            clinicEventConfigurations: nextConfigurations,
          }
        })
      }
    }

    loadMissingClinicAppointmentTypes()
    return () => {
      active = false
    }
  }, [clinicAppointmentTypesByKey, form.clinicAssignments])

  useEffect(() => {
    if (form.practitionerType !== 'screener' || !primaryTargetSpecialitySlug) {
      return
    }
    setForm((prev) => {
      let changed = false
      const nextConfigurations = (prev.clinicEventConfigurations || []).map((configuration) => {
        if (trimValue(configuration?.eventType).toLowerCase() !== EVENT_TYPE_SCREENING) {
          return configuration
        }
        if (trimValue(configuration?.targetSpecialtySlug) === primaryTargetSpecialitySlug) {
          return configuration
        }
        changed = true
        return {
          ...configuration,
          targetSpecialtySlug: primaryTargetSpecialitySlug,
        }
      })
      return changed
        ? {
            ...prev,
            clinicEventConfigurations: nextConfigurations,
          }
        : prev
    })
  }, [form.practitionerType, primaryTargetSpecialitySlug])

  // Phase 14p — when the practitioner has exactly ONE target speciality selected on the main
  // page, every clinic event configuration should default to that speciality. This used to
  // run only for screeners; now it covers all practitioner types so therapists and doctors
  // also get their events tagged with the right speciality without the agent having to retype
  // it on each row. When there are multiple specialities we leave the slug alone so the agent
  // can pick the right one per event from the inline + drawer dropdowns added below.
  useEffect(() => {
    const slugs = normalizeStringArray(form.targetSpecialitySlugs)
    if (slugs.length !== 1) return
    const onlySlug = slugs[0]
    if (!onlySlug) return
    setForm((prev) => {
      let changed = false
      const nextConfigurations = (prev.clinicEventConfigurations || []).map((configuration) => {
        const current = trimValue(configuration?.targetSpecialtySlug)
        if (current === onlySlug) return configuration
        // Don't overwrite an explicit non-empty slug that disagrees with the practitioner's
        // single selection — that's a deliberate per-event override; skip silently.
        if (current && current !== onlySlug) return configuration
        changed = true
        return { ...configuration, targetSpecialtySlug: onlySlug }
      })
      return changed
        ? { ...prev, clinicEventConfigurations: nextConfigurations }
        : prev
    })
  }, [form.targetSpecialitySlugs])

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function setEngagementFeeModelSelection(applicableTo, feeModelId) {
    setForm((prev) => {
      const nextSelections = {
        ...createDefaultEngagementFeeModelIdsByApplicableTo(),
        ...(prev.engagementFeeModelIdsByApplicableTo || {}),
        [applicableTo]: trimValue(feeModelId),
      }
      const firstSelected = ENGAGEMENT_APPLICABLE_TO_OPTIONS
        .map((option) => ({
          applicableTo: option.id,
          feeModelId: trimValue(nextSelections[option.id]),
        }))
        .find((selection) => selection.feeModelId)
      return {
        ...prev,
        engagementApplicableTo: firstSelected?.applicableTo || prev.engagementApplicableTo,
        engagementFeeModelId: firstSelected?.feeModelId || '',
        engagementFeeModelIdsByApplicableTo: nextSelections,
        engagementCountryCode: 'IN',
        engagementCurrencyCode: 'inr',
      }
    })
  }

  function updateAssignment(index, changes) {
    setForm((prev) => ({
      ...prev,
      clinicAssignments: prev.clinicAssignments.map((assignment, assignmentIndex) =>
        assignmentIndex === index ? { ...assignment, ...changes } : assignment,
      ),
    }))
  }

  function updateAuditorAssignment(index, changes) {
    setForm((prev) => ({
      ...prev,
      auditorAssignments: prev.auditorAssignments.map((assignment, assignmentIndex) =>
        assignmentIndex === index ? { ...assignment, ...changes } : assignment,
      ),
    }))
  }

  function updateRegistrationId(index, changes) {
    setForm((prev) => ({
      ...prev,
      registrationIds: prev.registrationIds.map((registrationId, registrationIndex) =>
        registrationIndex === index ? { ...registrationId, ...changes } : registrationId,
      ),
    }))
  }

  function updateDocument(index, changes) {
    setForm((prev) => ({
      ...prev,
      certificateDocuments: prev.certificateDocuments.map((document, documentIndex) =>
        documentIndex === index ? { ...document, ...changes } : document,
      ),
    }))
  }

  function updateClinicEventConfiguration(configId, changes) {
    setForm((prev) => ({
      ...prev,
      clinicEventConfigurations: prev.clinicEventConfigurations.map((configuration) =>
        configuration.id === configId ? { ...configuration, ...changes } : configuration,
      ),
    }))
  }

  /**
   * Phase 14l — append a brand-new event configuration that the agent hand-builds in the drawer.
   * Used by the "Add custom event" button under each Clinic Assignment's events tab; the
   * existing flow only auto-seeded events when a clinic appointment type already existed at the
   * location, which left agents with no way to create one inline.
   *
   * The new entry gets a random local id, the assignment's brand+location, the active tab's
   * event type, and a default mode/duration. The drawer opens immediately so the agent can fine
   * tune title / price / etc. before saving.
   */
  function addCustomClinicEvent(assignment, eventType) {
    const newId =
      typeof window !== 'undefined' && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `local-${Date.now()}-${Math.floor(Math.random() * 100000)}`
    const newEntry = {
      id: newId,
      clinicBrandId: assignment?.clinicBrandId || '',
      clinicLocationId: assignment?.clinicLocationId || '',
      clinicAppointmentTypeId: '',
      eventType: eventType || EVENT_TYPE_THERAPY_SESSION,
      title: '',
      mode: 'offline',
      durationInMins: 30,
      price: null,
      currencyCode: 'INR',
      targetSpecialtySlug: '',
      enabled: true,
      isAssessmentEvent: false,
      clinicalAssessmentIds: [],
    }
    setForm((prev) => ({
      ...prev,
      clinicEventConfigurations: [...(prev.clinicEventConfigurations || []), newEntry],
    }))
    setEventEditDrawerConfigId(newId)
  }

  // Auto-enable seed for assessment-style clinic events whose appointment type's
  // clinicalAssessmentIds intersects the practitioner's attached assessmentListIds. The seed
  // defaults to false for assessments (see buildClinicEventConfigFromAppointmentType); this
  // effect flips it back on as soon as the agent attaches a matching assessment list.
  // Non-assessment events keep their existing enabled state.
  useEffect(() => {
    const attachedListIds = new Set(
      Array.isArray(form.assessmentListIds) ? form.assessmentListIds.filter(Boolean) : [],
    )
    if (attachedListIds.size === 0) return
    setForm((prev) => {
      let mutated = false
      const nextConfigs = (prev.clinicEventConfigurations || []).map((cfg) => {
        if (!cfg?.isAssessmentEvent) return cfg
        if (cfg.enabled) return cfg
        const matches = (cfg.clinicalAssessmentIds || []).some((id) => attachedListIds.has(id))
        if (!matches) return cfg
        mutated = true
        return { ...cfg, enabled: true }
      })
      return mutated ? { ...prev, clinicEventConfigurations: nextConfigs } : prev
    })
  }, [form.assessmentListIds, form.clinicEventConfigurations.length])

  function handlePractitionerTypeChange(nextType) {
    setSelectedPractitioner(null)
    setDetailState({ loading: false, error: '', data: null })
    setClinicAppointmentTypesByKey({})
    setPatientContactVisibility(null)
    setPatientContactVisibilityError('')
    setSlugState({
      suggestedSlug: '',
      loadingSuggestion: false,
      validating: false,
      available: null,
      message: 'Slug is auto-suggested from the name and checked for uniqueness.',
    })
    setForm((prev) => ({
      ...createEmptyForm(nextType),
      firstName: prev.firstName,
      lastName: prev.lastName,
      email: prev.email,
      mobile: prev.mobile,
      title: prev.title,
      profilePicId: prev.profilePicId,
      shortDesc: prev.shortDesc,
      longDesc: prev.longDesc,
      qualifications: prev.qualifications,
      registrationIds: prev.registrationIds,
      certificateDocuments: prev.certificateDocuments,
      specializations: prev.specializations,
      listableInWeb: prev.listableInWeb,
      allowSelfReschedule: prev.allowSelfReschedule,
      allowSelfCancel: prev.allowSelfCancel,
      remarks: prev.remarks,
    }))
  }

  function getLocationOptionsForAssignment(assignment) {
    if (!assignment?.clinicBrandId) {
      return clinicLocationOptions
    }
    return clinicLocationOptions.filter(
      (option) => option?.clinicBrandId === assignment.clinicBrandId,
    )
  }

  function getRoomOptionsForAssignment(assignment) {
    const requireTherapySession = form.practitionerType === 'therapist'
    return chamberRoomOptions.filter((option) => {
      if (assignment?.clinicBrandId && option?.clinicBrandId !== assignment.clinicBrandId) {
        return false
      }
      if (assignment?.clinicLocationId && option?.clinicLocationId !== assignment.clinicLocationId) {
        return false
      }
      // For therapists, only surface rooms whose supportedEventTypes include 'therapy-session'.
      // Rooms with no supportedEventTypes declared stay visible (treated as universal so
      // legacy rooms aren't excluded until the data is backfilled).
      if (requireTherapySession) {
        const supported = Array.isArray(option?.supportedEventTypes) ? option.supportedEventTypes : []
        if (supported.length > 0) {
          const hasTherapy = supported.some(
            (eventType) => trimValue(eventType).toLowerCase() === EVENT_TYPE_THERAPY_SESSION,
          )
          if (!hasTherapy) return false
        }
      }
      return true
    })
  }

  function getAssistantOptionsForAssignment(assignment) {
    return clinicAssistantOptions.filter((option) => {
      if (assignment?.clinicBrandId && option?.clinicBrandId !== assignment.clinicBrandId) {
        return false
      }
      if (assignment?.clinicLocationId && option?.clinicLocationId !== assignment.clinicLocationId) {
        return false
      }
      return true
    })
  }

  function getClinicAppointmentTypesForAssignment(assignment) {
    return (
      clinicAppointmentTypesByKey[
        getClinicAssignmentKey(assignment?.clinicBrandId, assignment?.clinicLocationId)
      ] || []
    )
  }

  function getClinicEventConfigurationsForAssignment(assignment) {
    const assignmentKey = getClinicAssignmentKey(assignment?.clinicBrandId, assignment?.clinicLocationId)
    return (form.clinicEventConfigurations || []).filter(
      (configuration) =>
        getClinicAssignmentKey(configuration?.clinicBrandId, configuration?.clinicLocationId) === assignmentKey &&
        (form.practitionerType !== 'screener' ||
          trimValue(configuration?.eventType).toLowerCase() === EVENT_TYPE_SCREENING),
    )
  }

  // Phase 14al — event types each practitioner role is allowed to run. Keyed by the
  // practitionerType the form already drives; the chamber-room status pill on each location
  // chip flips green only when the rooms attached to that location include at least one of
  // these event types. Slice 3 of the rebuild moves this map server-side so chamber-room
  // save validation can enforce the same gate; for now it's a pure-frontend convention so the
  // UX can ship without a server-side migration.
  const ROLE_ALLOWED_EVENT_TYPES = {
    therapist: ['therapy-session', 'assessment'],
    doctor: ['consultation'],
    screener: ['screening'],
    'psychology-student': ['therapy-session', 'assessment'],
  }

  /**
   * Phase 14al — three independent status indicators per (brand, location) pair, used by
   * the location-chip pill row in the new brand-grouped Clinic Assignment IA. Each pill is
   * its own bool because the user wants them displayed separately rather than rolled into
   * one "fully configured" check.
   */
  function computeLocationStatus(assignment) {
    if (!assignment?.clinicBrandId || !assignment?.clinicLocationId) {
      return { hasOnlineEvent: false, hasOfflineEvent: false, hasMatchingRoom: false }
    }
    const eventConfigs = getClinicEventConfigurationsForAssignment(assignment)
    const enabledConfigs = eventConfigs.filter((c) => c?.enabled !== false)
    const hasOnlineEvent = enabledConfigs.some(
      (c) => trimValue(c?.meetingSrc).toLowerCase() === 'aumhum',
    )
    const hasOfflineEvent = enabledConfigs.some(
      (c) => trimValue(c?.meetingSrc).toLowerCase() === 'offline',
    )
    // Phase 14al Slice 3 — prefer the server-supplied policy when it's loaded; fall back to
    // the hardcoded ROLE_ALLOWED_EVENT_TYPES constant defensively. Both maps share keys.
    const serverPolicies = roleEventTypePolicies || {}
    const serverAllowed = Array.isArray(serverPolicies[form.practitionerType])
      ? serverPolicies[form.practitionerType]
      : null
    const allowedTypes =
      serverAllowed && serverAllowed.length > 0
        ? serverAllowed
        : ROLE_ALLOWED_EVENT_TYPES[form.practitionerType] || []
    const attachedRoomIds = Array.isArray(assignment?.chamberRoomIds)
      ? assignment.chamberRoomIds.filter(Boolean)
      : []
    const hasMatchingRoom = attachedRoomIds.some((roomId) => {
      const room = chamberRoomOptions.find((option) => option?.id === roomId)
      const roomEventTypes = Array.isArray(room?.eventTypes)
        ? room.eventTypes.map((t) => trimValue(t).toLowerCase())
        : []
      return roomEventTypes.some((t) => allowedTypes.includes(t))
    })
    return { hasOnlineEvent, hasOfflineEvent, hasMatchingRoom }
  }

  function validateForm() {
    // Phase 14g — validation now collects ALL field-level errors instead of bailing on the first
    // miss, so the agent sees every gap in one pass via inline error highlights + a summary
    // toast. Returns { message, fieldErrors } where fieldErrors maps a field-name to a per-field
    // message; handleSave uses that to populate submitErrors + scroll to the first error.
    const fieldErrors = {}
    let message = null
    const fail = (field, msg) => {
      if (!fieldErrors[field]) fieldErrors[field] = msg
      if (!message) message = msg
    }

    if (!trimValue(form.practitionerType)) {
      fail('practitionerType', 'Select a practitioner type before saving.')
    }

    // Therapy discipline mandatory for therapists (KYC-locked elsewhere; here we just enforce it).
    if (form.practitionerType === 'therapist'
        && normalizeStringArray(form.approvedTherapyDisciplines).length === 0) {
      fail('approvedTherapyDisciplines',
        'Pick at least one therapy discipline before saving a therapist.')
    }

    if (form.practitionerType === 'screener' && normalizeStringArray(form.targetSpecialitySlugs).length === 0) {
      fail('targetSpecialitySlugs', 'Select the screening target specialty before saving a screener.')
    }

    if (!selectedPractitioner) {
      if (!trimValue(form.firstName)) {
        fail('firstName', 'First name is required to create a new practitioner.')
      }
      if (!trimValue(form.email) && !trimValue(form.mobile)) {
        fail('email', 'Email or mobile is required to create a new practitioner.')
      }
    }

    // Primary contact mandatory choice when both email + mobile are present.
    const hasEmail = Boolean(trimValue(form.email))
    const hasMobile = Boolean(trimValue(form.mobile))
    if (hasEmail && hasMobile && !trimValue(form.primaryContact)) {
      fail('primaryContact',
        'Both email and mobile are filled — pick which one drives login.')
    }

    if (trimValue(form.slug) && slugState.available === false) {
      fail('slug', 'Use an available slug before saving.')
    }

    for (const assignment of form.clinicAssignments) {
      const hasRooms = Array.isArray(assignment?.chamberRoomIds) && assignment.chamberRoomIds.length > 0
      const hasAssistants =
        Array.isArray(assignment?.clinicAssistantAssignmentIds) &&
        assignment.clinicAssistantAssignmentIds.length > 0

      if ((hasRooms || hasAssistants) && !assignment?.clinicLocationId) {
        fail('clinicAssignments',
          'Select a clinic location before assigning rooms or clinic assistants.')
        break
      }
    }

    const incompleteRegistrationId = (form.registrationIds || []).find(
      (registrationId) => trimValue(registrationId?.typeId) || trimValue(registrationId?.id),
    )
    if (incompleteRegistrationId) {
      const hasType = Boolean(trimValue(incompleteRegistrationId?.typeId))
      const hasId = Boolean(trimValue(incompleteRegistrationId?.id))
      if (!hasType || !hasId) {
        fail('registrationIds',
          'Complete or remove any partial registration ID rows before saving.')
      }
    }

    const incompleteDocument = (form.certificateDocuments || []).find(
      (document) => trimValue(document?.title) || trimValue(document?.fileId),
    )
    if (incompleteDocument) {
      const hasTitle = Boolean(trimValue(incompleteDocument?.title))
      const hasFile = Boolean(trimValue(incompleteDocument?.fileId))
      if (!hasTitle || !hasFile) {
        fail('certificateDocuments',
          'Complete or remove any partial credential document rows before saving.')
      }
    }

    if (form.practitionerType === 'therapist' && form.allowedToAssess) {
      if (normalizeStringArray(form.assessmentListIds).length === 0) {
        fail('assessmentListIds',
          'Add at least one assessment list ID when Allowed To Assess is enabled.')
      }
    }

    return { message, fieldErrors }
  }

  function buildPayload() {
    const activeAssignmentKeys = new Set(
      (form.clinicAssignments || [])
        .map((assignment) => getClinicAssignmentKey(assignment?.clinicBrandId, assignment?.clinicLocationId))
        .filter((key) => key !== '::'),
    )
    const normalizedTargetSpecialitySlugs = normalizeStringArray(form.targetSpecialitySlugs)
    const primaryScreeningTargetSpecialitySlug =
      form.practitionerType === 'screener' ? normalizedTargetSpecialitySlugs[0] || '' : ''
    const clinicEventConfigurations = (form.clinicEventConfigurations || [])
      .filter(
        (configuration) => {
          const eventType = trimValue(configuration?.eventType).toLowerCase()
          return (
            Boolean(configuration?.enabled) &&
            eventType &&
            activeAssignmentKeys.has(
              getClinicAssignmentKey(configuration?.clinicBrandId, configuration?.clinicLocationId),
            ) &&
            (form.practitionerType !== 'screener' || eventType === EVENT_TYPE_SCREENING)
          )
        },
      )
      .map((configuration) => {
        const eventType = trimValue(configuration?.eventType).toLowerCase()
        return {
          clinicBrandId: trimValue(configuration?.clinicBrandId) || undefined,
          clinicLocationId: trimValue(configuration?.clinicLocationId) || undefined,
          clinicAppointmentTypeId: trimValue(configuration?.clinicAppointmentTypeId) || undefined,
          eventType: eventType || undefined,
          title: trimValue(configuration?.title) || undefined,
          targetSpecialtySlug:
            trimValue(configuration?.targetSpecialtySlug) ||
            (eventType === EVENT_TYPE_SCREENING ? primaryScreeningTargetSpecialitySlug : undefined),
          currencyCode: trimValue(configuration?.currencyCode) || undefined,
          price: toNumberOrNull(configuration?.price),
          durationInMins: toIntegerOrNull(configuration?.durationInMins),
          enabled: Boolean(configuration?.enabled),
        }
      })

    // Mentor-event derivation: skip assessment-style event types so the server doesn't create
    // a private mentor event for assessments. Assessments live only as clinic appointments per
    // the support spec — the practitioner runs them inside a clinic context, not as a personal
    // event on their public profile.
    const derivedEventTypes =
      clinicEventConfigurations.length > 0
        ? [...new Set(
            clinicEventConfigurations
              .map((configuration) => configuration.eventType)
              .filter(Boolean)
              .filter((t) => {
                const lower = trimValue(t).toLowerCase()
                return lower !== 'assessment' && lower !== 'assessments'
              }),
          )]
        : normalizeStringArray(form.eventTypes).filter((t) => {
            const lower = trimValue(t).toLowerCase()
            return lower !== 'assessment' && lower !== 'assessments'
          })
    const engagementFeeModelIdsByApplicableTo = {
      ...createDefaultEngagementFeeModelIdsByApplicableTo(),
      ...(form.engagementFeeModelIdsByApplicableTo || {}),
    }
    const engagementModelSelections = ENGAGEMENT_APPLICABLE_TO_OPTIONS.map((option) => ({
      applicableTo: option.id,
      feeModelId: trimValue(engagementFeeModelIdsByApplicableTo[option.id]),
    }))
    const firstEngagementModelSelection = engagementModelSelections.find((selection) => selection.feeModelId)
    const engagementModels = engagementModelSelections.map((selection) => ({
      applicableTo: selection.applicableTo,
      countryCode: 'IN',
      currencyCode: 'inr',
      enabled: Boolean(selection.feeModelId),
      feeRanges: selection.feeModelId
        ? [
            {
              min: 0,
              max: null,
              modelId: selection.feeModelId,
            },
          ]
        : [],
    }))

    return {
      practitionerId: trimValue(form.practitionerId) || undefined,
      practitionerSlug: trimValue(form.practitionerId)
        ? undefined
        : trimValue(form.practitionerSlug) || undefined,
      practitionerType: trimValue(form.practitionerType),
      firstName: trimValue(form.firstName) || undefined,
      lastName: trimValue(form.lastName) || undefined,
      email: trimValue(form.email) || undefined,
      mobile: trimValue(form.mobile) || undefined,
      primaryContact: trimValue(form.primaryContact) || undefined,
      whatsappNumber: trimValue(form.whatsappNumber) || undefined,
      voiceCallNumber: trimValue(form.voiceCallNumber) || undefined,
      mandatoryCredentialDocFileId: trimValue(form.mandatoryCredentialDocFileId) || undefined,
      mandatoryCredentialDocTitle: trimValue(form.mandatoryCredentialDocTitle) || undefined,
      gender: trimValue(form.gender) || undefined,
      slug: trimValue(form.slug) || undefined,
      title: trimValue(form.title) || undefined,
      profilePicId: trimValue(form.profilePicId) || undefined,
      shortDesc: trimValue(form.shortDesc) || undefined,
      longDesc: trimValue(form.longDesc) || undefined,
      qualifications: normalizeStringArray(form.qualifications),
      experienceStartMonth: trimValue(form.experienceStartMonth) || undefined,
      experienceStartYear: trimValue(form.experienceStartYear) || undefined,
      registrationIds: (form.registrationIds || [])
        .filter((registrationId) => trimValue(registrationId?.typeId) && trimValue(registrationId?.id))
        .map((registrationId) => ({
          typeId: trimValue(registrationId.typeId),
          id: trimValue(registrationId.id),
        })),
      certificateIds: (form.certificateDocuments || [])
        .map((document) => trimValue(document?.fileId))
        .filter(Boolean),
      kycStatus: 'approved',
      enableServices: Boolean(form.enableServices),
      disableAfterSave: false,
      createDefaultEvents: Boolean(form.createDefaultEvents),
      allowedToPrescribe: form.practitionerType === 'doctor' ? Boolean(form.allowedToPrescribe) : false,
      allowSelfReschedule: form.allowSelfReschedule !== false,
      allowSelfCancel: form.allowSelfCancel !== false,
      listableInWeb: Boolean(form.listableInWeb),
      specializations: normalizeStringArray(form.specializations),
      // Send approvedTherapyDisciplines only on the therapist path (the backend ignores it for
      // doctor / screener but sending null avoids cluttering the payload). Empty array clears
      // the practitioner's existing value.
      approvedTherapyDisciplines:
        form.practitionerType === 'therapist'
          ? normalizeStringArray(form.approvedTherapyDisciplines)
          : undefined,
      targetSpecialitySlugs: normalizedTargetSpecialitySlugs,
      remarks: trimValue(form.remarks) || undefined,
      eventTypes: derivedEventTypes,
      eventCurrencyCode:
        trimValue(form.eventCurrencyCode) ||
        trimValue(clinicEventConfigurations?.[0]?.currencyCode) ||
        undefined,
      eventPrice:
        toNumberOrNull(form.eventPrice) ??
        toNumberOrNull(clinicEventConfigurations?.[0]?.price),
      eventDurationInMins:
        toIntegerOrNull(form.eventDurationInMins) ??
        toIntegerOrNull(clinicEventConfigurations?.[0]?.durationInMins),
      clinicEventConfigurations,
      engagementApplicableTo: firstEngagementModelSelection?.applicableTo,
      engagementFeeModelId: firstEngagementModelSelection?.feeModelId,
      engagementCountryCode: 'IN',
      engagementCurrencyCode: 'inr',
      engagementModels,
      appointmentCalendarSettings: form.appointmentCalendarSettings,
      reportViewType: trimValue(form.reportViewType) || 'minimal',
      preferredReportLanguages: normalizeStringArray(form.preferredReportLanguages).length
        ? normalizeStringArray(form.preferredReportLanguages)
        : ['en'],
      allowedToAssess: form.practitionerType === 'therapist' ? Boolean(form.allowedToAssess) : undefined,
      assessmentListIds:
        form.practitionerType === 'therapist' ? normalizeStringArray(form.assessmentListIds) : [],
      clinicAssignments: form.clinicAssignments
        .filter((assignment) => {
          return (
            trimValue(assignment?.clinicBrandId) ||
            trimValue(assignment?.clinicLocationId) ||
            (assignment?.chamberRoomIds || []).length > 0 ||
            (assignment?.clinicAssistantAssignmentIds || []).length > 0
          )
        })
        .map((assignment) => ({
          clinicBrandId: trimValue(assignment?.clinicBrandId) || undefined,
          clinicLocationId: trimValue(assignment?.clinicLocationId) || undefined,
          chamberRoomIds: normalizeStringArray(assignment?.chamberRoomIds),
          clinicAssistantAssignmentIds: normalizeStringArray(assignment?.clinicAssistantAssignmentIds),
        })),
      auditorAssignments: form.auditorAssignments
        .filter((assignment) => trimValue(assignment?.mentorId))
        .map((assignment) => ({
          mentorId: trimValue(assignment?.mentorId),
          type: trimValue(assignment?.type) || 'SUPERVISOR',
          chamberRoomIds: normalizeStringArray(assignment?.chamberRoomIds),
      })),
      // Phase 14k — incoming auditors (other practitioners auditing THIS practitioner per room).
      incomingAuditors: (form.incomingAuditors || [])
        .filter((entry) => trimValue(entry?.auditorPractitionerId) && trimValue(entry?.chamberRoomId))
        .map((entry) => ({
          chamberRoomId: trimValue(entry.chamberRoomId),
          auditorPractitionerId: trimValue(entry.auditorPractitionerId),
          type: trimValue(entry.type) || 'SUPERVISOR',
        })),
    }
  }

  function buildSettingsPayload() {
    return {
      showFeeModelsOnMentorPortal: Boolean(form.showFeeModelsOnMentorPortal),
      showPayoutReportsOnMentorPortal: Boolean(form.showPayoutReportsOnMentorPortal),
      signatureMediaId: trimValue(form.signatureMediaId) || '',
      licenseType: trimValue(form.licenseType) || '',
      licenseNumber: trimValue(form.licenseNumber) || '',
    }
  }

  function getPrimarySaveButtonLabel() {
    if (trimValue(form.practitionerId)) {
      return `Save ${practitionerTypeLabel} Onboarding`
    }
    return `Create Pre-approved ${practitionerTypeLabel}`
  }

  function getSecondarySaveButtonLabel() {
    if (trimValue(form.practitionerId)) {
      return `Save And Keep ${practitionerTypeLabel} Disabled`
    }
    return `Create ${practitionerTypeLabel} And Disable Temporarily`
  }

  async function handleSave(nextMode = 'active') {
    const { message: validationError, fieldErrors } = validateForm()
    if (validationError) {
      // Phase 14g — surface inline highlights for every offending field, not just the first one.
      // Then scroll the first errored anchor into view (manually — no scrollIntoView so we don't
      // accidentally cascade into the page-jump-to-top regression we just fixed).
      setSubmitErrors(fieldErrors)
      const firstErrorField = Object.keys(fieldErrors)[0]
      const node = firstErrorField ? fieldRefs.current[firstErrorField] : null
      if (node && typeof node.getBoundingClientRect === 'function') {
        const rect = node.getBoundingClientRect()
        const targetTop = rect.top + window.scrollY - 96
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
          window.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' })
        }
        if (typeof node.focus === 'function') {
          // Give the smooth-scroll a tick to settle before stealing focus.
          setTimeout(() => node.focus(), 280)
        }
      }
      toast.error(validationError)
      return
    }
    // Clear error highlights from a previous attempt now that all fields validate.
    if (Object.keys(submitErrors).length > 0) setSubmitErrors({})

    // Phase 14g — slug recheck right before submit. The debounced effect catches stale state
    // most of the time, but in the race window between the agent's last keystroke and the
    // server save another agent could've claimed the same slug. We re-call the availability
    // check to be sure, and on miss we surface a fresh suggestion the agent can accept inline.
    const trimmedSlug = trimValue(form.slug)
    const currentPractitionerSlug = trimValue(form.practitionerSlug)
    if (trimmedSlug && trimmedSlug !== currentPractitionerSlug) {
      // Set the loading state on the buttons during the slug recheck so the agent sees activity.
      setSavingMode(nextMode)
      // Phase 14ai — exclude self id (see the live-validation effect above for the same fix).
      const slugCheck = await checkPractitionerSlugAvailability(trimmedSlug, form.practitionerId)
      if (slugCheck.ok && slugCheck.data?.available === false) {
        // Pull a fresh suggestion based on the practitioner's current name so the agent has a
        // ready alternative. If the suggester misses, we just highlight the field with a generic
        // message and let the agent retype.
        const suggestionResp = await suggestPractitionerSlug(
          trimValue([form.firstName, form.lastName].filter(Boolean).join(' ')) || trimmedSlug,
        )
        const suggestion = trimValue(suggestionResp?.data)
        setSlugState((prev) => ({
          ...prev,
          available: false,
          suggestedSlug: suggestion || prev.suggestedSlug,
          message: suggestion
            ? `Taken — try "${suggestion}" or pick another.`
            : 'This slug is taken — pick another.',
        }))
        setSubmitErrors((prev) => ({
          ...prev,
          slug: suggestion
            ? `Slug is taken. Suggested: ${suggestion}`
            : 'Slug is taken — pick another before saving.',
        }))
        const node = fieldRefs.current.slug
        if (node && typeof node.getBoundingClientRect === 'function') {
          const rect = node.getBoundingClientRect()
          window.scrollTo({
            top: Math.max(rect.top + window.scrollY - 96, 0),
            behavior: 'smooth',
          })
          if (typeof node.focus === 'function') setTimeout(() => node.focus(), 280)
        }
        toast.error('Slug is taken — pick another before saving.')
        setSavingMode('')
        return
      }
    }

    const disableAfterSave = nextMode === 'disabled'
    // setSavingMode is already set above when we ran the slug recheck; calling it again here is
    // a no-op for the active path but ensures the button stays in its loading state when slug
    // was empty (no-recheck branch) too.
    if (savingMode !== nextMode) setSavingMode(nextMode)
    const onboardingResponse = await savePractitionerOnboarding({
      ...buildPayload(),
      disableAfterSave,
    })
    if (!onboardingResponse.ok || !onboardingResponse.data) {
      setSavingMode('')
      toast.error(onboardingResponse.message || 'Unable to save practitioner onboarding.')
      return
    }

    const nextOption = buildPractitionerOptionFromDetail(onboardingResponse.data)
    let settingsSaveFailed = false

    if (nextOption?.id || nextOption?.slug) {
      const settingsResponse = await saveSupportPractitionerSettings(
        buildPractitionerIdentityParams(nextOption),
        buildSettingsPayload(),
      )
      settingsSaveFailed = !settingsResponse.ok
    }

    setSavingMode('')

    const nextForm = {
      ...mapDetailToForm(onboardingResponse.data),
      showFeeModelsOnMentorPortal: Boolean(form.showFeeModelsOnMentorPortal),
      showPayoutReportsOnMentorPortal: Boolean(form.showPayoutReportsOnMentorPortal),
      signatureMediaId: form.signatureMediaId,
      licenseType: form.licenseType,
      licenseNumber: form.licenseNumber,
    }

    setDetailState({ loading: false, error: '', data: onboardingResponse.data })
    // Server-side save succeeded → suppress the auto-save tick triggered by this hydration
    // setForm. The backend itself clears the draft inside the /save handler so we don't need
    // to do a parallel DELETE from here.
    draftAutosaveSuppressedRef.current = true
    setLastDraftSavedAt(null)
    setPendingDraft(null)
    setForm(nextForm)
    setSelectedPractitioner(nextOption)
    setBootstrapState((prev) => {
      if (!prev?.data) {
        return prev
      }
      return {
        ...prev,
        data: {
          ...prev.data,
          practitioners: mergePractitionerOptionList(prev.data.practitioners || [], nextOption),
        },
      }
    })

    if (nextOption) {
      await Promise.all([
        hydratePractitionerSettings(nextOption),
        canManagePatientContactVisibility
          ? hydratePatientContactVisibility(nextOption)
          : Promise.resolve(),
      ])
    }

    if (settingsSaveFailed) {
      toast.warning('Onboarding saved, but portal visibility/license settings need another save.')
      return
    }

    toast.success(
      disableAfterSave
        ? `${practitionerTypeLabel} onboarding saved and the practitioner has been disabled temporarily.`
        : `${practitionerTypeLabel} onboarding saved successfully. Scroll down to configure payouts inline, or open the dedicated page.`,
    )

    // Note: an earlier iteration auto-scrolled to the Payment section after a successful save,
    // which felt jarring — agents reported losing their place mid-edit. The toast carries the
    // affordance ("scroll down to configure payouts") so we leave the viewport where the agent
    // left it.
  }

  if (bootstrapState.loading) {
    return (
      <>
        <SupportHeader heading={pageHeading} showBackButton />
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography color="text.secondary">
              Loading practitioner onboarding references...
            </Typography>
          </Stack>
        </Box>
      </>
    )
  }

  // Phase 12 — when the page is opened with no ?type query param, render the type picker
  // instead of the form. Each tile deep-links into the same page with ?type=doctor|therapist|
  // screener so the existing scoped-flow behaviour kicks in.
  if (!isScopedPractitionerFlow) {
    return <PractitionerOnboardingTypePicker />
  }

  return (
    <>
      <SupportHeader heading={pageHeading} showBackButton />

      <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f8fafc', minHeight: '100%' }}>
        <Stack spacing={3}>
          <Paper
            sx={{
              ...SECTION_PAPER_SX,
              background:
                'linear-gradient(135deg, rgba(7, 89, 133, 0.08) 0%, rgba(15, 118, 110, 0.08) 100%)',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '14px',
                    display: 'grid',
                    placeItems: 'center',
                    color: '#fff',
                    background:
                      'linear-gradient(135deg, rgba(7, 89, 133, 1) 0%, rgba(15, 118, 110, 1) 100%)',
                  }}
                >
                  <PersonAddAlt1 />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {isScopedPractitionerFlow
                      ? `Single-Flow ${practitionerTypeLabel} Onboarding`
                      : 'Single-Flow Practitioner Onboarding'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isScopedPractitionerFlow
                      ? `Create a pre-approved ${practitionerTypeLabel.toLowerCase()}, attach clinics and rooms, link assistants, configure clinic-driven events, save engagement visibility, and track go-live readiness from one page.`
                      : 'Create or resume a practitioner, enable services, attach clinics and rooms, link assistants, configure clinic-driven events, save engagement visibility, and track go-live readiness from one page.'}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Chip
                  label={
                    isScopedPractitionerFlow
                      ? `Scoped to: ${practitionerTypeLabel}`
                      : `Allowed: ${allowedPractitionerTypes.map((type) => PRACTITIONER_TYPE_LABELS[type] || type).join(', ')}`
                  }
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => selectedPractitioner && loadPractitionerDetail(selectedPractitioner)}
                  disabled={!selectedPractitioner || detailState.loading}
                >
                  Refresh Status
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {bootstrapState.error && <Alert severity="error">{bootstrapState.error}</Alert>}
          {detailState.error && <Alert severity="warning">{detailState.error}</Alert>}

          {/* Draft auto-save banner — surfaces when localStorage has a draft for this
              practitioner+type that meaningfully differs from the freshly-loaded form.
              The agent decides whether to resume their unsaved work or discard. */}
          {pendingDraft && (
            <Alert
              severity="info"
              action={
                <Stack direction="row" spacing={1}>
                  <Button color="inherit" size="small" onClick={handleDraftDiscard}>
                    Discard
                  </Button>
                  <Button color="primary" size="small" variant="contained" onClick={handleDraftRestore}>
                    Resume draft
                  </Button>
                </Stack>
              }
            >
              You have an unsaved draft from{' '}
              {pendingDraft.savedAt
                ? new Date(pendingDraft.savedAt).toLocaleString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'an earlier session'}
              . Resume editing where you left off, or discard to keep the freshly loaded data.
            </Alert>
          )}

          {/* Two-pane layout (Phase 11): explicit flexbox with the sidebar pinned via
              position:sticky and its own internal scroll. Dropping MUI Grid here — Grid's
              internal flex semantics (alignItems:stretch by default plus its container/item
              dance) keep tripping up sticky in subtle ways across breakpoints. A plain Box flex
              with alignSelf:flex-start on the sticky child is the most reliable pattern: the
              sidebar stays in document flow, the right column drives page height, and the
              sidebar pins to top:STICKY_TOP_OFFSET while remaining within the flex container.
              On mobile (<lg), the layout collapses to a column with the form first so the
              checklist doesn't push the form below the fold. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'flex-start',
              gap: 3,
            }}
          >
            <Box
              ref={checklistScrollRef}
              sx={{
                width: { xs: '100%', md: 320, lg: 380 },
                flexShrink: 0,
                // Sticky kicks in at md (≥900px) — most support agents work on 1366×768 / 1440×900
                // laptops, which sit in md. Pinning only at lg meant the sidebar hid on the
                // majority of screens. md+ gives us the desk-class behaviour everyone expects.
                position: { md: 'sticky' },
                top: { md: 16 },
                alignSelf: 'flex-start',
                maxHeight: { md: 'calc(100vh - 32px)' },
                overflowY: { md: 'auto' },
                // Phase 13 — hide the visible scrollbar; ChecklistPanel programmatically scrolls
                // the active item into view when the right form is scrolled, so the user never
                // sees a scrollbar but the active checklist item always stays in view.
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                order: { xs: 2, md: 1 },
                zIndex: 1,
              }}
            >
              <ChecklistPanel
                detail={detailState.data}
                checklist={checklist}
                activeSectionId={activeSectionId}
                scrollContainerRef={checklistScrollRef}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%', order: { xs: 1, md: 2 } }}>
              <Stack spacing={3}>
                {/* Phase 14ai — applicant-submission review panel. Renders only when the
                    practitioner was created via /apply (or has any /apply-uploaded media).
                    Shows banking + uploaded documents + applicant context as read-only,
                    with a Mark Verified flow for banking. The agent uses this to cross-check
                    against the editable Practitioner form below. */}
                {!isAuditorDedicatedView && !isScopedPractitionerFlow && (
                  <ApplicantSubmissionsPanel
                    practitioner={detailState.data?.practitioner}
                    applicantDetails={detailState.data?.applicantDetails}
                    onBankingChanged={(nextBanking) => {
                      setDetailState((prev) => {
                        if (!prev?.data) return prev
                        const prevApplicantDetails = prev.data.applicantDetails || {}
                        return {
                          ...prev,
                          data: {
                            ...prev.data,
                            applicantDetails: {
                              ...prevApplicantDetails,
                              banking: nextBanking || null,
                            },
                          },
                        }
                      })
                    }}
                  />
                )}
                {/* On the dedicated /auditor route the page renders only the Auditor Access card
                    (everything above it is hidden, and Save And Review below is hidden too). The
                    auto-scroll effect from Phase 4 still runs to keep the deep-link feel. */}
                {!isAuditorDedicatedView && !isScopedPractitionerFlow && (
                  <Box id={SECTION_PROFILE_SCOPE} sx={{ scrollMarginTop: 88 }} />
                )}
                {!isAuditorDedicatedView && !isScopedPractitionerFlow && (
                  <SectionCard
                    title="Profile Scope"
                    description="Pick the practitioner type you are onboarding, or resume an existing practitioner to continue setup."
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Practitioner Type"
                          value={form.practitionerType}
                          onChange={(event) => handlePractitionerTypeChange(event.target.value)}
                          disabled={Boolean(selectedPractitioner)}
                          helperText={
                            PRACTITIONER_TYPE_HELPER[form.practitionerType] ||
                            'Choose the practitioner persona for this onboarding flow.'
                          }
                        >
                          {allowedPractitionerTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {PRACTITIONER_TYPE_LABELS[type] || type}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={practitionerOptions}
                          value={selectedPractitioner}
                          onChange={(_event, nextValue) => {
                            if (!nextValue) {
                              setSelectedPractitioner(null)
                              setDetailState({ loading: false, error: '', data: null })
                              setClinicAppointmentTypesByKey({})
                              setPatientContactVisibility(null)
                              setPatientContactVisibilityError('')
                              setForm(createEmptyForm(form.practitionerType || allowedPractitionerTypes[0] || ''))
                              return
                            }
                            loadPractitionerDetail(nextValue)
                          }}
                          getOptionLabel={(option) =>
                            `${getOptionLabel(option)}${option?.subtitle ? ` (${option.subtitle})` : ''}`
                          }
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Resume Existing Practitioner"
                              helperText="Optional: load an existing practitioner to continue onboarding or fix pending items."
                            />
                          )}
                        />
                      </Grid>
                      {selectedPractitioner && (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            Existing practitioner selected. Saving will update the practitioner and fill in any missing onboarding steps.
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </SectionCard>
                )}

                {!isAuditorDedicatedView && (
                <>
                <Box id={SECTION_BASIC_PROFILE} sx={{ scrollMarginTop: 88 }} />
                <SectionCard
                  title="Basic Profile"
                  description="Capture the contact, slug, image, and public profile data needed to create the practitioner record."
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          alignItems={{ md: 'center' }}
                        >
                          <Box sx={{ minWidth: { md: 220 } }}>
                            <PictureUploadComponent
                              id="profilePicId"
                              value={form.profilePicId}
                              onChange={(event) => setField('profilePicId', event.target.value)}
                              title="Profile Picture"
                              aspectWidth={300}
                              aspectHeight={300}
                              showRemoveButton={Boolean(form.profilePicId)}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                              Basic identity and portal listing
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Upload the profile picture used in therapist or doctor listings, then finish the public identity fields below.
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Combined Name field. Saves the full string to firstName per the support-portal
                        onboarding contract; lastName is intentionally left blank because the
                        practitioner record uses firstName as the canonical display string. */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        inputRef={setFieldRef('firstName')}
                        fullWidth
                        required={!selectedPractitioner}
                        label={selectedPractitioner ? 'Name' : 'Name *'}
                        value={form.firstName}
                        error={Boolean(submitErrors.firstName)}
                        onChange={(event) => {
                          setForm((prev) => ({ ...prev, firstName: event.target.value, lastName: '' }))
                          setSubmitErrors((prev) => {
                            if (!prev.firstName) return prev
                            const next = { ...prev }
                            delete next.firstName
                            return next
                          })
                        }}
                        helperText={
                          submitErrors.firstName ||
                          "Single field — saved to firstName internally. Include any honorific the practitioner uses (e.g., 'Aria Sharma')."
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Display Title"
                        value={form.title}
                        onChange={(event) => setField('title', event.target.value)}
                        placeholder="Dr. Aria Sharma, MBBS, MD"
                        helperText="Long-form practitioner title used in listings and reports. Include qualifications/honorifics."
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <TextField
                          inputRef={setFieldRef('slug')}
                          fullWidth
                          label="Slug"
                          value={form.slug}
                          onChange={(event) => {
                            setField('slug', event.target.value.toLowerCase().replace(/\s+/g, '-'))
                            // Clear the submit-time slug error as soon as the agent starts editing.
                            setSubmitErrors((prev) => {
                              if (!prev.slug) return prev
                              const next = { ...prev }
                              delete next.slug
                              return next
                            })
                          }}
                          error={Boolean(submitErrors.slug)}
                          helperText={submitErrors.slug || slugState.message}
                        />
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {slugState.loadingSuggestion && <Chip size="small" label="Suggesting..." variant="outlined" />}
                          {slugState.validating && <Chip size="small" label="Checking..." variant="outlined" />}
                          {slugState.available === true && <Chip size="small" color="success" label="Available" />}
                          {slugState.available === false && <Chip size="small" color="error" label="Unavailable" />}
                          {slugState.suggestedSlug &&
                            slugState.suggestedSlug !== trimValue(form.slug) && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setField('slug', slugState.suggestedSlug)}
                              >
                                Use {slugState.suggestedSlug}
                              </Button>
                            )}
                        </Stack>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Gender"
                        value={form.gender}
                        onChange={(event) => setField('gender', event.target.value)}
                      >
                        <MenuItem value="">Prefer not to say</MenuItem>
                        {GENDER_LIST_ONBOARDING.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Email + verification + duplicate-check */}
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <TextField
                          inputRef={setFieldRef('email')}
                          fullWidth
                          label="Email"
                          type="email"
                          value={form.email}
                          error={Boolean(submitErrors.email)}
                          onChange={(event) => {
                            setField('email', event.target.value)
                            setSubmitErrors((prev) => {
                              if (!prev.email) return prev
                              const next = { ...prev }
                              delete next.email
                              return next
                            })
                          }}
                          helperText={submitErrors.email || ' '}
                        />
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          {form.emailVerified ? (
                            <Chip
                              size="small"
                              color="success"
                              icon={<CheckCircleOutline fontSize="small" />}
                              label={form.emailVerifiedOfflineBy ? 'Verified (offline)' : 'Verified'}
                            />
                          ) : (
                            <Chip
                              size="small"
                              icon={<RadioButtonUnchecked fontSize="small" />}
                              label="Not verified"
                            />
                          )}
                          {existenceCheck.email.count != null && existenceCheck.email.count > 0 && (
                            <Link
                              component="button"
                              type="button"
                              onClick={() => setShowExistenceModal('email')}
                              sx={{ fontSize: 13 }}
                            >
                              {existenceCheck.email.count} existing user
                              {existenceCheck.email.count === 1 ? '' : 's'} with this email
                            </Link>
                          )}
                          <Button
                            size="small"
                            variant="text"
                            disabled={
                              !form.userId ||
                              form.emailVerified ||
                              verifyOfflinePending.email
                            }
                            onClick={() => handleMarkVerifiedOffline('email')}
                          >
                            {verifyOfflinePending.email ? 'Marking…' : 'Mark verified offline'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Grid>

                    {/* Mobile + verification + duplicate-check */}
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <TextField
                          fullWidth
                          label="Mobile"
                          value={form.mobile}
                          onChange={(event) => setField('mobile', event.target.value)}
                        />
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          {form.mobileVerified ? (
                            <Chip
                              size="small"
                              color="success"
                              icon={<CheckCircleOutline fontSize="small" />}
                              label={form.mobileVerifiedOfflineBy ? 'Verified (offline)' : 'Verified'}
                            />
                          ) : (
                            <Chip
                              size="small"
                              icon={<RadioButtonUnchecked fontSize="small" />}
                              label="Not verified"
                            />
                          )}
                          {existenceCheck.mobile.count != null && existenceCheck.mobile.count > 0 && (
                            <Link
                              component="button"
                              type="button"
                              onClick={() => setShowExistenceModal('mobile')}
                              sx={{ fontSize: 13 }}
                            >
                              {existenceCheck.mobile.count} existing user
                              {existenceCheck.mobile.count === 1 ? '' : 's'} with this mobile
                            </Link>
                          )}
                          <Button
                            size="small"
                            variant="text"
                            disabled={
                              !form.userId ||
                              form.mobileVerified ||
                              verifyOfflinePending.mobile
                            }
                            onClick={() => handleMarkVerifiedOffline('mobile')}
                          >
                            {verifyOfflinePending.mobile ? 'Marking…' : 'Mark verified offline'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Grid>

                    {/* Primary contact + KYC chip. Phase 14g — only auto-pinned when exactly one
                        of email / mobile is filled; with both filled the agent must consciously
                        pick (mobile is the default suggestion but no longer pre-committed). */}
                    <Grid item xs={12} md={6}>
                      <Paper
                        ref={setFieldRef('primaryContact')}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          borderColor: submitErrors.primaryContact ? 'error.main' : undefined,
                          borderWidth: submitErrors.primaryContact ? 2 : 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Primary contact (used for login)
                          {(form.email || '').trim() && (form.mobile || '').trim() ? (
                            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                          ) : null}
                        </Typography>
                        <RadioGroup
                          row
                          value={form.primaryContact || ''}
                          onChange={(event) => {
                            setField('primaryContact', event.target.value)
                            setSubmitErrors((prev) => {
                              if (!prev.primaryContact) return prev
                              const next = { ...prev }
                              delete next.primaryContact
                              return next
                            })
                          }}
                        >
                          <FormControlLabel
                            value="mobile"
                            control={<Radio size="small" />}
                            label="Mobile"
                            disabled={!(form.mobile || '').trim()}
                          />
                          <FormControlLabel
                            value="email"
                            control={<Radio size="small" />}
                            label="Email"
                            disabled={!(form.email || '').trim()}
                          />
                        </RadioGroup>
                        {submitErrors.primaryContact ? (
                          <Typography variant="caption" color="error.main">
                            {submitErrors.primaryContact}
                          </Typography>
                        ) : !((form.email || '').trim() || (form.mobile || '').trim()) ? (
                          <Typography variant="caption" color="text.secondary">
                            Add an email or mobile first — the channel auto-pins to whichever you use.
                          </Typography>
                        ) : null}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="KYC Status"
                        value="approved"
                        disabled
                        helperText="This onboarding flow approves KYC as part of the save step."
                      >
                        <MenuItem value="approved">Approved</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Different communication numbers — collapsed by default */}
                    <Grid item xs={12}>
                      <Accordion variant="outlined" disableGutters sx={{ borderRadius: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">
                            Different WhatsApp / voice-call numbers (optional)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="WhatsApp number (if different from mobile)"
                                value={form.whatsappNumber}
                                onChange={(event) => setField('whatsappNumber', event.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Voice-call number (if different from mobile)"
                                value={form.voiceCallNumber}
                                onChange={(event) => setField('voiceCallNumber', event.target.value)}
                              />
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>

                    {/* Therapy disciplines come BEFORE Specializations because the discipline pick
                        scopes which specialization catalog is loaded. KYC-locked; therapist-only.
                        The backend onboarding service ignores this field for doctor / screener
                        approvals so it's hidden for those flows. */}
                    {form.practitionerType === 'therapist' && (
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={THERAPY_DISCIPLINE_OPTIONS.map((opt) => opt.code)}
                          getOptionLabel={(code) =>
                            THERAPY_DISCIPLINE_OPTIONS.find((opt) => opt.code === code)?.label || code
                          }
                          value={form.approvedTherapyDisciplines || []}
                          onChange={(_event, nextValue) =>
                            setField('approvedTherapyDisciplines', normalizeStringArray(nextValue))
                          }
                          renderTags={(value, getTagProps) =>
                            value.map((code, index) => {
                              const label =
                                THERAPY_DISCIPLINE_OPTIONS.find((opt) => opt.code === code)?.label || code
                              return (
                                <Chip
                                  label={label}
                                  size="small"
                                  color={
                                    code === THERAPY_DISCIPLINE_PSYCHOTHERAPY ? 'default' : 'primary'
                                  }
                                  {...getTagProps({ index })}
                                  key={`${code}-${index}`}
                                />
                              )
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              inputRef={setFieldRef('approvedTherapyDisciplines')}
                              label="Therapy Disciplines (KYC-locked) *"
                              required
                              error={Boolean(submitErrors.approvedTherapyDisciplines)}
                              helperText={
                                submitErrors.approvedTherapyDisciplines ||
                                'Required. Drives the specialization catalog and clinic-event filtering below. Add Nutrition Consultation to allow this practitioner to create nutrition events.'
                              }
                            />
                          )}
                        />
                      </Grid>
                    )}

                    {/* Specializations: discipline-aware catalog (preferred + fallback) loaded
                        for the discipline picked above. Falls back to the generic
                        /support/categories list when no discipline is set (e.g. doctor flow). */}
                    <Grid item xs={12}>
                      {specializationCatalog && (specializationCatalog.preferred?.length || specializationCatalog.fallback?.length) ? (
                        (() => {
                          const grouped = [
                            ...(specializationCatalog.preferred || []).map((item) => ({
                              ...item,
                              group: 'Suggested for ' + (specializationCatalog.displayLabel || activeDiscipline),
                            })),
                            ...(specializationCatalog.fallback || []).map((item) => ({
                              ...item,
                              group: 'Other',
                            })),
                          ]
                          return (
                            <Autocomplete
                              multiple
                              freeSolo
                              options={grouped}
                              groupBy={(option) => option.group}
                              getOptionLabel={(option) =>
                                typeof option === 'string'
                                  ? option
                                  : option?.label || option?.id || ''
                              }
                              isOptionEqualToValue={(option, value) =>
                                (typeof option === 'string' ? option : option.id) ===
                                (typeof value === 'string' ? value : value.id)
                              }
                              value={form.specializations}
                              onChange={(_event, nextValue) =>
                                setField(
                                  'specializations',
                                  nextValue.map((v) => (typeof v === 'string' ? v : v.id)).filter(Boolean),
                                )
                              }
                              renderTags={(value, getTagProps) =>
                                value.map((id, index) => {
                                  const match = grouped.find((opt) => opt.id === id)
                                  return (
                                    <Chip
                                      label={match?.label || id}
                                      size="small"
                                      {...getTagProps({ index })}
                                      key={`${id}-${index}`}
                                    />
                                  )
                                })
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Specializations"
                                  helperText={`Pick from the curated list for ${specializationCatalog.displayLabel || activeDiscipline}, or type a custom value.`}
                                />
                              )}
                            />
                          )
                        })()
                      ) : (
                        <Autocomplete
                          multiple
                          freeSolo
                          options={filteredCategoryOptions.map((opt) => opt.id)}
                          getOptionLabel={(optionId) => {
                            const match = filteredCategoryOptions.find((opt) => opt.id === optionId)
                            return match?.title || match?.name || optionId || ''
                          }}
                          value={form.specializations}
                          onChange={(_event, nextValue) => setField('specializations', normalizeStringArray(nextValue))}
                          renderTags={(value, getTagProps) =>
                            value.map((optionId, index) => {
                              const match = filteredCategoryOptions.find((opt) => opt.id === optionId)
                              const label = match?.title || match?.name || optionId
                              return (
                                <Chip label={label} size="small" {...getTagProps({ index })} key={`${optionId}-${index}`} />
                              )
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Specializations"
                              helperText={
                                filteredCategoryOptions.length > 0
                                  ? 'Pick one or more specialisations from the catalogue.'
                                  : 'Specialisation catalogue is loading — free-form entry is allowed as a fallback.'
                              }
                            />
                          )}
                        />
                      )}
                    </Grid>

                    {/* Bio: shown to patients on listing pages. The placeholder is generated from
                        the current discipline / role / specializations + name (Phase 14g) so it
                        looks like a real example rather than a generic stand-in. Pressing Tab
                        when the field is empty fills the textarea with the hint, which the agent
                        can then edit lightly. */}
                    <Grid item xs={12}>
                      <TextField
                        inputRef={setFieldRef('shortDesc')}
                        fullWidth
                        multiline
                        minRows={3}
                        label="Bio (visible to patients)"
                        value={form.shortDesc}
                        onChange={(event) => setField('shortDesc', event.target.value)}
                        onKeyDown={(event) => {
                          // Tab-to-fill: only when the field is empty AND the user pressed Tab
                          // alone (not Shift+Tab, which navigates backwards). preventDefault
                          // keeps focus on the field so they can continue editing immediately.
                          if (
                            event.key === 'Tab' &&
                            !event.shiftKey &&
                            !(form.shortDesc || '').trim() &&
                            bioHint
                          ) {
                            event.preventDefault()
                            setField('shortDesc', bioHint)
                          }
                        }}
                        placeholder={bioHint}
                        helperText={
                          (form.shortDesc || '').trim()
                            ? '2–4 lines, written in third person. Mention experience, modalities, populations, and languages.'
                            : 'Tip — press Tab to fill with a sample bio tailored to the selected discipline + specializations + name. Edit lightly.'
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Accordion variant="outlined" disableGutters sx={{ borderRadius: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">More details (optional long description)</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Long Description"
                            value={form.longDesc}
                            onChange={(event) => setField('longDesc', event.target.value)}
                            helperText="Optional. Used for richer profile pages where space allows."
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </SectionCard>

                <Box id={SECTION_CREDENTIALS} sx={{ scrollMarginTop: 88 }} />
                <SectionCard
                  title="Professional Credentials"
                  description="Bring in the mentor-portal style fields for qualifications, work experience, registration details, documents, and prescription signature/license metadata."
                  action={
                    <Button
                      variant="outlined"
                      startIcon={<AddCircleOutline />}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          certificateDocuments: [...prev.certificateDocuments, createEmptyDocument()],
                        }))
                      }
                    >
                      Add Document
                    </Button>
                  }
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {qualificationCatalog && (qualificationCatalog.preferred?.length || qualificationCatalog.fallback?.length) ? (
                        (() => {
                          const grouped = [
                            ...(qualificationCatalog.preferred || []).map((item) => ({
                              id: item.id || item.label,
                              label: item.label,
                              group: 'Suggested for ' + (qualificationCatalog.displayLabel || activeDiscipline),
                            })),
                            ...(qualificationCatalog.fallback || []).map((item) => ({
                              id: item.longTitle || item.title || item.id,
                              label: item.longTitle || item.title || item.id,
                              group: 'Other',
                            })),
                          ]
                          return (
                            <Autocomplete
                              multiple
                              freeSolo
                              options={grouped}
                              groupBy={(option) => option.group}
                              getOptionLabel={(option) =>
                                typeof option === 'string'
                                  ? option
                                  : option?.label || option?.id || ''
                              }
                              isOptionEqualToValue={(option, value) =>
                                (typeof option === 'string' ? option : option.label) ===
                                (typeof value === 'string' ? value : value.label)
                              }
                              value={form.qualifications}
                              onChange={(_event, nextValue) =>
                                setField(
                                  'qualifications',
                                  nextValue.map((v) => (typeof v === 'string' ? v : v.label)).filter(Boolean),
                                )
                              }
                              renderTags={(value, getTagProps) =>
                                value.map((label, index) => (
                                  <Chip
                                    label={label}
                                    size="small"
                                    {...getTagProps({ index })}
                                    key={`${label}-${index}`}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Qualifications"
                                  helperText={`Pick from the curated list for ${qualificationCatalog.displayLabel || activeDiscipline}, or type a custom value.`}
                                />
                              )}
                            />
                          )
                        })()
                      ) : (
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={form.qualifications}
                          onChange={(_event, nextValue) =>
                            setField('qualifications', normalizeStringArray(nextValue))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Qualifications"
                              helperText="Add qualifications like MBBS, MPhil, PsyD, RCI-certified, etc."
                            />
                          )}
                        />
                      )}
                    </Grid>

                    {/* Mandatory document upload — RCI license copy when an RCI registration ID is
                        present, otherwise the practitioner's highest relevant educational record. */}
                    {(() => {
                      const hasRciRegistration = (form.registrationIds || []).some(
                        (r) => trimValue(r?.typeId).toLowerCase().includes('rci') && trimValue(r?.id),
                      )
                      const docLabel = hasRciRegistration
                        ? 'RCI License copy (mandatory)'
                        : 'Highest relevant educational document (mandatory)'
                      const docHelper = hasRciRegistration
                        ? 'Upload a clear copy of the RCI license. PDF or image; the practitioner cannot run sessions without this on file.'
                        : 'No RCI license on file — upload the highest relevant educational document/record (degree, diploma, internship completion, etc.).'
                      const isMissing = !trimValue(form.mandatoryCredentialDocFileId)
                      return (
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              borderColor: isMissing ? 'warning.main' : 'divider',
                              borderWidth: isMissing ? 2 : 1,
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                              {isMissing && <Warning fontSize="small" color="warning" />}
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {docLabel}
                              </Typography>
                            </Stack>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} md={5}>
                                <TextField
                                  fullWidth
                                  label="Document Title"
                                  value={form.mandatoryCredentialDocTitle}
                                  onChange={(event) => setField('mandatoryCredentialDocTitle', event.target.value)}
                                  placeholder={
                                    hasRciRegistration ? 'RCI License (front)' : 'M.Phil Clinical Psychology degree'
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={7}>
                                <DocumentUploadComponent
                                  id="mandatoryCredentialDocFileId"
                                  value={form.mandatoryCredentialDocFileId}
                                  fileType="application/pdf,image/*"
                                  showDownloadButton
                                  onChange={(event) =>
                                    setField('mandatoryCredentialDocFileId', event.target.value)
                                  }
                                  // Phase 14p — open the file inside an in-app side drawer instead
                                  // of jumping straight to a new browser tab. The drawer keeps the
                                  // agent's place in the form and still offers an "Open in new tab"
                                  // button for callers who want the wider native viewer.
                                  handleView={
                                    form.mandatoryCredentialDocFileId
                                      ? () =>
                                          setCredentialDocViewer({
                                            fileId: form.mandatoryCredentialDocFileId,
                                            title:
                                              form.mandatoryCredentialDocTitle ||
                                              'Mandatory credential document',
                                          })
                                      : undefined
                                  }
                                />
                              </Grid>
                            </Grid>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              {docHelper}
                            </Typography>
                          </Paper>
                        </Grid>
                      )
                    })()}
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Experience Start Month"
                        value={form.experienceStartMonth}
                        onChange={(event) => setField('experienceStartMonth', event.target.value)}
                      >
                        <MenuItem value="">Not set</MenuItem>
                        {EXPERIENCE_MONTH_OPTIONS.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Experience Start Year"
                        value={form.experienceStartYear}
                        onChange={(event) => setField('experienceStartYear', event.target.value)}
                      >
                        <MenuItem value="">Not set</MenuItem>
                        {EXPERIENCE_YEAR_OPTIONS.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Registration IDs
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddCircleOutline />}
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              registrationIds: [...prev.registrationIds, createEmptyRegistrationId()],
                            }))
                          }
                        >
                          Add Registration ID
                        </Button>
                      </Stack>
                      {form.registrationIds.length === 0 ? (
                        <Alert severity="info">Add registration identifiers like RCI, MCI, or CMO when applicable.</Alert>
                      ) : (
                        <Stack spacing={2}>
                          {form.registrationIds.map((registrationId, index) => (
                            <Paper key={`registration-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={5}>
                                  {registrationTypeOptions.length > 0 ? (
                                    <TextField
                                      select
                                      fullWidth
                                      label="Registration Type"
                                      value={registrationId.typeId}
                                      onChange={(event) =>
                                        updateRegistrationId(index, { typeId: event.target.value })
                                      }
                                    >
                                      <MenuItem value="">Select type</MenuItem>
                                      {registrationTypeOptions.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                          {option.title}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  ) : (
                                    <TextField
                                      fullWidth
                                      label="Registration Type"
                                      value={registrationId.typeId}
                                      onChange={(event) =>
                                        updateRegistrationId(index, { typeId: event.target.value })
                                      }
                                    />
                                  )}
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <TextField
                                    fullWidth
                                    label="Registration ID"
                                    value={registrationId.id}
                                    onChange={(event) =>
                                      updateRegistrationId(index, { id: event.target.value })
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <Button
                                    fullWidth
                                    color="error"
                                    startIcon={<DeleteOutline />}
                                    onClick={() =>
                                      setForm((prev) => ({
                                        ...prev,
                                        registrationIds: prev.registrationIds.filter(
                                          (_item, itemIndex) => itemIndex !== index,
                                        ),
                                      }))
                                    }
                                  >
                                    Remove
                                  </Button>
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Credential Documents
                      </Typography>
                      {form.certificateDocuments.length === 0 ? (
                        <Alert severity="info">
                          Upload certificates, registrations, or KYC support files here. These are saved into the practitioner credential record.
                        </Alert>
                      ) : (
                        <Stack spacing={2}>
                          {form.certificateDocuments.map((document, index) => (
                            <Paper key={`document-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Document Title"
                                    value={document.title}
                                    onChange={(event) => updateDocument(index, { title: event.target.value })}
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <DocumentUploadComponent
                                    id={`document-file-${index}`}
                                    value={document.fileId}
                                    fileType="application/pdf,image/*"
                                    showDownloadButton
                                    onChange={(event) => updateDocument(index, { fileId: event.target.value })}
                                    // Phase 14p — same in-app side-drawer viewer as the mandatory
                                    // credential row above; keeps the agent's place in the form.
                                    handleView={
                                      document.fileId
                                        ? () =>
                                            setCredentialDocViewer({
                                              fileId: document.fileId,
                                              title:
                                                document.title ||
                                                `Credential document ${index + 1}`,
                                            })
                                        : undefined
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <Button
                                    fullWidth
                                    color="error"
                                    startIcon={<DeleteOutline />}
                                    onClick={() =>
                                      setForm((prev) => ({
                                        ...prev,
                                        certificateDocuments: prev.certificateDocuments.filter(
                                          (_item, itemIndex) => itemIndex !== index,
                                        ),
                                      }))
                                    }
                                  >
                                    Remove
                                  </Button>
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {/* Phase 14g — signature is upload-only on the support side. Support agents
                          can't sign for the practitioner, so the canvas is intentionally hidden;
                          they upload the image the practitioner emails / shares. The mentor-portal
                          Stage 2 page exposes the canvas (alongside upload) so the practitioner
                          can sign there. */}
                      <SignaturePad
                        id="signatureMediaId"
                        value={form.signatureMediaId}
                        onChange={(event) => setField('signatureMediaId', event.target.value)}
                        title="Signature (upload only)"
                        helperText="Support agents can only upload an image of the practitioner's signature; the practitioner signs themselves on the mentor-portal Stage 2 page."
                        mode="upload-only"
                        width={400}
                        height={180}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {/* License Type — discipline + role aware (Phase 14g). The dropdown is
                          built from a curated catalog (RCI / IDA / NMC / etc.) plus two synthetic
                          fallbacks ("License Not Required" / "License Not Available") so the
                          agent has an out for screeners-in-house, freshly-graduated psychology
                          students, or practitioners whose paperwork is still in transit. */}
                      <Autocomplete
                        freeSolo
                        options={licenseOptions}
                        value={form.licenseType || ''}
                        onChange={(_event, value) => setField('licenseType', value || '')}
                        onInputChange={(_event, value, reason) => {
                          if (reason === 'input') setField('licenseType', value)
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="License Type"
                            helperText={
                              form.licenseType === LICENSE_NOT_REQUIRED
                                ? 'Marked as not required — License Number can stay blank.'
                                : form.licenseType === LICENSE_NOT_AVAILABLE
                                  ? 'Marked as not available — chase the number before approving.'
                                  : 'Pick from the curated list, or type a custom one. Free-text accepted.'
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="License Number"
                        value={form.licenseNumber}
                        onChange={(event) => setField('licenseNumber', event.target.value)}
                        // Disable the number when the type is "Not Required" — there's nothing to
                        // capture and it nudges the agent to leave it empty instead of guessing.
                        disabled={form.licenseType === LICENSE_NOT_REQUIRED}
                        helperText={
                          form.licenseType === LICENSE_NOT_REQUIRED
                            ? 'Not required for this license type.'
                            : ' '
                        }
                      />
                    </Grid>
                  </Grid>
                </SectionCard>

                {/* Service Approvals & Defaults — moved above Assessment Settings + Clinic
                    Assignment so the practitioner-wide flags + target specialty selection drive
                    which assessment lists and clinic event types are surfaced below. */}
                <Box id={SECTION_SERVICE_APPROVALS} sx={{ scrollMarginTop: 88 }} />
                <SectionCard
                  title="Service Approvals & Defaults"
                  description="Service approval and default-event toggles, plus the target specialties that scope assessment lists and clinic event types in the sections below."
                >
                  {disciplineGateBanner()}
                  <Box sx={disciplineMissing ? { opacity: 0.5, pointerEvents: 'none' } : null}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(form.enableServices)}
                              onChange={(event) => setField('enableServices', event.target.checked)}
                            />
                          }
                          label="Enable approved services"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(form.createDefaultEvents)}
                              onChange={(event) => setField('createDefaultEvents', event.target.checked)}
                            />
                          }
                          label="Create practitioner events if missing"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(form.listableInWeb)}
                              onChange={(event) => setField('listableInWeb', event.target.checked)}
                            />
                          }
                          label="Listable on web"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(form.allowedToPrescribe)}
                              onChange={(event) => setField('allowedToPrescribe', event.target.checked)}
                              disabled={form.practitionerType !== 'doctor'}
                            />
                          }
                          label="Allowed to prescribe"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.allowSelfReschedule !== false}
                              onChange={(event) => setField('allowSelfReschedule', event.target.checked)}
                            />
                          }
                          label="Can self-reschedule"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.allowSelfCancel !== false}
                              onChange={(event) => setField('allowSelfCancel', event.target.checked)}
                            />
                          }
                          label="Can self-cancel"
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      {form.practitionerType === 'screener' ? (
                        <Autocomplete
                          options={targetSpecialtyOptions}
                          value={selectedTargetSpecialty}
                          onChange={(_event, nextValue) =>
                            setField(
                              'targetSpecialitySlugs',
                              nextValue ? [nextValue.slug || nextValue.id].filter(Boolean) : [],
                            )
                          }
                          getOptionLabel={(option) =>
                            option?.subtitle ? `${option.title} (${option.subtitle})` : getOptionLabel(option)
                          }
                          isOptionEqualToValue={(option, value) =>
                            (option?.slug || option?.id) === (value?.slug || value?.id)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              label="Screening Target Specialty"
                              helperText="Required for screener config, screening event routing, and screening report form selection."
                            />
                          )}
                        />
                      ) : (
                        <Autocomplete
                          multiple
                          options={targetSpecialtyOptions}
                          value={selectedTargetSpecialties}
                          onChange={(_event, nextValue) =>
                            setField(
                              'targetSpecialitySlugs',
                              nextValue.map((option) => option.slug || option.id),
                            )
                          }
                          getOptionLabel={(option) =>
                            option?.subtitle ? `${option.title} (${option.subtitle})` : getOptionLabel(option)
                          }
                          isOptionEqualToValue={(option, value) =>
                            (option?.slug || option?.id) === (value?.slug || value?.id)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Target Specialties"
                              helperText="Drives which assessment lists are shown below and which clinic event types surface in Clinic Assignment."
                            />
                          )}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info">
                        Per-clinic event seeding lives inside each Clinic Assignment row below (Therapy Session / Assessment tabs).
                      </Alert>
                    </Grid>
                  </Grid>
                  </Box>
                </SectionCard>

                {/* Assessment Settings — sits between Service Approvals and Clinic Assignment so
                    the agent picks Allowed-to-Assess + assessment lists before configuring per-
                    clinic event seeding. Therapist-only. */}
                {form.practitionerType === 'therapist' && (
                  <Box id={SECTION_ASSESSMENT} sx={{ scrollMarginTop: 88 }} />
                )}
                {form.practitionerType === 'therapist' && (
                  <SectionCard
                    title="Assessment Settings"
                    description="Approve the practitioner to run assessments and pick the assessment lists they're allowed to administer. Service approval entries are written into TeacherUneditableFields so the practitioner can see clinic appointments tagged as assessments."
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(form.allowedToAssess)}
                              onChange={(event) => setField('allowedToAssess', event.target.checked)}
                            />
                          }
                          label="Allowed To Assess"
                        />
                      </Grid>
                      {/* Assessment-list picker only renders once Allowed-to-Assess is on. Lists
                          are scoped client-side to the practitioner's approved discipline AND
                          target specialties (see assessmentListOptions memo). Each selected list
                          gets a "Preview" chip the agent can click to inspect the list contents. */}
                      {Boolean(form.allowedToAssess) && (
                        <Grid item xs={12}>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={assessmentListOptions}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') {
                                const match = allAssessmentListOptions.find(
                                  (item) => item?.id === option,
                                )
                                return match?.title || option
                              }
                              return option?.subtitle
                                ? `${option.title} (${option.subtitle})`
                                : option?.title || option?.id || ''
                            }}
                            isOptionEqualToValue={(option, value) =>
                              (typeof option === 'string' ? option : option?.id) ===
                              (typeof value === 'string' ? value : value?.id)
                            }
                            value={form.assessmentListIds}
                            onChange={(_event, nextValue) =>
                              setField(
                                'assessmentListIds',
                                nextValue
                                  .map((v) => (typeof v === 'string' ? v : v?.id))
                                  .filter(Boolean),
                              )
                            }
                            renderTags={(value, getTagProps) =>
                              value.map((id, index) => {
                                const match = allAssessmentListOptions.find((item) => item?.id === id)
                                return (
                                  <Chip
                                    label={match?.title || id}
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setAssessmentPreviewId(id)
                                    }}
                                    {...getTagProps({ index })}
                                    key={`${id}-${index}`}
                                  />
                                )
                              })
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Assessment Lists"
                                helperText={
                                  assessmentListOptions.length > 0
                                    ? 'Click a chip to preview the list. Lists are scoped to this practitioner\'s discipline + target specialties; free-form ids are still accepted.'
                                    : 'No matching assessment lists for this discipline + target specialty combination — free-form ids are accepted as a fallback.'
                                }
                              />
                            )}
                          />
                        </Grid>
                      )}
                      {!form.allowedToAssess && (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            Toggle &quot;Allowed To Assess&quot; on to pick the assessment lists this practitioner can administer.
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </SectionCard>
                )}

                <Box id={SECTION_CLINIC_ASSIGNMENT} sx={{ scrollMarginTop: 88 }} />
                <SectionCard
                  title="Clinic Assignment"
                  description="Tick the clinic brands this practitioner serves (filtered by their target specialties), then add the specific locations under each brand. Each location chip carries three independent status pills — In-clinic event present, Online event present, Chamber room with role-matching event types — so you can see at a glance what's still missing. Click any location to open its drawer and edit clinic assistants, chamber rooms, and events for that location."
                >
                  {disciplineGateBanner()}
                  <Box sx={disciplineMissing ? { opacity: 0.5, pointerEvents: 'none' } : null}>
                  {/* Phase 14al — brand-grouped IA. Top: list of all target-specialty-matching
                      clinic brands as checkboxes. Each checked brand expands to a row of
                      location chips with three independent status pills + a + Add location
                      affordance. Clicking a chip opens the location drawer (drawer renders the
                      same per-assignment detail content the page used to show inline). */}
                  {(() => {
                    if (clinicBrandOptions.length === 0) {
                      return (
                        <Alert severity="info">
                          {Array.isArray(form.targetSpecialitySlugs) && form.targetSpecialitySlugs.length > 0
                            ? `No clinic brands match the target specialties (${form.targetSpecialitySlugs.join(', ')}). Either add the matching specialty on the brand in Control Center or pick a different specialty in Service Approvals & Defaults.`
                            : 'Pick target specialties in Service Approvals & Defaults first — clinic brands are filtered to those matching the practitioner\'s specialties.'}
                        </Alert>
                      )
                    }
                    const assignmentsByBrand = new Map()
                    ;(form.clinicAssignments || []).forEach((a, i) => {
                      const brandId = trimValue(a?.clinicBrandId)
                      if (!brandId) return
                      const arr = assignmentsByBrand.get(brandId) || []
                      arr.push({ assignment: a, index: i })
                      assignmentsByBrand.set(brandId, arr)
                    })
                    const checkedBrandIds = new Set(assignmentsByBrand.keys())
                    return (
                      <Stack spacing={2}>
                        {clinicBrandOptions.map((brand) => {
                          const brandId = brand?.id
                          const checked = checkedBrandIds.has(brandId)
                          const brandRows = assignmentsByBrand.get(brandId) || []
                          // Locations attached for this brand under the current practitioner.
                          // Each location chip is keyed off the underlying assignment index.
                          const attachedLocationIds = brandRows
                            .map((r) => trimValue(r.assignment?.clinicLocationId))
                            .filter(Boolean)
                          // Locations that haven't been added yet — power the "+ Add location" menu.
                          const allBrandLocations = clinicLocationOptions.filter(
                            (loc) => loc?.clinicBrandId === brandId,
                          )
                          const unattachedLocations = allBrandLocations.filter(
                            (loc) => !attachedLocationIds.includes(loc?.id),
                          )
                          return (
                            <Paper
                              key={`brand-${brandId}`}
                              variant="outlined"
                              sx={{ p: 1.5, borderRadius: 2 }}
                            >
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1.5}
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                              >
                                <FormControlLabel
                                  sx={{ minWidth: 240, mr: 0 }}
                                  control={
                                    <Checkbox
                                      checked={checked}
                                      onChange={(e) => {
                                        const nextChecked = e.target.checked
                                        if (nextChecked === checked) return
                                        setForm((prev) => {
                                          const list = (prev.clinicAssignments || []).slice()
                                          if (nextChecked) {
                                            // Auto-attach the sole location when the brand has just one.
                                            const locs = clinicLocationOptions.filter(
                                              (loc) => loc?.clinicBrandId === brandId,
                                            )
                                            const soleLocId = locs.length === 1 ? locs[0].id : ''
                                            const matchingRooms = soleLocId
                                              ? chamberRoomOptions.filter(
                                                  (option) =>
                                                    option?.clinicBrandId === brandId &&
                                                    option?.clinicLocationId === soleLocId,
                                                )
                                              : []
                                            const soleRoomIds =
                                              matchingRooms.length === 1 ? [matchingRooms[0].id] : []
                                            const defaultAssistantIds = soleLocId
                                              ? clinicAssistantOptions
                                                  .filter(
                                                    (option) =>
                                                      option?.clinicBrandId === brandId &&
                                                      option?.clinicLocationId === soleLocId,
                                                  )
                                                  .map((option) => option.id)
                                              : []
                                            list.push({
                                              ...createEmptyAssignment(),
                                              clinicBrandId: brandId,
                                              clinicLocationId: soleLocId,
                                              chamberRoomIds: soleRoomIds,
                                              clinicAssistantAssignmentIds: defaultAssistantIds,
                                            })
                                          } else {
                                            // Drop ALL assignments for this brand. Confirms via window.confirm
                                            // when there's any non-trivial config to lose so the agent
                                            // doesn't clobber it accidentally.
                                            const hasConfig = (prev.clinicAssignments || []).some(
                                              (a) =>
                                                a?.clinicBrandId === brandId &&
                                                ((a?.chamberRoomIds || []).length > 0 ||
                                                  (a?.clinicAssistantAssignmentIds || []).length > 0 ||
                                                  trimValue(a?.clinicLocationId)),
                                            )
                                            if (
                                              hasConfig &&
                                              typeof window !== 'undefined' &&
                                              !window.confirm(
                                                `Removing this brand will clear ${brandRows.length} location attachment${brandRows.length === 1 ? '' : 's'} (rooms + assistants + events) for ${getOptionLabel(brand)}. Continue?`,
                                              )
                                            ) {
                                              return prev
                                            }
                                            return {
                                              ...prev,
                                              clinicAssignments: list.filter(
                                                (a) => a?.clinicBrandId !== brandId,
                                              ),
                                            }
                                          }
                                          return { ...prev, clinicAssignments: list }
                                        })
                                      }}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography sx={{ fontWeight: 600 }}>
                                        {getOptionLabel(brand)}
                                      </Typography>
                                      {brand?.targetSpecialitySlugs?.length > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                          {brand.targetSpecialitySlugs.join(', ')}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                />
                                {checked && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ flex: 1, flexWrap: 'wrap', gap: 1 }}
                                  >
                                    {brandRows.map((row) => {
                                      const locId = trimValue(row.assignment?.clinicLocationId)
                                      const loc = clinicLocationOptions.find((l) => l?.id === locId)
                                      const status = computeLocationStatus(row.assignment)
                                      const labelText = locId
                                        ? getOptionLabel(loc) || 'Unknown location'
                                        : 'No location yet'
                                      const PillIcon = ({ ok }) =>
                                        ok ? (
                                          <CheckCircleOutline
                                            fontSize="inherit"
                                            sx={{ color: '#16a34a' }}
                                          />
                                        ) : (
                                          <RadioButtonUnchecked
                                            fontSize="inherit"
                                            sx={{ color: '#9ca3af' }}
                                          />
                                        )
                                      return (
                                        <Paper
                                          key={`brand-${brandId}-loc-${row.index}`}
                                          variant="outlined"
                                          onClick={() => setActiveAssignmentIndex(row.index)}
                                          sx={{
                                            p: 1,
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            transition: 'background-color 120ms',
                                            '&:hover': { backgroundColor: '#f8fafc' },
                                          }}
                                        >
                                          <Stack direction="row" spacing={1.25} alignItems="center">
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: 600, minWidth: 120 }}
                                            >
                                              {labelText}
                                            </Typography>
                                            <Stack
                                              direction="row"
                                              spacing={0.5}
                                              alignItems="center"
                                              sx={{ fontSize: 14 }}
                                            >
                                              <Tooltip title="In-clinic event present">
                                                <Box
                                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                                                >
                                                  <PillIcon ok={status.hasOfflineEvent} />
                                                  <Typography variant="caption">In-clinic</Typography>
                                                </Box>
                                              </Tooltip>
                                              <Tooltip title="Online (video-call) event present">
                                                <Box
                                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                                                >
                                                  <PillIcon ok={status.hasOnlineEvent} />
                                                  <Typography variant="caption">Online</Typography>
                                                </Box>
                                              </Tooltip>
                                              <Tooltip
                                                title={(() => {
                                                  const fromServer =
                                                    (roleEventTypePolicies || {})[form.practitionerType]
                                                  const allowed =
                                                    Array.isArray(fromServer) && fromServer.length > 0
                                                      ? fromServer
                                                      : ROLE_ALLOWED_EVENT_TYPES[form.practitionerType] || []
                                                  return `Chamber room with eventType matching this role (${allowed.join(', ') || 'none configured'})`
                                                })()}
                                              >
                                                <Box
                                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                                                >
                                                  <PillIcon ok={status.hasMatchingRoom} />
                                                  <Typography variant="caption">Room</Typography>
                                                </Box>
                                              </Tooltip>
                                            </Stack>
                                          </Stack>
                                        </Paper>
                                      )
                                    })}
                                    {unattachedLocations.length > 0 && (
                                      <TextField
                                        select
                                        size="small"
                                        value=""
                                        SelectProps={{ displayEmpty: true }}
                                        sx={{ minWidth: 200 }}
                                        onChange={(e) => {
                                          const nextLocId = e.target.value
                                          if (!nextLocId) return
                                          const matchingRooms = chamberRoomOptions.filter(
                                            (option) =>
                                              option?.clinicBrandId === brandId &&
                                              option?.clinicLocationId === nextLocId,
                                          )
                                          const soleRoomIds =
                                            matchingRooms.length === 1 ? [matchingRooms[0].id] : []
                                          const defaultAssistantIds = clinicAssistantOptions
                                            .filter(
                                              (option) =>
                                                option?.clinicBrandId === brandId &&
                                                option?.clinicLocationId === nextLocId,
                                            )
                                            .map((option) => option.id)
                                          // Reuse the first empty (no-location) entry for this brand
                                          // when present so we don't accumulate stub rows; otherwise
                                          // append a fresh assignment.
                                          setForm((prev) => {
                                            const list = (prev.clinicAssignments || []).slice()
                                            const stubIdx = list.findIndex(
                                              (a) =>
                                                a?.clinicBrandId === brandId &&
                                                !trimValue(a?.clinicLocationId),
                                            )
                                            const nextEntry = {
                                              ...createEmptyAssignment(),
                                              clinicBrandId: brandId,
                                              clinicLocationId: nextLocId,
                                              chamberRoomIds: soleRoomIds,
                                              clinicAssistantAssignmentIds: defaultAssistantIds,
                                            }
                                            if (stubIdx >= 0) {
                                              list[stubIdx] = nextEntry
                                            } else {
                                              list.push(nextEntry)
                                            }
                                            return { ...prev, clinicAssignments: list }
                                          })
                                          // Open the drawer for the just-added location.
                                          // The state update above is async; defer the index lookup.
                                          setTimeout(() => {
                                            setForm((prev) => {
                                              const idx = (prev.clinicAssignments || []).findIndex(
                                                (a) =>
                                                  a?.clinicBrandId === brandId &&
                                                  a?.clinicLocationId === nextLocId,
                                              )
                                              if (idx >= 0) setActiveAssignmentIndex(idx)
                                              return prev
                                            })
                                          }, 0)
                                        }}
                                      >
                                        <MenuItem value="">+ Add location</MenuItem>
                                        {unattachedLocations.map((loc) => (
                                          <MenuItem key={loc.id} value={loc.id}>
                                            {getOptionLabel(loc)}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    )}
                                  </Stack>
                                )}
                              </Stack>
                            </Paper>
                          )
                        })}
                      </Stack>
                    )
                  })()}
                  {/* Phase 14al — drawer renders the same per-assignment detail content the
                      old inline Paper used to show. State is the entry index in
                      form.clinicAssignments; the existing helpers (locationOptions,
                      roomOptions, eventConfigs, etc.) all close over that index unchanged. */}
                  <Drawer
                    anchor="right"
                    open={activeAssignmentIndex !== null}
                    onClose={() => setActiveAssignmentIndex(null)}
                    PaperProps={{
                      sx: { width: { xs: '100%', md: 880 }, maxWidth: '100%' },
                    }}
                  >
                    <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Clinic location configuration
                          </Typography>
                          {(() => {
                            const a =
                              activeAssignmentIndex !== null
                                ? form.clinicAssignments?.[activeAssignmentIndex]
                                : null
                            const brandLabel = a
                              ? getOptionLabel(
                                  clinicBrandOptions.find((b) => b?.id === a.clinicBrandId),
                                )
                              : ''
                            const locLabel = a
                              ? getOptionLabel(
                                  clinicLocationOptions.find((l) => l?.id === a.clinicLocationId),
                                )
                              : ''
                            return (
                              <Typography variant="body2" color="text.secondary">
                                {[brandLabel, locLabel].filter(Boolean).join(' · ') ||
                                  'Pick a brand + location to configure.'}
                              </Typography>
                            )
                          })()}
                        </Box>
                        <IconButton
                          onClick={() => setActiveAssignmentIndex(null)}
                          aria-label="Close clinic location drawer"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 2.5, overflowY: 'auto' }}>
                  <Stack spacing={2}>
                    {(activeAssignmentIndex !== null
                      ? [
                          {
                            assignment: form.clinicAssignments?.[activeAssignmentIndex],
                            index: activeAssignmentIndex,
                          },
                        ].filter((x) => x.assignment)
                      : []
                    ).map(({ assignment, index }) => {
                      const locationOptions = getLocationOptionsForAssignment(assignment)
                      const roomOptions = getRoomOptionsForAssignment(assignment)
                      const assistantOptions = getAssistantOptionsForAssignment(assignment)
                      const assignmentKey = getClinicAssignmentKey(
                        assignment?.clinicBrandId,
                        assignment?.clinicLocationId,
                      )
                      const appointmentTypesForRow = getClinicAppointmentTypesForAssignment(assignment)
                      const allEventConfigs = getClinicEventConfigurationsForAssignment(assignment)
                      const therapyConfigs = allEventConfigs.filter((c) =>
                        /therapy/i.test(trimValue(c?.eventType)),
                      )
                      // Assessment tab strictly = event type "assessment" / "assessments". We
                      // intentionally exclude screening, consultation, and other types so the tab
                      // only shows truly assessment-style appointments.
                      const assessmentConfigs = allEventConfigs.filter((c) => {
                        const t = trimValue(c?.eventType).toLowerCase()
                        return t === 'assessment' || t === 'assessments'
                      })
                      const activeTab = activeEventTabByAssignment[assignmentKey] || 'therapy-session'
                      const tabConfigs = activeTab === 'assessment' ? assessmentConfigs : therapyConfigs
                      const isLoadingAppointmentTypes =
                        trimValue(assignment?.clinicLocationId) &&
                        !Object.prototype.hasOwnProperty.call(clinicAppointmentTypesByKey, assignmentKey)

                      // Phase 14al Slice 2.5 — focus mode inside the location drawer.
                      //   null  → summary cards only (default; agent picks what to edit)
                      //   'rooms' / 'assistants' / 'events' → that section in focus panel
                      //   'all' → all sections side-by-side (escape hatch)
                      // State is per-(brand, location) keyed so switching between locations
                      // remembers where the agent was. Setter accepts null to return to
                      // summary view; the existing inline conditional rendering below treats
                      // null + 'all' as "render summary mode" via the summary-cards block.
                      const focusModeKey = `focus-mode::${assignmentKey}`
                      const activeFocusMode = focusModeByAssignment[focusModeKey] || null
                      const setActiveFocusMode = (next) =>
                        setFocusModeByAssignment((prev) => ({
                          ...prev,
                          [focusModeKey]: next,
                        }))

                      // Three independent status pills (room match / online event / offline
                      // event) reused for the section-tab "completion" indicators below.
                      const focusModeStatus = computeLocationStatus(assignment)
                      const renderFocusPill = (ok) =>
                        ok ? (
                          <CheckCircleOutline fontSize="inherit" sx={{ color: '#16a34a', ml: 0.5 }} />
                        ) : (
                          <RadioButtonUnchecked fontSize="inherit" sx={{ color: '#9ca3af', ml: 0.5 }} />
                        )

                      // Compute "attached assessment list expects an appointment type we don't yet
                      // have at this clinic location" — this is what the pending-clinic-appointment
                      // request flow needs to fill in. We surface it inline in the Assessment tab
                      // so the support agent knows what's coming through.
                      const attachedAssessmentLists = (form.assessmentListIds || [])
                        .map((id) => allAssessmentListOptions.find((o) => o?.id === id)?.preview)
                        .filter(Boolean)
                      const expectedAssessmentIds = new Set()
                      attachedAssessmentLists.forEach((list) => {
                        ;(list?.assessments || []).forEach((a) => {
                          const value = a?.value || a?.id
                          if (value) expectedAssessmentIds.add(value)
                        })
                      })
                      const presentAssessmentIds = new Set()
                      assessmentConfigs.forEach((cfg) => {
                        ;(cfg.clinicalAssessmentIds || []).forEach((id) =>
                          presentAssessmentIds.add(id),
                        )
                      })
                      const missingAssessmentAppointmentIds = Array.from(expectedAssessmentIds).filter(
                        (id) => !presentAssessmentIds.has(id),
                      )

                      return (
                        <Paper key={`assignment-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Stack spacing={2}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Assignment {index + 1}
                              </Typography>
                              {form.clinicAssignments.length > 1 && (
                                <Button
                                  color="error"
                                  startIcon={<DeleteOutline />}
                                  onClick={() =>
                                    setForm((prev) => ({
                                      ...prev,
                                      clinicAssignments: prev.clinicAssignments.filter(
                                        (_item, itemIndex) => itemIndex !== index,
                                      ),
                                    }))
                                  }
                                >
                                  Remove
                                </Button>
                              )}
                            </Box>

                            {/* Phase 14al Slice 2.5 — summary-card navigation. Each card carries
                                count + status pills + an Edit button. Clicking Edit puts that
                                section in focus mode (the active section gets a sticky header
                                + drawer-panel framing below); clicking Back returns to the
                                summary-only view. Same focusMode state as Slice 2; just a
                                different presentation layer. */}
                            {!activeFocusMode || activeFocusMode === 'all' ? (
                              <Grid container spacing={1.5}>
                                {[
                                  {
                                    key: 'rooms',
                                    title: 'Chamber Rooms',
                                    count: Array.isArray(assignment?.chamberRoomIds)
                                      ? assignment.chamberRoomIds.length
                                      : 0,
                                    pills: (
                                      <Tooltip title="Chamber room with eventType matching this role">
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                          {renderFocusPill(focusModeStatus.hasMatchingRoom)}
                                        </Box>
                                      </Tooltip>
                                    ),
                                  },
                                  {
                                    key: 'assistants',
                                    title: 'Clinic Assistants',
                                    count: Array.isArray(assignment?.clinicAssistantAssignmentIds)
                                      ? assignment.clinicAssistantAssignmentIds.length
                                      : 0,
                                    pills: null,
                                  },
                                  {
                                    key: 'events',
                                    title: 'Events',
                                    count: allEventConfigs.length,
                                    pills: (
                                      <Stack
                                        direction="row"
                                        spacing={0.25}
                                        alignItems="center"
                                      >
                                        <Tooltip title="Online (video-call) event present">
                                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            {renderFocusPill(focusModeStatus.hasOnlineEvent)}
                                          </Box>
                                        </Tooltip>
                                        <Tooltip title="In-clinic (offline) event present">
                                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            {renderFocusPill(focusModeStatus.hasOfflineEvent)}
                                          </Box>
                                        </Tooltip>
                                      </Stack>
                                    ),
                                  },
                                ].map((card) => (
                                  <Grid item xs={12} sm={4} key={card.key}>
                                    <Paper
                                      variant="outlined"
                                      onClick={() => setActiveFocusMode(card.key)}
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        transition: 'all 120ms',
                                        '&:hover': {
                                          backgroundColor: '#f8fafc',
                                          borderColor: '#94a3b8',
                                        },
                                      }}
                                    >
                                      <Stack spacing={0.5}>
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          justifyContent="space-between"
                                        >
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: 11 }}>
                                            {card.title}
                                          </Typography>
                                          {card.pills}
                                        </Stack>
                                        <Stack
                                          direction="row"
                                          alignItems="baseline"
                                          justifyContent="space-between"
                                        >
                                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                            {card.count}
                                          </Typography>
                                          <Typography
                                            variant="button"
                                            sx={{
                                              color: 'primary.main',
                                              fontWeight: 600,
                                              fontSize: 12,
                                            }}
                                          >
                                            Edit →
                                          </Typography>
                                        </Stack>
                                      </Stack>
                                    </Paper>
                                  </Grid>
                                ))}
                                {activeFocusMode === 'all' && (
                                  <Grid item xs={12}>
                                    <Button
                                      size="small"
                                      onClick={() => setActiveFocusMode(null)}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Switch back to summary view
                                    </Button>
                                  </Grid>
                                )}
                              </Grid>
                            ) : (
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 0,
                                  borderRadius: 2,
                                  borderColor: '#7c3aed',
                                  boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 8px 24px -8px rgba(124,58,237,0.18)',
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{
                                    p: 1.25,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    background: 'linear-gradient(180deg, rgba(124,58,237,0.04) 0%, transparent 100%)',
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => setActiveFocusMode(null)}
                                    aria-label="Back to summary"
                                  >
                                    <ArrowBack fontSize="small" />
                                  </IconButton>
                                  <Typography sx={{ fontWeight: 700, flex: 1 }}>
                                    {activeFocusMode === 'rooms' && 'Chamber Rooms'}
                                    {activeFocusMode === 'assistants' && 'Clinic Assistants'}
                                    {activeFocusMode === 'events' && 'Events'}
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setActiveFocusMode('all')}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Show all sections
                                  </Button>
                                </Stack>
                              </Paper>
                            )}

                            <Grid container spacing={2}>
                              {clinicBrandOptions.length === 0 && (
                                <Grid item xs={12}>
                                  <Alert severity="info">
                                    {Array.isArray(form.targetSpecialitySlugs) &&
                                    form.targetSpecialitySlugs.length > 0
                                      ? `No clinic brands match the target specialties (${form.targetSpecialitySlugs.join(', ')}). Either add the matching specialty on the brand in Control Center or pick a different specialty in Service Approvals & Defaults.`
                                      : 'Pick target specialties in Service Approvals & Defaults first — clinic brands are filtered to those matching the practitioner\'s specialties.'}
                                  </Alert>
                                </Grid>
                              )}
                              <Grid item xs={12} md={6}>
                                <TextField
                                  select
                                  fullWidth
                                  label="Clinic Brand"
                                  value={assignment.clinicBrandId}
                                  disabled={clinicBrandOptions.length === 0}
                                  onChange={(event) => {
                                    const nextBrandId = event.target.value
                                    // Auto-select sole location for the chosen brand. Chain into
                                    // sole-room auto-select so the agent's clicks scale with the
                                    // size of each clinic — single-location, single-room brands
                                    // become 1-click attachments.
                                    const matchingLocations = clinicLocationOptions.filter(
                                      (option) => option?.clinicBrandId === nextBrandId,
                                    )
                                    const soleLocationId =
                                      matchingLocations.length === 1 ? matchingLocations[0].id : ''
                                    const matchingRooms = soleLocationId
                                      ? chamberRoomOptions.filter(
                                          (option) =>
                                            option?.clinicBrandId === nextBrandId &&
                                            option?.clinicLocationId === soleLocationId,
                                        )
                                      : []
                                    const soleRoomIds =
                                      matchingRooms.length === 1 ? [matchingRooms[0].id] : []
                                    const defaultAssistantIds = soleLocationId
                                      ? clinicAssistantOptions
                                          .filter(
                                            (option) =>
                                              option?.clinicBrandId === nextBrandId &&
                                              option?.clinicLocationId === soleLocationId,
                                          )
                                          .map((option) => option.id)
                                      : []
                                    updateAssignment(index, {
                                      clinicBrandId: nextBrandId,
                                      clinicLocationId: soleLocationId,
                                      chamberRoomIds: soleRoomIds,
                                      clinicAssistantAssignmentIds: defaultAssistantIds,
                                    })
                                  }}
                                  helperText={
                                    clinicBrandOptions.length === 0
                                      ? 'No matching brands.'
                                      : Array.isArray(form.targetSpecialitySlugs) &&
                                          form.targetSpecialitySlugs.length > 0
                                        ? `${clinicBrandOptions.length} brand${clinicBrandOptions.length === 1 ? '' : 's'} match ${form.targetSpecialitySlugs.join(', ')}.`
                                        : undefined
                                  }
                                >
                                  <MenuItem value="">Not selected</MenuItem>
                                  {clinicBrandOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                      {getOptionLabel(option)}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  select
                                  fullWidth
                                  label="Clinic Location"
                                  value={assignment.clinicLocationId}
                                  onChange={(event) => {
                                    const nextLocationId = event.target.value
                                    const nextLocation = clinicLocationOptions.find(
                                      (option) => option.id === nextLocationId,
                                    )
                                    const nextBrandId =
                                      nextLocation?.clinicBrandId || assignment.clinicBrandId
                                    // Auto-select sole chamber room when the picked location only
                                    // has one room — saves a click in single-room clinics.
                                    const matchingRooms = nextLocationId
                                      ? chamberRoomOptions.filter(
                                          (option) =>
                                            (!nextBrandId ||
                                              option?.clinicBrandId === nextBrandId) &&
                                            option?.clinicLocationId === nextLocationId,
                                        )
                                      : []
                                    const soleRoomIds =
                                      matchingRooms.length === 1 ? [matchingRooms[0].id] : []
                                    // Auto-fill clinic assistants matching this brand+location so
                                    // support starts with sensible defaults. The agent can
                                    // de-select any they don't want before save.
                                    const defaultAssistantIds = clinicAssistantOptions
                                      .filter(
                                        (option) =>
                                          (!nextBrandId || option?.clinicBrandId === nextBrandId) &&
                                          (!nextLocationId ||
                                            option?.clinicLocationId === nextLocationId),
                                      )
                                      .map((option) => option.id)
                                    updateAssignment(index, {
                                      clinicBrandId: nextBrandId,
                                      clinicLocationId: nextLocationId,
                                      chamberRoomIds: soleRoomIds,
                                      clinicAssistantAssignmentIds: defaultAssistantIds,
                                    })
                                  }}
                                  helperText={
                                    !assignment.clinicBrandId
                                      ? 'Pick a clinic brand first.'
                                      : locationOptions.length === 1
                                        ? 'Only one location available — auto-selected.'
                                        : `${locationOptions.length} location${locationOptions.length === 1 ? '' : 's'} for this brand.`
                                  }
                                >
                                  <MenuItem value="">Not selected</MenuItem>
                                  {locationOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                      {getOptionLabel(option)}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              {(activeFocusMode === 'rooms' || activeFocusMode === 'all') && (
                              <>
                              <Grid item xs={12}>
                                <Autocomplete
                                  multiple
                                  options={roomOptions}
                                  value={roomOptions.filter((option) =>
                                    assignment.chamberRoomIds.includes(option.id),
                                  )}
                                  onChange={(_event, nextValue) =>
                                    updateAssignment(index, {
                                      chamberRoomIds: nextValue.map((option) => option.id),
                                    })
                                  }
                                  getOptionLabel={(option) =>
                                    option?.subtitle ? `${option.title} (${option.subtitle})` : getOptionLabel(option)
                                  }
                                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Chamber Rooms"
                                      helperText={
                                        roomOptions.length === 0
                                          ? 'Pick a clinic location to see available rooms.'
                                          : roomOptions.length === 1
                                            ? 'Only one room available — auto-selected.'
                                            : 'These room links also drive mentor access combinations in the backend.'
                                      }
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Phase 14k — per-room auditors over THIS practitioner. For each
                                  selected chamber room, show currently-attached auditors as
                                  removable chips and an inline picker to add another. Mirrors
                                  the auditorAssignments flow but in the inverse direction. */}
                              {assignment.chamberRoomIds && assignment.chamberRoomIds.length > 0 && (
                                <Grid item xs={12}>
                                  <Stack spacing={1}>
                                    <Typography variant="caption" color="text.secondary">
                                      Auditors per room (other practitioners who can audit you in
                                      that room):
                                    </Typography>
                                    {assignment.chamberRoomIds.map((roomId) => {
                                      const room =
                                        chamberRoomOptions.find((opt) => opt?.id === roomId) || {
                                          id: roomId,
                                          title: roomId,
                                        }
                                      const roomLabel = room.subtitle
                                        ? `${room.title} (${room.subtitle})`
                                        : room.title
                                      const roomAuditors = (form.incomingAuditors || []).filter(
                                        (entry) => entry.chamberRoomId === roomId,
                                      )
                                      const candidateOptions = (
                                        bootstrapState.data?.practitioners || []
                                      ).filter(
                                        (option) =>
                                          option?.id &&
                                          option.id !== form.practitionerId &&
                                          !roomAuditors.some(
                                            (e) => e.auditorPractitionerId === option.id,
                                          ),
                                      )
                                      return (
                                        <Paper
                                          key={`${index}-room-${roomId}`}
                                          variant="outlined"
                                          sx={{ p: 1.25, borderRadius: 2 }}
                                        >
                                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {roomLabel}
                                          </Typography>
                                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                                            {roomAuditors.length === 0 ? (
                                              <Typography variant="caption" color="text.secondary">
                                                No auditors set for this room.
                                              </Typography>
                                            ) : (
                                              roomAuditors.map((entry) => {
                                                const auditor = (
                                                  bootstrapState.data?.practitioners || []
                                                ).find((p) => p.id === entry.auditorPractitionerId)
                                                const label = auditor
                                                  ? auditor.subtitle
                                                    ? `${auditor.title} (${auditor.subtitle}) · ${entry.type}`
                                                    : `${auditor.title} · ${entry.type}`
                                                  : `${entry.auditorPractitionerId} · ${entry.type}`
                                                return (
                                                  <Chip
                                                    key={`${roomId}-${entry.auditorPractitionerId}`}
                                                    size="small"
                                                    color={entry.type === 'SUPERVISOR' ? 'primary' : 'default'}
                                                    variant={
                                                      entry.type === 'SUPERVISOR' ? 'filled' : 'outlined'
                                                    }
                                                    label={label}
                                                    onDelete={() =>
                                                      setForm((prev) => ({
                                                        ...prev,
                                                        incomingAuditors: (prev.incomingAuditors || []).filter(
                                                          (e) =>
                                                            !(
                                                              e.chamberRoomId === roomId &&
                                                              e.auditorPractitionerId ===
                                                                entry.auditorPractitionerId
                                                            ),
                                                        ),
                                                      }))
                                                    }
                                                  />
                                                )
                                              })
                                            )}
                                          </Stack>
                                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            <Autocomplete
                                              size="small"
                                              fullWidth
                                              options={candidateOptions}
                                              value={null}
                                              clearOnBlur
                                              disablePortal={false}
                                              onChange={(_event, value) => {
                                                if (!value?.id) return
                                                setForm((prev) => ({
                                                  ...prev,
                                                  incomingAuditors: [
                                                    ...(prev.incomingAuditors || []),
                                                    {
                                                      chamberRoomId: roomId,
                                                      auditorPractitionerId: value.id,
                                                      type: 'SUPERVISOR',
                                                    },
                                                  ],
                                                }))
                                              }}
                                              getOptionLabel={(option) =>
                                                option?.subtitle
                                                  ? `${option.title} (${option.subtitle})`
                                                  : getOptionLabel(option)
                                              }
                                              isOptionEqualToValue={(option, value) =>
                                                option?.id === value?.id
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  label="Add auditor for this room"
                                                  placeholder="Pick a practitioner…"
                                                />
                                              )}
                                            />
                                          </Stack>
                                        </Paper>
                                      )
                                    })}
                                  </Stack>
                                </Grid>
                              )}
                              </>
                              )}{/* /rooms focus mode */}

                              {(activeFocusMode === 'assistants' || activeFocusMode === 'all') && (
                              <Grid item xs={12}>
                                <Autocomplete
                                  multiple
                                  options={assistantOptions}
                                  value={assistantOptions.filter((option) =>
                                    assignment.clinicAssistantAssignmentIds.includes(option.id),
                                  )}
                                  onChange={(_event, nextValue) =>
                                    updateAssignment(index, {
                                      clinicAssistantAssignmentIds: nextValue.map((option) => option.id),
                                    })
                                  }
                                  getOptionLabel={(option) =>
                                    option?.subtitle ? `${option.title} (${option.subtitle})` : getOptionLabel(option)
                                  }
                                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Clinic Assistants"
                                      helperText="Auto-selected from the assistants assigned to this brand + location. De-select any you don't want this practitioner linked to."
                                    />
                                  )}
                                />
                              </Grid>
                              )}{/* /assistants focus mode */}
                            </Grid>

                            {/* Per-assignment event configuration with Therapy Session / Assessment
                                tabs. Therapy Session lists clinic appointment types whose eventType
                                contains "therapy"; Assessment lists everything else (screening,
                                evaluation, assessment, etc.). Both share the same checkbox + price +
                                duration UX so support can curate which appointment types should
                                seed practitioner events at this location. */}
                            {(activeFocusMode === 'events' || activeFocusMode === 'all') &&
                              trimValue(assignment?.clinicLocationId) && (
                              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                                <Tabs
                                  value={activeTab}
                                  onChange={(_event, nextTab) =>
                                    setActiveEventTabByAssignment((prev) => ({
                                      ...prev,
                                      [assignmentKey]: nextTab,
                                    }))
                                  }
                                  variant="fullWidth"
                                  sx={{ mb: 2 }}
                                >
                                  <Tab
                                    value="therapy-session"
                                    label={`${
                                      // Phase 14g — title mirrors the selected therapy discipline
                                      // ("Nutrition Consultation" for nutrition, "Therapy Session"
                                      // for psychotherapy / fallback). therapyDisciplineLabel
                                      // returns the display label or the raw code.
                                      form.practitionerType === 'therapist' && form.approvedTherapyDisciplines?.[0]
                                        ? therapyDisciplineLabel(form.approvedTherapyDisciplines[0])
                                        : 'Therapy Session'
                                    } (${therapyConfigs.length})`}
                                  />
                                  <Tab
                                    value="assessment"
                                    label={`Assessment (${assessmentConfigs.length})`}
                                  />
                                </Tabs>

                                {/* Phase 14l — explicit callout that the rows below become real
                                    bookable events on the mentor when the form saves. Surface this
                                    plus an Add-event affordance per tab so agents can hand-build a
                                    custom event when no matching clinic appointment type exists. */}
                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  spacing={1.5}
                                  alignItems={{ sm: 'center' }}
                                  justifyContent="space-between"
                                  sx={{ mb: 2 }}
                                >
                                  <Alert severity="info" icon={false} sx={{ flex: 1, py: 0.5 }}>
                                    Mentor&apos;s events will be created when you save. Each row here
                                    becomes a bookable event for this practitioner — toggle the
                                    seed switch to drop one, or use Edit all fields to fine-tune
                                    title / price / duration.
                                  </Alert>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddCircleOutline />}
                                    onClick={() =>
                                      addCustomClinicEvent(
                                        assignment,
                                        activeTab === 'assessment'
                                          ? 'assessment'
                                          : EVENT_TYPE_THERAPY_SESSION,
                                      )
                                    }
                                  >
                                    Add custom event
                                  </Button>
                                </Stack>

                                {/* Pending-request hint: in the Assessment tab, when the
                                    practitioner has attached an assessment list whose assessments
                                    aren't backed by a clinic appointment type at this location,
                                    surface that gap so the agent knows the support team needs to
                                    queue a pending request (auto-activates when the clinic later
                                    creates the matching appointment type). */}
                                {activeTab === 'assessment' &&
                                  missingAssessmentAppointmentIds.length > 0 && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                      {missingAssessmentAppointmentIds.length} assessment
                                      {missingAssessmentAppointmentIds.length === 1 ? ' is' : 's are'}{' '}
                                      attached to this practitioner but not yet offered as a clinic
                                      appointment at this location. A pending request will be queued
                                      against this clinic; the assessment will auto-activate when the
                                      clinic creates the matching appointment type.
                                    </Alert>
                                  )}

                                {isLoadingAppointmentTypes ? (
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={18} />
                                    <Typography variant="body2" color="text.secondary">
                                      Loading clinic appointment types...
                                    </Typography>
                                  </Stack>
                                ) : appointmentTypesForRow.length === 0 ? (
                                  <Alert severity="warning">
                                    No clinic appointment types are configured at this location yet. Set up clinic services first, or keep the practitioner attached without seeding events from this clinic.
                                  </Alert>
                                ) : tabConfigs.length === 0 ? (
                                  <Alert severity="info">
                                    {activeTab === 'assessment'
                                      ? 'No assessment-style appointment types at this location.'
                                      : 'No therapy-session appointment types at this location.'}
                                  </Alert>
                                ) : (
                                  <Stack spacing={2}>
                                    {tabConfigs.map((configuration) => (
                                      <Paper key={configuration.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Grid container spacing={2}>
                                          <Grid item xs={12} md={3}>
                                            <FormControlLabel
                                              control={
                                                <Switch
                                                  checked={Boolean(configuration.enabled)}
                                                  onChange={(event) =>
                                                    updateClinicEventConfiguration(configuration.id, {
                                                      enabled: event.target.checked,
                                                    })
                                                  }
                                                />
                                              }
                                              label="Seed event"
                                            />
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              useFlexGap
                                              flexWrap="wrap"
                                              sx={{ mt: 1 }}
                                            >
                                              <Chip
                                                size="small"
                                                label={trimValue(configuration.eventType || 'event')}
                                                variant="outlined"
                                              />
                                              <Chip
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                label={
                                                  trimValue(configuration.mode).toLowerCase() === 'online'
                                                    ? 'Video call'
                                                    : 'In-clinic'
                                                }
                                              />
                                              <Chip
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                label="Clinic appointment"
                                              />
                                              {/* Phase 14p — surface the target speciality on every
                                                  row, even when unset, so the agent can spot
                                                  events still tagged with the wrong speciality
                                                  (or none at all) without having to open Edit
                                                  all fields. The selectable dropdown lives in a
                                                  full-width row below; this chip is the at-a-
                                                  glance indicator. */}
                                              <Chip
                                                size="small"
                                                color={
                                                  trimValue(configuration.targetSpecialtySlug)
                                                    ? 'info'
                                                    : 'warning'
                                                }
                                                label={
                                                  trimValue(configuration.targetSpecialtySlug)
                                                    ? `Speciality: ${configuration.targetSpecialtySlug}`
                                                    : 'Speciality: not set'
                                                }
                                                variant="outlined"
                                              />
                                              {configuration.isAssessmentEvent &&
                                                Array.isArray(configuration.clinicalAssessmentIds) &&
                                                configuration.clinicalAssessmentIds.some((id) =>
                                                  (form.assessmentListIds || []).includes(id),
                                                ) && (
                                                  <Chip
                                                    size="small"
                                                    color="success"
                                                    label="In attached list"
                                                  />
                                                )}
                                            </Stack>
                                          </Grid>
                                          <Grid item xs={12} md={3}>
                                            <TextField
                                              fullWidth
                                              label="Event Title"
                                              value={configuration.title}
                                              onChange={(event) =>
                                                updateClinicEventConfiguration(configuration.id, {
                                                  title: event.target.value,
                                                })
                                              }
                                            />
                                          </Grid>
                                          <Grid item xs={12} md={2}>
                                            <TextField
                                              fullWidth
                                              label="Duration (mins)"
                                              type="number"
                                              value={configuration.durationInMins}
                                              onChange={(event) =>
                                                updateClinicEventConfiguration(configuration.id, {
                                                  durationInMins: event.target.value,
                                                })
                                              }
                                            />
                                          </Grid>
                                          <Grid item xs={12} md={2}>
                                            <TextField
                                              fullWidth
                                              label="Currency"
                                              value={configuration.currencyCode}
                                              onChange={(event) =>
                                                updateClinicEventConfiguration(configuration.id, {
                                                  currencyCode: event.target.value,
                                                })
                                              }
                                            />
                                          </Grid>
                                          <Grid item xs={12} md={2}>
                                            <TextField
                                              fullWidth
                                              label="Price"
                                              type="number"
                                              value={configuration.price}
                                              onChange={(event) =>
                                                updateClinicEventConfiguration(configuration.id, {
                                                  price: event.target.value,
                                                })
                                              }
                                            />
                                          </Grid>
                                          {/* Phase 14p — target speciality picker on the inline
                                              row. We pre-fill from form.targetSpecialitySlugs
                                              when a single one is selected (see the auto-fill
                                              effect above); when the practitioner has multiple
                                              specialities the agent picks per-row from this
                                              dropdown. With zero specialities on the parent we
                                              fall back to a free-text field so legacy slugs
                                              already on the row stay editable. */}
                                          {(() => {
                                            const practitionerSpecs = normalizeStringArray(
                                              form.targetSpecialitySlugs,
                                            )
                                            const currentSlug = trimValue(
                                              configuration.targetSpecialtySlug,
                                            )
                                            const onChangeSlug = (nextSlug) =>
                                              updateClinicEventConfiguration(configuration.id, {
                                                targetSpecialtySlug: nextSlug,
                                              })
                                            // 0 specs on parent → free text, scoped to this row.
                                            if (practitionerSpecs.length === 0) {
                                              return (
                                                <Grid item xs={12} md={6}>
                                                  <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Target speciality slug"
                                                    value={currentSlug}
                                                    onChange={(e) => onChangeSlug(e.target.value)}
                                                    helperText="Add a speciality on the practitioner's main page to pick from a dropdown."
                                                  />
                                                </Grid>
                                              )
                                            }
                                            // 1 spec → read-only, single chip; auto-fill effect
                                            // already aligned the row's slug with the practitioner.
                                            if (practitionerSpecs.length === 1) {
                                              return (
                                                <Grid item xs={12} md={6}>
                                                  <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Target speciality"
                                                    value={practitionerSpecs[0]}
                                                    disabled
                                                    helperText="Inherited from the practitioner's only target speciality."
                                                  />
                                                </Grid>
                                              )
                                            }
                                            // Multiple specs → dropdown with each option +
                                            // an explicit "(none)" so the agent can leave a row
                                            // unscoped if needed.
                                            return (
                                              <Grid item xs={12} md={6}>
                                                <TextField
                                                  select
                                                  fullWidth
                                                  size="small"
                                                  label="Target speciality"
                                                  value={
                                                    practitionerSpecs.includes(currentSlug)
                                                      ? currentSlug
                                                      : ''
                                                  }
                                                  onChange={(e) => onChangeSlug(e.target.value)}
                                                  helperText="Pick which of this practitioner's specialities this event is scoped to."
                                                >
                                                  <MenuItem value="">
                                                    <em>(none — leave unscoped)</em>
                                                  </MenuItem>
                                                  {practitionerSpecs.map((slug) => (
                                                    <MenuItem key={slug} value={slug}>
                                                      {slug}
                                                    </MenuItem>
                                                  ))}
                                                </TextField>
                                              </Grid>
                                            )
                                          })()}
                                          {/* Phase 14l — Edit all fields opens the same row in
                                              a drawer with the long tail (event type, mode,
                                              target speciality, etc.). */}
                                          <Grid item xs={12} md={6}>
                                            <Stack
                                              direction="row"
                                              justifyContent="flex-end"
                                              alignItems="center"
                                              sx={{ height: '100%' }}
                                            >
                                              <Button
                                                size="small"
                                                onClick={() =>
                                                  setEventEditDrawerConfigId(configuration.id)
                                                }
                                              >
                                                Edit all fields
                                              </Button>
                                            </Stack>
                                          </Grid>
                                        </Grid>
                                      </Paper>
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            )}
                          </Stack>
                        </Paper>
                      )
                    })}
                  </Stack>
                    </Box>
                  </Drawer>
                  </Box>
                </SectionCard>

                <Box id={SECTION_ENGAGEMENT} sx={{ scrollMarginTop: 88 }} />
                <SectionCard
                  title="Engagement And Reporting"
                  description="Save the engagement model, report defaults, and practitioner portal visibility from the same flow."
                >
                  <Grid container spacing={2}>
                    {ENGAGEMENT_APPLICABLE_TO_OPTIONS.map((applicableToOption) => {
                      const selectedFeeModelId = trimValue(
                        form.engagementFeeModelIdsByApplicableTo?.[applicableToOption.id],
                      )
                      const selectedFeeModelValue = getFeeModelValue(selectedFeeModelId)
                      return (
                        <Grid item xs={12} key={applicableToOption.id}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack spacing={1.5}>
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                spacing={1}
                              >
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {applicableToOption.title}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {selectedFeeModelId ? 'Fee model selected.' : 'No fee model selected.'}
                                  </Typography>
                                </Box>
                                {selectedFeeModelId && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setEngagementFeeModelSelection(applicableToOption.id, '')}
                                  >
                                    Clear
                                  </Button>
                                )}
                              </Stack>
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="flex-start">
                                <Box sx={{ flex: 1, width: '100%' }}>
                                  <Autocomplete
                                    options={feeModelOptions}
                                    value={selectedFeeModelValue}
                                    onChange={(_event, nextValue) =>
                                      setEngagementFeeModelSelection(applicableToOption.id, nextValue?.id || '')
                                    }
                                    getOptionLabel={(option) =>
                                      option?.subtitle
                                        ? `${option.title} (${option.subtitle})`
                                        : getOptionLabel(option)
                                    }
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Fee Model"
                                        helperText={
                                          feeModelGroups.length > 0
                                            ? `Available fee model groups: ${feeModelGroups
                                                .map((group) => group.title)
                                                .slice(0, 4)
                                                .join(', ')}`
                                            : 'Select a fee model or leave this blank.'
                                        }
                                      />
                                    )}
                                  />
                                </Box>
                                <Button
                                  variant="outlined"
                                  disabled={!selectedFeeModelValue?.preview}
                                  onClick={() => setFeeModelPreviewOption(selectedFeeModelValue)}
                                  sx={{ mt: 1 }}
                                >
                                  Preview
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        </Grid>
                      )
                    })}
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Practitioner Portal Visibility
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            These controls match the practitioner-facing business model, payout report, and patient-contact visibility settings.
                          </Typography>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(form.showFeeModelsOnMentorPortal)}
                                  onChange={(event) =>
                                    setField('showFeeModelsOnMentorPortal', event.target.checked)
                                  }
                                />
                              }
                              label="Show business / engagement model"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(form.showPayoutReportsOnMentorPortal)}
                                  onChange={(event) =>
                                    setField('showPayoutReportsOnMentorPortal', event.target.checked)
                                  }
                                />
                              }
                              label="Show payout reports"
                            />
                            {canManagePatientContactVisibility && (
                              <FormControlLabel
                                sx={{ alignItems: 'flex-start', maxWidth: 420 }}
                                control={
                                  <Switch
                                    checked={showPatientContactDetailsOnMentorPortal}
                                    onChange={handlePatientContactVisibilityChange}
                                    disabled={
                                      !hasPatientContactVisibilityReference ||
                                      loadingPatientContactVisibility ||
                                      savingPatientContactVisibility
                                    }
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                                      Show patient contact details
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      When off, patient email and mobile are not sent to this practitioner portal.
                                    </Typography>
                                  </Box>
                                }
                              />
                            )}
                            {supplementaryLoading && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  Loading current portal settings...
                                </Typography>
                              </Stack>
                            )}
                            {loadingPatientContactVisibility && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  Loading patient contact visibility...
                                </Typography>
                              </Stack>
                            )}
                            {savingPatientContactVisibility && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  Saving patient contact visibility...
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                          {canManagePatientContactVisibility && !hasPatientContactVisibilityReference && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              Save the practitioner first to unlock patient contact visibility.
                            </Alert>
                          )}
                          {patientContactVisibilityError && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              {patientContactVisibilityError}
                            </Alert>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Report View Type and Preferred Report Languages were dropped from the
                        form: the server defaults them to "minimal" and ["en"] in
                        SupportPractitionerOnboardingService.saveReportSettings, and existing
                        practitioner records preserve whatever values they already have. */}

                    {/* Appointment Calendar Settings — pre-collapsed "Extra settings" accordion.
                        The defaults (showPatientAgeAndGender, showPatientVisits, showWaitingTime
                        on; showAppointmentNumberHorizontally off) live in
                        createDefaultAppointmentCalendarSettings(); support only opens this when
                        they actually want to override one of them. */}
                    <Grid item xs={12}>
                      <Accordion variant="outlined" disableGutters sx={{ borderRadius: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">
                            Extra settings — Appointment Calendar (defaults applied)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            {APPOINTMENT_CALENDAR_FIELDS.map((field) => (
                              <FormControlLabel
                                key={field.id}
                                control={
                                  <Switch
                                    checked={Boolean(
                                      form.appointmentCalendarSettings?.[field.id],
                                    )}
                                    onChange={(event) =>
                                      setField('appointmentCalendarSettings', {
                                        ...form.appointmentCalendarSettings,
                                        [field.id]: event.target.checked,
                                      })
                                    }
                                  />
                                }
                                label={field.label}
                              />
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>

                  </Grid>
                </SectionCard>

                {/* Payment Configuration — Phase 14k moved the editor into a drawer so the
                    long form (UPI, bank, GST/PAN) doesn't fight the rest of the page for
                    vertical space. The main card just summarises configured-or-not status and
                    opens the drawer on click. */}
                <Box id={SECTION_PAYMENT} sx={{ scrollMarginTop: 88 }} />
                <SectionCard
                  title="Payment Configuration"
                  description="Configure payouts (UPI, bank, GST/PAN) in the side drawer. Available once the practitioner is saved."
                  action={
                    trimValue(form.practitionerId) ? (
                      <Button
                        variant="outlined"
                        onClick={() => setPaymentDrawerOpen(true)}
                      >
                        Edit Payment Configuration
                      </Button>
                    ) : null
                  }
                >
                  {trimValue(form.practitionerId) ? (
                    <Alert severity="info" icon={false}>
                      Click <strong>Edit Payment Configuration</strong> to open the side drawer
                      and review payouts (UPI, bank, GST/PAN). Saved separately from the main
                      onboarding form.
                    </Alert>
                  ) : (
                    <Alert severity="info">
                      Save the practitioner first — payout configuration unlocks once a practitioner record exists.
                    </Alert>
                  )}
                </SectionCard>
                </>
                )}

                {/* Phase 14k — Auditor Access editor moved into a side drawer. The main card
                    now shows a read-only summary of currently-configured assignments so the
                    agent can spot gaps without opening the editor. */}
                <Box id="auditor-access-section" />
                <SectionCard
                  title="Auditor Access"
                  description="Audit relationships for this practitioner — both directions. Edit in the side drawer; this card surfaces a read-only summary of what's currently configured."
                  action={
                    <Button
                      variant="outlined"
                      onClick={() => setAuditorDrawerOpen(true)}
                    >
                      Edit Auditor Access
                    </Button>
                  }
                >
                  {(() => {
                    const practitionerOptions = bootstrapState.data?.practitioners || []
                    const resolvePractitioner = (id) =>
                      practitionerOptions.find((p) => p.id === id) || null
                    const resolveRoom = (id) =>
                      chamberRoomOptions.find((opt) => opt?.id === id) || null
                    const labelFor = (entity) =>
                      entity?.subtitle ? `${entity.title} (${entity.subtitle})` : entity?.title || ''

                    const outgoing = (form.auditorAssignments || []).filter(
                      (a) => trimValue(a?.mentorId),
                    )
                    const incoming = form.incomingAuditors || []
                    const hasAny = outgoing.length > 0 || incoming.length > 0

                    if (!hasAny) {
                      return (
                        <Alert severity="info">
                          No audit relationships configured. Use <strong>Edit Auditor Access</strong>
                          {' '}to attach this practitioner as an auditor over others; per-room incoming
                          auditors live inside Clinic Assignment.
                        </Alert>
                      )
                    }
                    return (
                      <Stack spacing={1.5}>
                        {outgoing.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              This practitioner audits:
                            </Typography>
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {outgoing.map((a, i) => {
                                const mentor = resolvePractitioner(a.mentorId)
                                const rooms = (a.chamberRoomIds || []).map(resolveRoom).filter(Boolean)
                                const scope = rooms.length
                                  ? rooms.map((r) => r.title).join(', ')
                                  : 'all rooms'
                                return (
                                  <Chip
                                    key={`out-${i}`}
                                    size="small"
                                    color={a.type === 'SUPERVISOR' ? 'primary' : 'default'}
                                    variant={a.type === 'SUPERVISOR' ? 'filled' : 'outlined'}
                                    label={`${mentor ? labelFor(mentor) : a.mentorId} · ${a.type} · ${scope}`}
                                  />
                                )
                              })}
                            </Stack>
                          </Box>
                        )}
                        {incoming.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Audited by (per room):
                            </Typography>
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {incoming.map((entry, i) => {
                                const auditor = resolvePractitioner(entry.auditorPractitionerId)
                                const room = resolveRoom(entry.chamberRoomId)
                                return (
                                  <Chip
                                    key={`in-${i}`}
                                    size="small"
                                    variant="outlined"
                                    label={`${auditor ? labelFor(auditor) : entry.auditorPractitionerId} · ${entry.type} · ${room ? labelFor(room) : entry.chamberRoomId}`}
                                  />
                                )
                              })}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    )
                  })()}
                </SectionCard>

                {!isAuditorDedicatedView && (
                <Box id={SECTION_SAVE} sx={{ scrollMarginTop: 88 }} />
                )}
                {!isAuditorDedicatedView && (
                <SectionCard
                  title="Save And Review"
                  description="Use remarks to capture onboarding context, then save to create or update the practitioner and refresh the readiness checklist."
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Remarks"
                        value={form.remarks}
                        onChange={(event) => setField('remarks', event.target.value)}
                        helperText="This is stored in the service-approval context for operational traceability."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button
                          variant="contained"
                          startIcon={
                            savingMode === 'active' ? <CircularProgress size={16} color="inherit" /> : <Save />
                          }
                          onClick={() => handleSave('active')}
                          disabled={Boolean(savingMode)}
                        >
                          {savingMode === 'active' ? 'Saving...' : getPrimarySaveButtonLabel()}
                        </Button>
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={
                            savingMode === 'disabled' ? <CircularProgress size={16} color="inherit" /> : <Save />
                          }
                          onClick={() => handleSave('disabled')}
                          disabled={Boolean(savingMode)}
                        >
                          {savingMode === 'disabled' ? 'Saving...' : getSecondarySaveButtonLabel()}
                        </Button>
                        {/* Phase 14g — re-enable affordance after Save & Keep Disabled. Visible
                            only when the practitioner is currently disabled (per the loaded
                            detail) so we don't clutter the toolbar in the normal active path. */}
                        {detailState.data?.user?.enabled === false && (
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={
                              savingMode === 'enabling' ? <CircularProgress size={16} color="inherit" /> : <Save />
                            }
                            disabled={Boolean(savingMode) || !selectedPractitioner}
                            onClick={async () => {
                              if (!selectedPractitioner) return
                              setSavingMode('enabling')
                              const resp = await updateSupportPractitionerEnabled(
                                {
                                  practitionerId: selectedPractitioner.id,
                                  practitionerSlug: selectedPractitioner.slug,
                                },
                                true,
                              )
                              setSavingMode('')
                              if (resp.ok) {
                                toast.success(`${practitionerTypeLabel} re-enabled.`)
                                loadPractitionerDetail(selectedPractitioner)
                              } else {
                                toast.error(resp.message || `Could not re-enable ${practitionerTypeLabel}.`)
                              }
                            }}
                          >
                            {savingMode === 'enabling' ? 'Enabling…' : `Enable ${practitionerTypeLabel}`}
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          startIcon={<Refresh />}
                          onClick={() => selectedPractitioner && loadPractitionerDetail(selectedPractitioner)}
                          disabled={!selectedPractitioner || detailState.loading}
                        >
                          Refresh Backend Status
                        </Button>
                        {/* Phase 14g — visual confirmation that the draft auto-save is alive.
                            Surfaces the timestamp of the last localStorage write so the agent
                            knows their work is safe to lose-and-recover. */}
                        {lastDraftSavedAt && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="default"
                            label={`Draft saved · ${new Date(lastDraftSavedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`}
                            sx={{ alignSelf: { sm: 'center' } }}
                          />
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </SectionCard>
                )}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Existing-users click-through dialog: opens from the count link next to email/mobile in
          Basic Profile. Shows up to 5 sample matches so the agent can decide whether to claim an
          existing record or create a new one. */}
      <Dialog
        open={showExistenceModal != null}
        onClose={() => setShowExistenceModal(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {showExistenceModal === 'email'
            ? 'Existing users with this email'
            : 'Existing users with this mobile'}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const channel = showExistenceModal
            if (!channel) return null
            const data = existenceCheck[channel] || {}
            const samples = data.samples || []
            if (samples.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  No sample records returned.
                </Typography>
              )
            }
            return (
              <Stack spacing={1.5}>
                {samples.map((sample) => (
                  <Paper key={sample.userId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {sample.name || sample.userId}
                          {sample.enabled === false && (
                            <Chip
                              size="small"
                              label="Disabled"
                              color="warning"
                              sx={{ ml: 1, height: 20 }}
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {sample.email || sample.mobile} · {sample.userId}
                        </Typography>
                        {Array.isArray(sample.authorities) && sample.authorities.length > 0 && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                            {sample.authorities.slice(0, 4).map((role) => (
                              <Chip key={role} label={role} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExistenceModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Engagement-model preview drawer (Phase 7c): opens from the "Preview" button next to the
          fee-model picker. Replicates the Control-Center practitioner-models display (Sessions /
          Therapist Share / Comment grid) and supports an inline editor that posts to
          /api/secure/cc/support/practitioner-onboarding/fee-model/save. */}
      <FeeModelPreviewDrawer
        open={Boolean(feeModelPreviewOption?.preview)}
        feeModelOption={feeModelPreviewOption}
        onClose={() => setFeeModelPreviewOption(null)}
        onSaved={(saved) => {
          // Patch the bootstrap option's preview with the saved fee model so the next preview
          // shows the latest values without a full bootstrap reload.
          if (!saved) return
          setBootstrapState((prev) => {
            if (!prev?.data?.feeModels) return prev
            return {
              ...prev,
              data: {
                ...prev.data,
                feeModels: prev.data.feeModels.map((opt) =>
                  opt?.id === saved.id ? { ...opt, preview: saved, title: saved.title || opt.title } : opt,
                ),
              },
            }
          })
        }}
      />

      {/* Assessment-list preview dialog: opens when the agent clicks an assessment-list chip in
          the Assessment Settings picker. Renders the list's description + nested assessments
          via the embedded `preview` payload from the bootstrap option. */}
      <Dialog
        open={Boolean(assessmentPreviewId)}
        onClose={() => setAssessmentPreviewId(null)}
        fullWidth
        maxWidth="sm"
      >
        {(() => {
          const option = allAssessmentListOptions.find((o) => o?.id === assessmentPreviewId)
          const preview = option?.preview || null
          return (
            <>
              <DialogTitle>{option?.title || assessmentPreviewId || 'Assessment list'}</DialogTitle>
              <DialogContent>
                {!preview ? (
                  <Typography variant="body2" color="text.secondary">
                    No preview payload available for this list.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {preview.description && (
                      <Typography variant="body2" color="text.secondary">
                        {preview.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {(preview.forDisciplines || []).map((d) => (
                        <Chip key={`d-${d}`} size="small" label={`Discipline: ${d}`} variant="outlined" />
                      ))}
                      {(preview.forTargetSpecialitySlugs || []).map((s) => (
                        <Chip key={`s-${s}`} size="small" label={`Specialty: ${s}`} variant="outlined" />
                      ))}
                      {(preview.specializations || []).map((sp) => (
                        <Chip key={`sp-${sp}`} size="small" label={sp} variant="outlined" />
                      ))}
                    </Stack>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Assessments ({(preview.assessments || []).length})
                      </Typography>
                      {(preview.assessments || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No assessments configured on this list yet.
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          {(preview.assessments || []).map((a, i) => (
                            <Typography key={`a-${i}`} variant="body2">
                              • {a?.value || a?.id || a?.type || JSON.stringify(a)}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </Stack>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAssessmentPreviewId(null)}>Close</Button>
              </DialogActions>
            </>
          )
        })()}
      </Dialog>

      {/* Phase 14l — event edit drawer. Same clinic-event row that's editable inline, surfaced
          in a focused side panel so the agent can fill in the long-tail fields (event type,
          mode, target speciality, currency, currency-scoped pricing) without the inline grid
          getting unwieldy. Edits persist to the same form state — Save And Review at the
          bottom of the page commits everything together. */}
      {(() => {
        const configForDrawer = (form.clinicEventConfigurations || []).find(
          (c) => c.id === eventEditDrawerConfigId,
        )
        const close = () => setEventEditDrawerConfigId(null)
        const update = (changes) =>
          updateClinicEventConfiguration(eventEditDrawerConfigId, changes)
        return (
          <Drawer
            anchor="right"
            open={Boolean(configForDrawer)}
            onClose={close}
            PaperProps={{ sx: { width: { xs: '100%', md: 600 }, maxWidth: '100%' } }}
          >
            {configForDrawer && (
              <>
                <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Edit event
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Saved with the rest of the onboarding form. Toggle Seed event to drop
                        this without removing it.
                      </Typography>
                    </Box>
                    <IconButton onClick={close} aria-label="close">
                      <span style={{ fontSize: 24, lineHeight: 1 }}>×</span>
                    </IconButton>
                  </Stack>
                </Box>
                <Box sx={{ p: 2.5, overflowY: 'auto' }}>
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(configForDrawer.enabled)}
                          onChange={(e) => update({ enabled: e.target.checked })}
                        />
                      }
                      label="Seed this event for the mentor on save"
                    />
                    <TextField
                      fullWidth
                      label="Event title"
                      value={configForDrawer.title || ''}
                      onChange={(e) => update({ title: e.target.value })}
                      helperText="Visible to patients on the booking page."
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        select
                        label="Event type"
                        value={configForDrawer.eventType || ''}
                        onChange={(e) => update({ eventType: e.target.value })}
                      >
                        {[
                          EVENT_TYPE_THERAPY_SESSION,
                          EVENT_TYPE_CONSULTATION,
                          EVENT_TYPE_SCREENING,
                          'assessment',
                        ].map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        select
                        label="Mode"
                        value={configForDrawer.mode || 'offline'}
                        onChange={(e) => update({ mode: e.target.value })}
                      >
                        <MenuItem value="offline">In-clinic (offline)</MenuItem>
                        <MenuItem value="online">Video call (online)</MenuItem>
                      </TextField>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Duration (mins)"
                        value={configForDrawer.durationInMins ?? ''}
                        onChange={(e) =>
                          update({ durationInMins: e.target.value === '' ? null : Number(e.target.value) })
                        }
                      />
                      <TextField
                        fullWidth
                        label="Currency"
                        value={configForDrawer.currencyCode || ''}
                        onChange={(e) => update({ currencyCode: e.target.value })}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Price"
                        value={configForDrawer.price ?? ''}
                        onChange={(e) =>
                          update({ price: e.target.value === '' ? null : Number(e.target.value) })
                        }
                      />
                    </Stack>
                    {/* Phase 14p — match the inline row's target-speciality picker so the
                        drawer and the table show the same options. With one parent speciality
                        the field is read-only and auto-inherited; with multiple, the agent
                        picks from a dropdown; with none, it falls back to free text. */}
                    {(() => {
                      const practitionerSpecs = normalizeStringArray(form.targetSpecialitySlugs)
                      const currentSlug = trimValue(configForDrawer.targetSpecialtySlug)
                      if (practitionerSpecs.length === 0) {
                        return (
                          <TextField
                            fullWidth
                            label="Target speciality slug"
                            value={configForDrawer.targetSpecialtySlug || ''}
                            onChange={(e) => update({ targetSpecialtySlug: e.target.value })}
                            helperText="No speciality on the practitioner's main page — type one here, or set it on the parent form to get a dropdown."
                          />
                        )
                      }
                      if (practitionerSpecs.length === 1) {
                        return (
                          <TextField
                            fullWidth
                            label="Target speciality"
                            value={practitionerSpecs[0]}
                            disabled
                            helperText="Inherited from the practitioner's only target speciality."
                          />
                        )
                      }
                      return (
                        <TextField
                          select
                          fullWidth
                          label="Target speciality"
                          value={
                            practitionerSpecs.includes(currentSlug) ? currentSlug : ''
                          }
                          onChange={(e) => update({ targetSpecialtySlug: e.target.value })}
                          helperText="Pick which of this practitioner's specialities this event is scoped to."
                        >
                          <MenuItem value="">
                            <em>(none — leave unscoped)</em>
                          </MenuItem>
                          {practitionerSpecs.map((slug) => (
                            <MenuItem key={slug} value={slug}>
                              {slug}
                            </MenuItem>
                          ))}
                        </TextField>
                      )
                    })()}
                    <TextField
                      fullWidth
                      label="Clinic appointment type id (read-only when seeded)"
                      value={configForDrawer.clinicAppointmentTypeId || ''}
                      onChange={(e) => update({ clinicAppointmentTypeId: e.target.value })}
                      helperText="Set automatically when seeded from a clinic appointment type. Editable for hand-built rows."
                      disabled={Boolean(configForDrawer.clinicAppointmentTypeId)}
                    />
                    <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                      <Button
                        color="error"
                        startIcon={<DeleteOutline />}
                        onClick={() => {
                          setForm((prev) => ({
                            ...prev,
                            clinicEventConfigurations: (prev.clinicEventConfigurations || []).filter(
                              (c) => c.id !== eventEditDrawerConfigId,
                            ),
                          }))
                          close()
                        }}
                      >
                        Delete this event
                      </Button>
                      <Button variant="contained" onClick={close}>
                        Done
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </>
            )}
          </Drawer>
        )
      })()}

      {/* Phase 14p — Professional Credentials document viewer. The mandatory credential row and
          each certificate row both pipe their View button into here so the agent can preview
          the file inline (PDF iframe / image) without leaving the form. The header carries an
          "Open in new tab" button for callers who want a full-page native viewer. */}
      {(() => {
        const file = credentialDocViewer
        const close = () => setCredentialDocViewer(null)
        const fileUrl = file?.fileId ? `${mediaURL}${file.fileId}` : ''
        const ext = (file?.fileId || '').split('.').pop()?.toLowerCase() || ''
        const isPdf = ext === 'pdf'
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
        return (
          <Drawer
            anchor="right"
            open={Boolean(file)}
            onClose={close}
            PaperProps={{ sx: { width: { xs: '100%', md: 720 }, maxWidth: '100%' } }}
          >
            {file && (
              <>
                <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>
                        {file.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Document preview — opens in this side panel; use Open in new tab for a
                        full-screen native viewer.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNew fontSize="small" />}
                        onClick={() => {
                          if (typeof window !== 'undefined' && fileUrl) {
                            window.open(fileUrl, '_blank', 'noopener,noreferrer')
                          }
                        }}
                      >
                        Open in new tab
                      </Button>
                      <IconButton onClick={close} aria-label="close">
                        <span style={{ fontSize: 24, lineHeight: 1 }}>×</span>
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                  }}
                >
                  {isPdf ? (
                    <iframe
                      src={fileUrl}
                      title={file.title}
                      style={{ width: '100%', height: '100%', minHeight: '80vh', border: 0 }}
                    />
                  ) : isImage ? (
                    <img
                      src={fileUrl}
                      alt={file.title}
                      style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
                    />
                  ) : (
                    // Unknown extension — fall back to an iframe; most browsers will either
                    // render or download. If neither, the agent can pop it open in a new tab.
                    <iframe
                      src={fileUrl}
                      title={file.title}
                      style={{ width: '100%', height: '100%', minHeight: '80vh', border: 0 }}
                    />
                  )}
                </Box>
              </>
            )}
          </Drawer>
        )
      })()}

      {/* Phase 14k — auditor access drawer. Holds the same editor that previously rendered
          inline. Top of the drawer surfaces the clinic brands + locations this practitioner is
          attached to (auditor access only makes sense within those), so the agent has the
          context they need without flipping back to the main page. */}
      <Drawer
        anchor="right"
        open={auditorDrawerOpen}
        onClose={() => setAuditorDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 760 }, maxWidth: '100%' } }}
      >
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Auditor Access
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Attach this practitioner as an auditor over other mentors. Per-room incoming
                auditors are managed inside each Clinic Assignment.
              </Typography>
            </Box>
            <IconButton onClick={() => setAuditorDrawerOpen(false)} aria-label="close">
              <span style={{ fontSize: 24, lineHeight: 1 }}>×</span>
            </IconButton>
          </Stack>
        </Box>
        <Box sx={{ p: 2.5, overflowY: 'auto' }}>
          {/* Clinic context: brands + locations this practitioner is attached to. Audit access
              is only meaningful inside those, so we surface them at the top of the drawer. */}
          {(() => {
            const brands = bootstrapState.data?.clinicBrands || []
            const locations = bootstrapState.data?.clinicLocations || []
            const attachedBrands = Array.from(
              new Set(
                (form.clinicAssignments || [])
                  .map((a) => a?.clinicBrandId)
                  .filter(Boolean),
              ),
            ).map((id) => brands.find((b) => b?.id === id) || { id, title: id })
            const attachedLocations = Array.from(
              new Set(
                (form.clinicAssignments || [])
                  .map((a) => a?.clinicLocationId)
                  .filter(Boolean),
              ),
            ).map((id) => locations.find((l) => l?.id === id) || { id, title: id })
            const attachedRooms = Array.from(
              new Set(
                (form.clinicAssignments || [])
                  .flatMap((a) => a?.chamberRoomIds || [])
                  .filter(Boolean),
              ),
            )
              .map((id) => chamberRoomOptions.find((opt) => opt?.id === id))
              .filter(Boolean)
            return (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Where this practitioner can audit
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Clinic brands:
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {attachedBrands.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No clinic brands attached yet — add a Clinic Assignment first.
                        </Typography>
                      ) : (
                        attachedBrands.map((b) => (
                          <Chip
                            key={`drawer-brand-${b.id}`}
                            size="small"
                            variant="outlined"
                            label={b.subtitle ? `${b.title} (${b.subtitle})` : b.title}
                          />
                        ))
                      )}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Clinic locations:
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {attachedLocations.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No locations attached yet.
                        </Typography>
                      ) : (
                        attachedLocations.map((l) => (
                          <Chip
                            key={`drawer-loc-${l.id}`}
                            size="small"
                            variant="outlined"
                            label={l.subtitle ? `${l.title} (${l.subtitle})` : l.title}
                          />
                        ))
                      )}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Chamber rooms (audit scope candidates):
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {attachedRooms.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No rooms attached yet.
                        </Typography>
                      ) : (
                        attachedRooms.map((room) => (
                          <Chip
                            key={`drawer-room-${room.id}`}
                            size="small"
                            variant="outlined"
                            label={room.subtitle ? `${room.title} (${room.subtitle})` : room.title}
                          />
                        ))
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            )
          })()}

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              This practitioner audits other mentors
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddCircleOutline />}
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  auditorAssignments: [
                    ...prev.auditorAssignments,
                    createEmptyAuditorAssignment(),
                  ],
                }))
              }
            >
              Add Auditor Link
            </Button>
          </Stack>

          {form.auditorAssignments.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Skip this section if auditor access is not needed for this practitioner.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {form.auditorAssignments.map((assignment, index) => (
                <Paper key={`drawer-auditor-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <Autocomplete
                        options={(bootstrapState.data?.practitioners || []).filter(
                          (option) => option?.id !== form.practitionerId,
                        )}
                        value={
                          (bootstrapState.data?.practitioners || []).find(
                            (option) => option?.id === assignment.mentorId,
                          ) || null
                        }
                        onChange={(_event, nextValue) =>
                          updateAuditorAssignment(index, {
                            mentorId: nextValue?.id || '',
                          })
                        }
                        getOptionLabel={(option) =>
                          option?.subtitle ? `${option.title} (${option.subtitle})` : getOptionLabel(option)
                        }
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Mentor To Audit"
                            helperText="The practitioner being onboarded will be attached as an auditor to this mentor."
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        select
                        fullWidth
                        label="Access Type"
                        value={assignment.type}
                        onChange={(event) =>
                          updateAuditorAssignment(index, { type: event.target.value })
                        }
                      >
                        {AUDITOR_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      {(() => {
                        const assignedRoomIds = Array.from(
                          new Set(
                            (form.clinicAssignments || [])
                              .flatMap((a) => a?.chamberRoomIds || [])
                              .filter(Boolean),
                          ),
                        )
                        const roomOptions = assignedRoomIds
                          .map((id) => chamberRoomOptions.find((opt) => opt?.id === id))
                          .filter(Boolean)
                        return (
                          <Autocomplete
                            multiple
                            options={roomOptions}
                            value={roomOptions.filter((opt) =>
                              (assignment.chamberRoomIds || []).includes(opt.id),
                            )}
                            onChange={(_event, nextValue) =>
                              updateAuditorAssignment(index, {
                                chamberRoomIds: nextValue.map((opt) => opt.id),
                              })
                            }
                            getOptionLabel={(option) =>
                              option?.subtitle
                                ? `${option.title} (${option.subtitle})`
                                : getOptionLabel(option)
                            }
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Scope to chamber rooms (optional)"
                                helperText={
                                  roomOptions.length === 0
                                    ? 'Add a clinic assignment first to scope auditor access by room.'
                                    : 'Pick rooms to scope this audit access to those rooms only. Leave empty for global.'
                                }
                              />
                            )}
                          />
                        )
                      })()}
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        color="error"
                        startIcon={<DeleteOutline />}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            auditorAssignments: prev.auditorAssignments.filter(
                              (_item, itemIndex) => itemIndex !== index,
                            ),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>

      {/* Phase 14k — payment configuration drawer. Same form that previously rendered inline,
          just behind a focused side panel. Width caps at 720px so it doesn't dominate large
          monitors but stays readable on smaller screens. */}
      <Drawer
        anchor="right"
        open={paymentDrawerOpen}
        onClose={() => setPaymentDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 720 }, maxWidth: '100%' } }}
      >
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Payment Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {form.firstName ? `Configuring payouts for ${form.firstName}` : 'Practitioner payout setup'}
              </Typography>
            </Box>
            <IconButton onClick={() => setPaymentDrawerOpen(false)}>
              <DeleteOutline sx={{ display: 'none' }} />
              <span aria-label="close" style={{ fontSize: 24, lineHeight: 1 }}>×</span>
            </IconButton>
          </Stack>
        </Box>
        <Box sx={{ p: 2.5, overflowY: 'auto' }}>
          {trimValue(form.practitionerId) ? (
            <PaymentConfigForm
              entityId={form.practitionerId}
              entityType="mentor"
              prefillData={paymentConfigPrefill}
              hideEntitySection
            />
          ) : (
            <Alert severity="info">
              Save the practitioner first — payout configuration unlocks once a practitioner record exists.
            </Alert>
          )}
        </Box>
      </Drawer>
    </>
  )
}
